import React, { useState, useEffect } from 'react';
import CutPlan from './CutPlan';
import { Calendar, BarChart2, List, Save, Scissors, Layers, CheckCircle, AlertTriangle, Clock, Filter, X, Maximize, Truck, FileText, Layout, Settings, Activity } from 'lucide-react';

// =================================================================================================
// DATABASE CONFIGURATION
// =================================================================================================
const API_BASE = 'https://nirvikalp.integrashion.com/api';

const saveToDatabase = async (category, data, type = null) => {
    try {
        const payload = { category, data };
        if (type) payload.type = type;
        const response = await fetch(`${API_BASE}/save_universal.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error(`HTTP ${response.status} — server error`);
        const result = await response.json();
        if (!result.success) {
            const msg = result.error || 'Save failed';
            console.error(`[DB] ${category} save failed:`, msg, data);
            throw new Error(msg);
        }
        console.log(`[DB] ${category} saved OK, id=${result.id}`);
        return result;  // {success, id, ...}
    } catch (err) {
        console.error('[DB] saveToDatabase error [' + category + ']:', err.message);
        // Never use alert() — browser can silently block it
        // Return error object so callers show their own toast
        return { success: false, error: err.message, id: null };
    }
};

const loadFromDatabase = async () => {
    try {
        const response = await fetch(`${API_BASE}/get_all_data.php`);
        const data = await response.json();
        return data;
    } catch (err) {
        console.error('Database load error:', err);
        return null;
    }
};

// =================================================================================================
// 1. UTILITIES & HELPERS
// =================================================================================================

// --- PO EXTRACTOR — reads from StyleRegistry pos[] directly, falls back to deep scan ---
const extractStylePOs = (style) => {
    // PRIMARY: StyleRegistry stores POs as style.pos[] (also check inside workstationData
    // in case PHP returned them nested, or the style was saved before the normalisation fix)
    const directPos = style.pos 
        || style.purchaseOrders 
        || style.orders 
        || style.workstationData?.pos       // nested inside workstationData
        || style.workstation_data?.pos      // snake_case variant
        || [];
    if (Array.isArray(directPos) && directPos.length > 0) {
        // Normalise to standard shape used throughout Planning
        return directPos.map(p => ({
            num:          p.num || p.po_no || p.poNo || p.number || '-',
            qty:          parseFloat(p.qty || p.quantity || p.total_qty || 0) || 0,
            col:          p.col || p.color || p.colour || '',
            fob:          p.fob || p.fob_price || '',
            date:         p.date || p.ex_fty_date || '',
            dest:         p.dest || p.destination || '',
            sizeBreakdown: p.sizeBreakdown || p.size_breakdown || '',
        })).filter(p => p.num !== '-');
    }

    // FALLBACK: deep scan for legacy or non-standard shapes
    let extracted = [];
    let candidateArrays = [];
    Object.keys(style).forEach(key => {
        if (Array.isArray(style[key])) {
            candidateArrays.push(style[key]);
        } else if (typeof style[key] === 'object' && style[key] !== null) {
            Object.values(style[key]).forEach(nestedVal => {
                if (Array.isArray(nestedVal)) candidateArrays.push(nestedVal);
            });
        }
    });
    candidateArrays.forEach(arr => {
        if (arr.length === 0) return;
        const sample = arr[0];
        const looksLikePO = sample && typeof sample === 'object' && !Array.isArray(sample) && (
            'PO#' in sample || 'PO' in sample || 'poNo' in sample || 'po_no' in sample || 
            'order_no' in sample || 'Qty' in sample || 'quantity' in sample
        );
        if (looksLikePO) {
            const groupedPOs = {};
            let lastSeenPO = null;
            arr.forEach(p => {
                let rawNo = p['PO#'] || p['PO'] || p.poNo || p.po_no || p.number || p.num || p.order_no;
                if (!rawNo && lastSeenPO && (p.Qty || p.quantity || p.QUANTITY)) rawNo = lastSeenPO;
                if (rawNo) lastSeenPO = rawNo;
                if (rawNo) {
                    const poKey = String(rawNo).trim();
                    const qtyVal = parseFloat(p.Qty || p.quantity || p.QUANTITY || p.qty || p.total_qty || 0) || 0;
                    if (!groupedPOs[poKey]) groupedPOs[poKey] = { num: poKey, qty: 0, col: p.color || p.col || '', date: p.date || p.ex_fty_date || '', sizeBreakdown: p.sizeBreakdown || '' };
                    groupedPOs[poKey].qty += qtyVal;
                }
            });
            Object.values(groupedPOs).forEach(po => extracted.push(po));
        }
    });
    return extracted.filter((po, i, self) => i === self.findIndex(t => t.num === po.num));
};


// =================================================================================================
// MASTER DATA CONNECTOR — resolves all important fields from all DB sources
// Usage: const sd = getStyleData(styleRef, db);
// =================================================================================================
const getStyleData = (styleRef, db) => {
  if (!styleRef || !db) return null;

  // 1. Base style from mgmtStyles
  const s = (db.mgmtStyles || []).find(st =>
    (st.styleRef || st.name || '').toLowerCase() === styleRef.toLowerCase()
  );
  if (!s) return null;

  // 2. POs — check every possible location
  const rawPos = s.pos
    || s.purchaseOrders
    || s.orders
    || s.workstationData?.pos
    || s.workstation_data?.pos
    || [];

  // Also pull from poSizeTable if pos[] is empty
  const poFromTable = rawPos.length === 0
    ? [...new Map(
        (db.poSizeTable || [])
          .filter(r => r.style_ref === styleRef)
          .map(r => [r.po_no, {
            num: r.po_no,
            qty: (db.poSizeTable || []).filter(x => x.style_ref === styleRef && x.po_no === r.po_no).reduce((a, x) => a + (parseInt(x.qty) || 0), 0),
            col: r.color || '',
            date: r.ex_fty_date || '',
            dest: r.destination || '',
            fob: r.fob || '',
            sizeBreakdown: ''
          }])
      ).values()]
    : [];

  const pos = rawPos.length > 0
    ? rawPos.map(p => ({
        num:  p.num  || p.po_no || p.poNo || p.number || '-',
        qty:  parseFloat(p.qty  || p.quantity || p.total_qty || 0) || 0,
        col:  p.col  || p.color || p.colour || '',
        fob:  p.fob  || p.fob_price || '',
        date: p.date || p.ex_fty_date || '',
        dest: p.dest || p.destination || '',
        sizeBreakdown: p.sizeBreakdown || p.size_breakdown || '',
      })).filter(p => p.num !== '-')
    : poFromTable;

  // 3. Total qty
  const totalQty = parseFloat(s.totalQty) > 0
    ? parseFloat(s.totalQty)
    : pos.reduce((a, p) => a + (parseFloat(p.qty) || 0), 0);

  // 4. Ex-factory date — earliest PO date, or from style plan
  const sortedPosByDate = [...pos].filter(p => p.date).sort((a, b) => new Date(a.date) - new Date(b.date));
  const earliestExFty = sortedPosByDate[0]?.date || '';
  const plan = (db.stylePlans || []).find(p => p.style === styleRef);
  const planExFty = (plan?.processes || []).find(p => p.name?.toLowerCase().includes('ex-factory'))?.planDate || '';
  const exFty = planExFty || earliestExFty;

  // 5. Final inspection date — from plan processes
  const finalInspDate = (plan?.processes || [])
    .find(p => p.name?.toLowerCase().includes('inspection') || p.name?.toLowerCase().includes('final'))?.planDate || '';

  // 6. Shipment logs
  const shipLogs = (db.shipmentLogs || []).filter(l => l.style_ref === styleRef);
  const lastShipLog = shipLogs.sort((a, b) => new Date(b.shipped_date) - new Date(a.shipped_date))[0];
  const shippedDate = lastShipLog?.shipped_date || lastShipLog?.actual_exfty_date || '';

  // 7. Sizes & Colors — from poSizeTable or parsed sizeBreakdowns
  const poTableRows = (db.poSizeTable || []).filter(r => r.style_ref === styleRef);
  const sizes = poTableRows.length > 0
    ? [...new Set(poTableRows.map(r => r.size).filter(Boolean))]
    : [...new Set(pos.flatMap(p => {
        const sb = p.sizeBreakdown;
        if (typeof sb === 'string' && sb.trim())
          return sb.split(',').map(s => s.split(':')[0].trim()).filter(Boolean);
        if (sb && typeof sb === 'object') return Object.keys(sb);
        return [];
      }))];
  const colors = [...new Set([
    ...pos.map(p => p.col).filter(Boolean),
    ...poTableRows.map(r => r.color).filter(Boolean)
  ])];

  // 8. SMV from workstation data
  const smv = parseFloat(
    s.workstationData?.aggregatedOps?.reduce((a, op) => a + (parseFloat(op.smv) || 0), 0)
    || s.workstationData?.smv
    || s.smv
    || 0
  ) || 0;

  // 9. Brand / Buyer / Season / Fabric / Product (normalised)
  const brand   = s.brand   || s.brandName   || '';
  const buyer   = s.buyer   || s.buyerName   || '';
  const season  = s.season  || '';
  const fabric  = s.fabric  || s.fabricType  || s.fabricComposition || s.material || '';
  const product = s.product || s.productType || s.garmentType || s.garmentCategory || s.category || '';

  return {
    // Identity
    id: s.id, styleRef, name: s.name || styleRef,
    brand, buyer, season, fabric, product, status: s.status || 'Development',
    // Quantities
    totalQty, smv,
    // POs
    pos, totalPOs: pos.length,
    // Dates
    exFty, finalInspDate, shippedDate,
    // Size/Color
    sizes, colors,
    // Plan
    plan, planProcesses: plan?.processes || [],
    // Shipping
    shipLogs, lastShipLog, isShipped: shippedDate !== '',
    // Raw style object for anything else
    _raw: s,
  };
};

// Convenience: get ALL POs for a style as options for a select
const getStylePOOptions = (styleRef, db) => {
  const sd = getStyleData(styleRef, db);
  if (!sd) return [];
  // If style has pos[], use them. Otherwise build from poSizeTable (AI-extracted POs)
  if (sd.pos.length > 0) return sd.pos;
  const dbNums = [...new Set((db.poSizeTable||[]).filter(r=>r.style_ref===styleRef).map(r=>r.po_no))];
  return dbNums.map(pn => {
    const rows = (db.poSizeTable||[]).filter(r=>r.style_ref===styleRef&&r.po_no===pn);
    return {
      num: pn,
      qty: rows.reduce((a,r)=>a+(parseInt(r.qty)||0),0),
      col: [...new Set(rows.map(r=>r.color).filter(Boolean))].join('/'),
      date: rows[0]?.ex_fty_date || '',
      dest: rows[0]?.destination || '',
    };
  });
};

// Convenience: get sizes+colors for a specific PO
const getPOSizeColor = (styleRef, poNo, db) => {
  // Primary source: po_size_table (populated when style is saved with size breakdown)
  const rows = (db.poSizeTable || []).filter(r =>
    r.style_ref === styleRef && (!poNo || r.po_no === poNo)
  );
  let sizes  = [...new Set(rows.map(r => r.size ).filter(Boolean))];
  let colors = [...new Set(rows.map(r => r.color).filter(Boolean))];
  const qty  = rows.reduce((a, r) => a + (parseInt(r.qty) || 0), 0);

  // Fallback: parse from style.pos[].sizeBreakdown when po_size_table is empty
  if (sizes.length === 0 || colors.length === 0) {
    const style = (db.mgmtStyles || []).find(s => s.styleRef === styleRef);
    const pos   = style?.pos || [];
    const matchPOs = poNo ? pos.filter(p => (p.num||p.po||'').split(/[\s·•]/)[0] === poNo.split(/[\s·•]/)[0]) : pos;
    matchPOs.forEach(p => {
      // color
      if (p.col || p.color) colors.push(p.col||p.color);
      // sizes from sizeBreakdown string like "S:1000,M:500,L:1000,XL:2000"
      const sb = p.sizes || p.sizeBreakdown || p.size_breakdown || '';
      if (typeof sb === 'string' && sb) {
        sb.split(',').forEach(part => {
          const s = part.trim().split(':')[0].trim();
          if (s) sizes.push(s);
        });
      } else if (Array.isArray(sb)) {
        sb.forEach(s => { const sv = typeof s==='string'?s:(s.size||''); if(sv) sizes.push(sv); });
      }
    });
    sizes  = [...new Set(sizes.filter(Boolean))];
    colors = [...new Set(colors.filter(Boolean))];
  }
  return { sizes, colors, qty };
};

// =================================================================================================
// PO QTY VALIDATION ENGINE
// Single source of truth for all cross-stage qty limits
// Hierarchy: PO qty → Cut ≤ PO (or admin-approved) → Sew ≤ Cut → Finish ≤ Sew → Ship ≤ Finish
// =================================================================================================

/**
 * getPOLedger(styleRef, poNo, color, size, db)
 * Returns cumulative qty at each stage for a specific PO+color+size combination.
 * Pass null/'' for color or size to get totals across all colors/sizes.
 */
// ── SHARED production data calculator — used by ALL views ──────────────────
// Rules:
//   • poNo match: strip labels, match core PO number only
//   • size filter: entries WITHOUT size only count in unfiltered (total) queries
//   • source separation: FOLLOWUP entries for totals, DATAPAD for MIS only
const extractPONo = (str) => (str || '').split(/[\s·•]/)[0].trim();

const getPOLedger = (styleRef, poNo, color, size, db) => {
  const targetPO = extractPONo(poNo);   // 'DL/INT/120426/BLU · Blue · 5k' → 'DL/INT/120426/BLU'
  const logs = (db.productionLogs || []).filter(l => {
    if (!l.style || l.style !== styleRef) return false;

    // PO filter — match on core PO number (strip display labels)
    if (targetPO) {
      const logPO = extractPONo(l.po_no || l.po || '');
      if (logPO && logPO !== targetPO) return false;
    }

    // Color filter — entries without color count towards total only
    if (color && l.color && l.color.toLowerCase() !== color.toLowerCase()) return false;

    // SIZE FILTER (the critical fix) — entries without size must NOT count
    // towards a size-specific query (prevents multiply-counting across sizes)
    if (size) {
      if (!l.size) return false;            // ← no size set → skip for size queries
      if (l.size !== size) return false;    // ← wrong size → skip
    }

    return true;
  });

  const cut    = logs.reduce((a, l) => a + (parseFloat(l.cutting)   || 0), 0);
  const rawSew = logs.reduce((a, l) => a + (parseFloat(l.sewing)    || 0), 0);
  const finish = logs.reduce((a, l) => a + (parseFloat(l.finishing) || 0), 0);
  // Physical constraint: can't sew more than cut
  const sew = cut > 0 ? Math.min(rawSew, cut) : rawSew;
  const shipLogs = (db.shipmentLogs || []).filter(l => {
    if (l.style_ref !== styleRef) return false;
    if (poNo && l.po_no && l.po_no !== poNo) return false;
    return true;
  });
  const shipped = shipLogs.reduce((a, l) => a + (parseFloat(l.shipped_qty || l.quantity || 0)), 0);
  return { cut, sew, finish, shipped };
};

/**
 * getPOQtyLimit(styleRef, poNo, color, size, db)
 * Returns the authorised qty limit for a PO+color+size from poSizeTable or pos[].
 */
const getPOQtyLimit = (styleRef, poNo, color, size, db) => {
  // 1. Try exact match in poSizeTable
  if (poNo && size && color) {
    const row = (db.poSizeTable || []).find(r =>
      r.style_ref === styleRef && r.po_no === poNo &&
      r.size === size && (r.color || '').toLowerCase() === (color || '').toLowerCase()
    );
    if (row) return parseInt(row.qty) || 0;
  }
  // 2. Match by PO+size (no color match)
  if (poNo && size) {
    const rows = (db.poSizeTable || []).filter(r =>
      r.style_ref === styleRef && r.po_no === poNo && r.size === size
    );
    if (rows.length > 0) return rows.reduce((a, r) => a + (parseInt(r.qty) || 0), 0);
  }
  // 3. Match by PO only — total PO qty
  if (poNo) {
    const rows = (db.poSizeTable || []).filter(r =>
      r.style_ref === styleRef && r.po_no === poNo
    );
    if (rows.length > 0) return rows.reduce((a, r) => a + (parseInt(r.qty) || 0), 0);
    // Fallback: from pos[] on style
    const sd = getStyleData(styleRef, db);
    const po = sd?.pos.find(p => p.num === poNo);
    if (po) return parseFloat(po.qty) || 0;
  }
  // 4. No PO specified — return total style qty
  const sd = getStyleData(styleRef, db);
  return sd?.totalQty || 0;
};

/**
 * validateStageQty(stage, styleRef, poNo, color, size, newQty, db)
 * Returns { ok, maxAllowed, used, poLimit, reason, warningOnly }
 * stage: 'CUTTING' | 'SEWING' | 'FINISHING' | 'SHIPPING' | 'PLANNING'
 */
const validateStageQty = (stage, styleRef, poNo, color, size, newQty, db) => {
  const poLimit  = getPOQtyLimit(styleRef, poNo, color, size, db);
  const ledger   = getPOLedger(styleRef, poNo, color, size, db);
  const qty      = parseFloat(newQty) || 0;

  // Admin-approved cut qty overrides PO qty ceiling for cutting
  const sd = getStyleData(styleRef, db);
  const adminCutQty = sd?._raw?.adminApprovedCutQty || 0;
  const cutCeiling = adminCutQty > 0 ? adminCutQty : poLimit;

  switch (stage) {
    case 'CUTTING': {
      const used = ledger.cut;
      const maxAllowed = Math.max(0, cutCeiling - used);
      if (cutCeiling > 0 && (used + qty) > cutCeiling) {
        const isAdminOverride = adminCutQty > 0 && cutCeiling === adminCutQty;
        return {
          ok: false, used, maxAllowed, poLimit, cutCeiling,
          reason: `Cut qty ${used + qty} exceeds ${isAdminOverride ? 'admin-approved' : 'PO'} limit of ${cutCeiling}. Max you can enter: ${maxAllowed}.`
        };
      }
      return { ok: true, used, maxAllowed, poLimit, cutCeiling };
    }
    case 'SEWING': {
      const used = ledger.sew;
      const ceiling = ledger.cut > 0 ? ledger.cut : cutCeiling; // can't sew more than cut
      const maxAllowed = Math.max(0, ceiling - used);
      if (ceiling > 0 && (used + qty) > ceiling) {
        return {
          ok: false, used, maxAllowed, poLimit, ceiling,
          reason: `Sew qty ${used + qty} exceeds cut qty ${ceiling}. Max: ${maxAllowed}. You cannot sew what hasn't been cut.`
        };
      }
      return { ok: true, used, maxAllowed, poLimit, ceiling };
    }
    case 'FINISHING': {
      const used = ledger.finish;
      const ceiling = ledger.sew > 0 ? ledger.sew : (ledger.cut > 0 ? ledger.cut : cutCeiling);
      const maxAllowed = Math.max(0, ceiling - used);
      if (ceiling > 0 && (used + qty) > ceiling) {
        return {
          ok: false, used, maxAllowed, poLimit, ceiling,
          reason: `Finish qty ${used + qty} exceeds sew qty ${ceiling}. Max: ${maxAllowed}.`
        };
      }
      return { ok: true, used, maxAllowed, poLimit, ceiling };
    }
    case 'SHIPPING': {
      const used = ledger.shipped;
      const ceiling = ledger.finish > 0 ? ledger.finish : (ledger.sew > 0 ? ledger.sew : poLimit);
      const maxAllowed = Math.max(0, ceiling - used);
      if (ceiling > 0 && (used + qty) > ceiling) {
        return {
          ok: false, used, maxAllowed, poLimit, ceiling,
          reason: `Ship qty ${used + qty} exceeds finished qty ${ceiling}. Max: ${maxAllowed}.`
        };
      }
      return { ok: true, used, maxAllowed, poLimit, ceiling };
    }
    case 'PLANNING': {
      // For allocation: planned qty across all lines for this style/PO should not exceed PO qty
      const maxAllowed = poLimit;
      if (poLimit > 0 && qty > poLimit) {
        return {
          ok: false, used: 0, maxAllowed, poLimit,
          reason: `Planned qty ${qty} exceeds PO limit ${poLimit}.`, warningOnly: true
        };
      }
      return { ok: true, used: 0, maxAllowed, poLimit };
    }
    default:
      return { ok: true, used: 0, maxAllowed: Infinity, poLimit };
  }
};

/**
 * QtyBadge — shows remaining/used at a glance in entry forms
 */
const QtyBadge = ({ stage, styleRef, poNo, color, size, db }) => {
  if (!styleRef) return null;
  const v = validateStageQty(stage, styleRef, poNo, color, size, 0, db);
  const pct = v.poLimit > 0 ? Math.min(100, Math.round((v.used / v.poLimit) * 100)) : 0;
  const barColor = pct >= 100 ? '#e2445c' : pct >= 80 ? '#fdab3d' : '#00a65e';
  const stageLabels = { CUTTING: 'cut', SEWING: 'sewn', FINISHING: 'finished', SHIPPING: 'shipped' };
  // If no specific PO, show vs totalQty (not just registered PO sum)
  const styleTotal = getStyleData(styleRef, db)?.totalQty || 0;
  const displayLimit = poNo ? v.poLimit : (styleTotal || v.poLimit);
  if (displayLimit === 0) return null;
  const displayPct = displayLimit > 0 ? Math.min(100, Math.round((v.used / displayLimit) * 100)) : 0;
  const displayBar = displayPct >= 100 ? '#e2445c' : displayPct >= 80 ? '#fdab3d' : '#00a65e';
  const displayMax = Math.max(0, displayLimit - v.used);
  return (
    <div style={{ fontSize: '0.65rem', color: '#676879', marginTop: '3px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
        <span>{stageLabels[stage] || stage}: <strong style={{ color: displayPct >= 100 ? '#e2445c' : '#323338' }}>{v.used.toLocaleString()}</strong></span>
        <span>limit: <strong>{displayLimit.toLocaleString()}</strong>{!poNo && styleTotal > v.poLimit && v.poLimit > 0 ? <span style={{color:'#fdab3d',fontSize:'0.6rem'}}> (full: {styleTotal.toLocaleString()})</span> : null}</span>
      </div>
      <div style={{ background: '#e6e9ef', borderRadius: '2px', height: '4px' }}>
        <div style={{ width: `${displayPct}%`, height: '100%', background: displayBar, borderRadius: '2px', transition: 'width 0.3s' }} />
      </div>
      <div style={{ color: displayPct >= 100 ? '#e2445c' : '#00a65e', marginTop: '2px' }}>
        {displayPct >= 100 ? '🔴 LIMIT REACHED' : `✓ ${displayMax.toLocaleString()} remaining`}
      </div>
    </div>
  );
};


const MultiSelect = ({ options, selected, onChange }) => (
  <div style={{
    height: '120px', 
    overflowY: 'auto', 
    border: '1px solid #c5c7d4', 
    padding: '8px', 
    borderRadius: '6px', 
    background: 'white'
  }}>
     {(options || []).map(o => (
       <label key={o} style={{display: 'flex', alignItems:'center', cursor: 'pointer', fontSize: '0.85rem', marginBottom:'4px', padding:'2px'}}>
         <input 
            type="checkbox" 
            checked={selected.includes(o)} 
            onChange={e => e.target.checked ? onChange([...selected, o]) : onChange(selected.filter(x => x !== o))} 
            style={{marginRight:'8px', width:'16px', height:'16px'}}
         /> 
         {o}
       </label>
     ))}
     {(options || []).length === 0 && <div style={{fontSize:'0.8rem', color:'#9699a6', textAlign:'center', padding:'10px'}}>No items available</div>}
  </div>
);

const getWorkingDays = (start, end) => {
  if (!start || !end) return 0;
  let count = 0;
  let cur = new Date(start);
  const finish = new Date(end);
  if(cur > finish) return 0;
  while (cur <= finish) {
    const day = cur.getDay();
    if (day !== 0) count++; 
    cur.setDate(cur.getDate() + 1);
  }
  return count;
};

const addDays = (date, days) => {
  if(!date) return "";
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result.toISOString().split('T')[0];
};

const getDaysArray = (start, days) => {
    const arr = [];
    for (let i = 0; i < days; i++) {
        const d = new Date(start);
        d.setDate(d.getDate() + i);
        arr.push(d);
    }
    return arr;
};

const getStyleColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) { hash = str.charCodeAt(i) + ((hash << 5) - hash); }
    const c = (hash & 0x00FFFFFF).toString(16).toUpperCase();
    return '#' + "00000".substring(0, 6 - c.length) + c;
};

// =================================================================================================
// 2. MAIN COMPONENT
// =================================================================================================
export default function ProductionPlanning({ db, setDb, openStylePassport }) {
  
  // --- STATE MANAGEMENT ---
  const [sub, setSub] = useState("TIMELINES");
  
  // 1. TIMELINE SETUP
  const [tlBrand, setTlBrand] = useState("");
  const [tlProcs, setTlProcs] = useState([]);
  const [procIn, setProcIn] = useState({ id: null, name: "", type: "Relative", anchor: "", days: 0, isSample: false });

  // 2. GANTT GENERATOR
  const [cpFilters, setCpFilters] = useState({ brand: "", styles: [] });
  const [rootDates, setRootDates] = useState({});
  const [showVisual, setShowVisual] = useState(null);
  const [generatedPlans, setGeneratedPlans] = useState([]);

  // --- PROCESS MODAL & SHIPPING MODULE STATE ---
  const [viewProcess, setViewProcess] = useState(null); 
  const [shippingView, setShippingView] = useState(null);
  const [shipmentForm, setShipmentForm] = useState({ po_no: '', shipped_qty: 0, shipped_date: '', actual_exfty_date: '', invoice_no: '', awb_no: '', notes: '', documents: [] });

  // 3. TRACKER
  const [trFilters, setTrFilters] = useState({ brands: [], styles: [] });

  // 4. LINE ALLOCATION
  const [allocView, setAllocView] = useState("LIST");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [planTab, setPlanTab] = useState("SEWING");
  const [planData, setPlanData] = useState({
    pcd: "", exFty: "", sewEndDate: "", finEndDate: "", workingHours: 8, targetEff: 60,
    allocatedLines: [], allocatedCutters: [], allocatedFinishers: []
  });
  const [targetPO, setTargetPO] = useState("");

  // ** FORMS **
  const [cutForm, setCutForm] = useState({ table: 'Table 1', layers: 50, ratio: 'S:1 M:2 L:2 XL:1', totalPcs: 0 });
  const [finForm, setFinForm] = useState({ type: 'Packing', qty: 0 });

  // 5. PLANNING BOARD
  const [boardStart, setBoardStart] = useState(new Date().toISOString().split('T')[0]);
  const [boardDays, setBoardDays] = useState(30);
  const [selectedFactories, setSelectedFactories] = useState([]);

  // 6. PRODUCTION FOLLOWUP — expanded cascading state
  // Local date helper — avoids UTC/IST mismatch (India is UTC+5:30,
  // so at 3am IST new Date().toISOString() still returns yesterday in UTC)
  const localToday = () => {
    const d = new Date();
    return d.getFullYear() + '-' +
      String(d.getMonth() + 1).padStart(2, '0') + '-' +
      String(d.getDate()).padStart(2, '0');
  };
  // Inline notification for production saves (no alert popups)
  const [prodNotify, setProdNotify] = useState(null); // {msg, type, ts}
  const prodToast = (msg, type='success') => {
    setProdNotify({msg, type, ts: Date.now()});
    setTimeout(() => setProdNotify(null), 4000);
  };

  const [prodDate, setProdDate] = useState(localToday());
  const [prodDateRange, setProdDateRange] = useState(() => {
    const today = localToday();
    const d30 = new Date(); d30.setDate(d30.getDate()-30);
    return { from: d30.toISOString().split('T')[0], to: today };
  });
  const [prodFilters, setProdFilters] = useState({ brand: "", season: "", style: "", po: "", factory: "", line: "" });
  const [prodLineTab, setProdLineTab] = useState("SEWING");
  const [prodEntry, setProdEntry] = useState({ hour: "09-10", color: "", size: "", cutting: 0, sewing: 0, finishing: 0 });

  // 7. BOARD — drag-drop + AI advice state
  const [dragBooking, setDragBooking] = useState(null);

  // 8. CUT PLAN — Full automation state
  const [cpStep, setCpStep] = useState(1);
  const [cpSetup, setCpSetup] = useState({ brand:'', season:'', style:'', po:'', color:'' });
  const [cpSizeBreakdown, setCpSizeBreakdown] = useState([]);
  const [cpMarkers, setCpMarkers] = useState([]);
  const [cpMarkerInput, setCpMarkerInput] = useState({
    markerNo:'', shrinkageL:'', shrinkageW:'', patternCode:'', width:'', length:'',
    cutPanels:'', markerYield:'', way:'1-Way', csvType:'', consumption:'', wastage:''
  });
  const [cpIgnoreShade, setCpIgnoreShade] = useState(false);
  const [cpMaxPlies, setCpMaxPlies] = useState('');
  const [cpPliesDeviation, setCpPliesDeviation] = useState(5);
  const [cpBundleSize, setCpBundleSize] = useState(10);
  const [cpAllowRelax, setCpAllowRelax] = useState(true);
  const [cpMatchedRolls, setCpMatchedRolls] = useState([]);
  const [cpCutOptions, setCpCutOptions] = useState([]);
  const [cpSelectedOption, setCpSelectedOption] = useState(null);
  const [cpExecutionPlan, setCpExecutionPlan] = useState(null);
  const [cpShowReport, setCpShowReport] = useState(false);
  const [cpShowLaySlip, setCpShowLaySlip] = useState(false);
  const [cpSplitLastBundle, setCpSplitLastBundle] = useState(false);
  const [cpSplitQty, setCpSplitQty] = useState(6);
  const [cpAlertMsg, setCpAlertMsg] = useState('');
  const [dragOverLine, setDragOverLine] = useState(null);
  // Efficiency buildup plan state (hoisted from render — fixes React error #310)
  const [showRampUp, setShowRampUp] = useState(false);
  const [rampDays, setRampDays] = useState([
    {day:1, eff:30}, {day:2, eff:40}, {day:3, eff:50},
    {day:4, eff:55}, {day:5, eff:60}, {day:'6+', eff:60}
  ]);
  const [showMoveModal, setShowMoveModal] = useState(null);
  const [moveQty, setMoveQty] = useState(0);
  const [aiAdvice, setAiAdvice] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  // Per-line entry state keyed by line.id — avoids hooks-in-map violation
  const [lineEntries, setLineEntries] = useState({});
  const getLineEntry = (lid) => lineEntries[lid] || { hour:"09-10", size:"", color:"", cutting:0, sewing:0, finishing:0 };
  const setLineEntry = (lid, val) => setLineEntries(prev => ({ ...prev, [lid]: typeof val === "function" ? val(getLineEntry(lid)) : val }));

  // ============================================================
  // 9. AUTO PLAN — Comprehensive Automated Planning System
  // ============================================================
  const [apView, setApView] = useState('BOARD');          // BOARD | CONFIRM | READINESS | GENERATE | DASHBOARD
  const [apSelectedPO, setApSelectedPO] = useState(null); // currently focused PO key "styleRef::poNo::color"
  const [apPoList, setApPoList] = useState([]);            // [{key,styleRef,poNo,color,sizes,totalQty,pcd,exFty,fiDate,confirmed,postponed}]
  const [apPlanResult, setApPlanResult] = useState(null);  // full generated plan object
  const [apManualMode, setApManualMode] = useState(false);
  const [apManualEdits, setApManualEdits] = useState({});  // {poKey: {cut:{}, sew:{}, fin:{}, packing:{}}}
  const [apHolidays, setApHolidays] = useState([]);
  const [apNewHoliday, setApNewHoliday] = useState('');
  const [apBuffer, setApBuffer] = useState(5);             // % daily buffer on top of target
  const [apLcDept, setApLcDept] = useState('cutting');
  const [apLcEntry, setApLcEntry] = useState({ dept:'cutting', plies:'', layLen:'', width:'', parts:'', fabricType:'', diffIndex:'', weight:'', content:'', smv:'', efficiency:'', country:'', zone:'', qty:'', output:'' });
  const [apLearning, setApLearning] = useState([]);        // persisted learning curve rows
  const [apPoForm, setApPoForm] = useState({ styleRef:'', poNo:'', color:'', sizes:[], totalQty:'', pcd:'', exFty:'', fiDate:'' });
  const [apPoSizeRow, setApPoSizeRow] = useState({ size:'', qty:'' });
  const [apConfirmNotes, setApConfirmNotes] = useState({});
  const [apShowConflicts, setApShowConflicts] = useState(false);
  const [apDashPeriod, setApDashPeriod] = useState(60);
  const [apEditPlan, setApEditPlan] = useState(null);      // {poKey, dept, day, field, value}

  
  const getEffectiveSMV = (styleObj, costObj) => {
    if (!styleObj) return 0;
    const aggOps = styleObj.workstationData?.aggregatedOps;
    const wsSmv  = Array.isArray(aggOps) ? aggOps.reduce((a,o)=>a+(parseFloat(o.smv)||0),0) : 0;
    return wsSmv || parseFloat(styleObj.workstationData?.smv) || parseFloat(costObj?.smv) || parseFloat(styleObj.manualSmv) || 0;
  };

  // --- DATABASE SYNC ON LOAD + AUTOPLAN STATE RESTORE ---
  useEffect(() => {
      const syncDatabase = async () => {
          const data = await loadFromDatabase();
          if (data) {
              setDb(data);
              // Restore AutoPlan state: DB first, then localStorage fallback
              const dbApState = (data.planning || [])
                  .filter(p => p.type === 'AUTOPLAN_STATE')
                  .sort((a, b) => new Date(b.savedAt || 0) - new Date(a.savedAt || 0))[0];
              let apState = dbApState;
              if (!apState) {
                  try { const lsRaw = localStorage.getItem('nirvikalp_autoplan_state'); if (lsRaw) apState = JSON.parse(lsRaw); } catch (e) {}
              }
              if (apState) {
                  if (Array.isArray(apState.poList) && apState.poList.length > 0) setApPoList(apState.poList);
                  if (apState.planResult) setApPlanResult(apState.planResult);
                  if (apState.manualEdits && Object.keys(apState.manualEdits).length > 0) setApManualEdits(apState.manualEdits);
                  if (Array.isArray(apState.holidays) && apState.holidays.length > 0) setApHolidays(apState.holidays);
                  if (typeof apState.buffer === 'number') setApBuffer(apState.buffer);
              }
              // Restore learning curve entries
              const lcEntries = (data.planning || []).filter(p => p.type === 'LEARNING_CURVE');
              if (lcEntries.length > 0) setApLearning(lcEntries);
          }
      };
      syncDatabase();
  }, []);

  // --- AUTO-SAVE AUTOPLAN STATE (debounced 1.5s) to localStorage + DB ---
  useEffect(() => {
      if (apPoList.length === 0 && !apPlanResult) return;
      const payload = {
          type: 'AUTOPLAN_STATE', poList: apPoList, planResult: apPlanResult,
          manualEdits: apManualEdits, holidays: apHolidays, buffer: apBuffer,
          savedAt: new Date().toISOString(),
      };
      try { localStorage.setItem('nirvikalp_autoplan_state', JSON.stringify(payload)); } catch (e) {}
      const timer = setTimeout(() => { saveToDatabase('planning', payload); }, 1500);
      return () => clearTimeout(timer);
  }, [apPoList, apPlanResult, apManualEdits, apHolidays, apBuffer]);

  // --- HELPER: GET FACTORIES FROM SUPPLIERS — filtered by product type if a style is selected ---
  const getFactories = (productType) => {
      const allFacs = (db.suppliers || []).filter(s => s.supplierType === 'Garment Supplier');
      if (!productType) return allFacs;
      const pt = productType.toLowerCase();
      return allFacs.filter(fac => {
          // If factory has no product types declared, show it for all
          const declared = fac.productTypes || fac.products || fac.categories || [];
          if (!Array.isArray(declared) || declared.length === 0) return true;
          return declared.some(t => String(t).toLowerCase().includes(pt) || pt.includes(String(t).toLowerCase()));
      });
  };

  // Robust: tries all known field names a factory might use for its lines
  // Also enriches each line with uid from db.factoryLines master table
  const getFactoryLines = (factoryName) => {
      const fac = getFactories().find(f => f.name === factoryName);
      if (!fac) return [];
      const lines = fac.sewingLines || fac.lines || fac.productionLines || fac.sewLines || [];
      // Enrich lines with UIDs from the factory_lines master table
      const masterLines = (db.factoryLines || []).filter(fl => String(fl.factory_id) === String(fac.id));
      const enriched = lines.length > 0 ? lines : (() => {
          for (const key of Object.keys(fac)) {
              const val = fac[key];
              if (Array.isArray(val) && val.length > 0) {
                  const s = val[0];
                  if (s && typeof s === 'object' && (s.name || s.category || s.id || s.machines !== undefined)) return val;
              }
          }
          return [];
      })();
      if (masterLines.length === 0) return enriched;
      // Match each display line to its master uid by display_name, preserving per-line sequence
      const nameCount = {};
      return enriched.map(line => {
          const displayName = line.category || line.name || '';
          nameCount[displayName] = (nameCount[displayName] || 0);
          const matching = masterLines.filter(ml => ml.display_name === displayName);
          const master   = matching[nameCount[displayName]] || matching[0] || null;
          nameCount[displayName]++;
          return { ...line, uid: master?.uid || null };
      });
  };

// =================================================================================================
  // LOGIC & DB CONNECTIONS
  // =================================================================================================

  const saveProcess = () => {
    if (!procIn.name) return alert("Enter Process Name");
    if (procIn.type === "Relative" && !procIn.anchor) return alert("Select Anchor Process for Relative types");
    if (procIn.id) {
       setTlProcs(tlProcs.map(p => p.id === procIn.id ? procIn : p));
    } else {
       setTlProcs([...tlProcs, { ...procIn, id: Date.now() }]);
    }
    setProcIn({ id: null, name: "", type: "Relative", anchor: "", days: 0, isSample: false });
  };

  const editProcess = (p) => { setProcIn(p); };
  const deleteProcess = (id) => { if(window.confirm("Delete?")) setTlProcs(tlProcs.filter(x => x.id !== id)); };

  const saveTimeline = async () => {
    if (!tlBrand || tlProcs.length === 0) return alert("Select Brand and add processes.");
    const newTl = { 
        type: 'TIMELINE', 
        name: tlBrand, 
        brand: tlBrand, 
        processes: tlProcs 
    };
    const dbId = await saveToDatabase('common', newTl);
    if (dbId) {
        setDb(prev => ({ 
            ...prev, 
            timelines: [...(prev.timelines || []).filter(t => t.brand !== tlBrand), { ...newTl, id: dbId }] 
        }));
        alert(`Timeline Template Saved for ${tlBrand}!`); 
        setTlProcs([]); setTlBrand("");
    }
  };

  const loadTimeline = (b) => {
    setTlBrand(b);
    const t = (db.timelines || []).find(x => x.brand === b);
    if (t) { setTlProcs(t.processes || []); } else { setTlProcs([]); } 
  };

  const loadStandardTemplate = () => {
     if (!tlBrand) return alert("Please select a Brand first.");
     setTlProcs([
        { id: 1, name: "Ex-Factory", type: "Root", anchor: "", days: 0, isSample: false },
        { id: 2, name: "Finishing", type: "Relative", anchor: "Ex-Factory", days: -5, isSample: false },
        { id: 3, name: "Washing", type: "Relative", anchor: "Finishing", days: -3, isSample: false },
        { id: 4, name: "Sewing Start", type: "Relative", anchor: "Washing", days: -10, isSample: false },
        { id: 5, name: "Cutting", type: "Relative", anchor: "Sewing Start", days: -3, isSample: false },
        { id: 6, name: "Fabric In-House", type: "Relative", anchor: "Cutting", days: -7, isSample: false },
        { id: 7, name: "PP Sample Approval", type: "Relative", anchor: "Cutting", days: -10, isSample: true },
        { id: 8, name: "Lab Dip Approval", type: "Relative", anchor: "Fabric In-House", days: -15, isSample: true }
     ]);
  };

  const generatePlan = async () => {
    if (!cpFilters.brand || cpFilters.styles.length === 0) return alert("Please Select Brand & Styles.");
    
    const template = (db.timelines || []).find(t => (t.brand || "").toLowerCase() === cpFilters.brand.toLowerCase());
    if (!template) return alert(`No Timeline Template found for ${cpFilters.brand}. Please create one in TIMELINES tab.`);
    
    const processes = template.processes || [];
    const rootProcs = processes.filter(p => p.type === "Root");
    const missingRoots = rootProcs.filter(rp => !rootDates[rp.name]);
    if (missingRoots.length > 0) return alert(`Please enter start dates for: ${missingRoots.map(r=>r.name).join(", ")}`);

    const newPlans = [...(db.stylePlans || [])];
    const currentBatch = [];
    
    for (const styleRef of cpFilters.styles) {
       const idx = newPlans.findIndex(p => p.style === styleRef);
       if(idx > -1) newPlans.splice(idx, 1);

       const plannedProcs = processes.map(p => ({ ...p, calculatedDate: null }));
       rootProcs.forEach(rp => {
          const p = plannedProcs.find(x => x.name === rp.name);
          if(p) { const d = new Date(rootDates[rp.name]); if(!isNaN(d.getTime())) p.calculatedDate = d; }
       });

       const resolve = (proc, visited=[]) => {
          if(proc.calculatedDate) return proc.calculatedDate;
          if(visited.includes(proc.name)) return null;
          const anchor = plannedProcs.find(a => a.name === proc.anchor);
          if(!anchor) return null; 
          const anchorDate = resolve(anchor, [...visited, proc.name]);
          if(!anchorDate) return null;
          const newD = new Date(anchorDate);
          newD.setDate(newD.getDate() + (parseInt(proc.days)||0));
          proc.calculatedDate = newD;
          return newD;
       };
       plannedProcs.forEach(p => resolve(p));

       // Merge style-level samples from StyleRegistry into processes
       const styleObj = (db.mgmtStyles || []).find(s => s.styleRef === styleRef);
       const srSamples = (styleObj?.samples || []).map(s => ({
          name: s.type,
          planDate: s.date || "",
          actualDate: s.actualDate || "",
          status: s.status || "Pending",
          isSample: true,
          qty: s.qty || "",
          awb: "",
          source: "StyleRegistry"
       }));

       const finalProcesses = plannedProcs.map(p => ({
          name: p.name,
          planDate: p.calculatedDate ? p.calculatedDate.toISOString().split('T')[0] : "",
          actualDate: "", status: "Pending", isSample: p.isSample, qty: "", awb: "", source: "Timeline"
       }));

       // Add SR samples that aren't already in the timeline
       srSamples.forEach(srs => {
          if (!finalProcesses.some(fp => fp.name.toLowerCase() === srs.name.toLowerCase())) {
             finalProcesses.push(srs);
          }
       });

       const planEntry = { 
           type: 'GANTT_PLAN',
           name: styleRef, 
           style: styleRef, 
           brand: cpFilters.brand, 
           processes: finalProcesses,
           generatedAt: new Date().toISOString()
       };
       
       const saveResult = await saveToDatabase('common', planEntry);
       if (saveResult && saveResult.success !== false) {
           planEntry.id = saveResult?.id ?? saveResult ?? 'local_' + Date.now();
           newPlans.push(planEntry);
           currentBatch.push(planEntry);
       } else {
           planEntry.id = 'local_' + Date.now();
           newPlans.push(planEntry);
           currentBatch.push(planEntry);
       }
    }

    if(currentBatch.length > 0) {
        setDb(p => ({ ...p, stylePlans: newPlans }));
        setGeneratedPlans(currentBatch); 
        alert(`Successfully Generated & Saved ${currentBatch.length} Plans!`);
    }
  };

  const saveProcessChanges = async (updatedProcess) => {
      const plans = [...(db.stylePlans || [])];
      const targetPlan = plans.find(p => p.id === viewProcess.planId);
      
      if (targetPlan) {
          targetPlan.processes[viewProcess.processIndex] = updatedProcess;
          
          const result = await saveToDatabase('common', { ...targetPlan, type: 'GANTT_PLAN' });
          
          if(result) {
              setDb(p => ({ ...p, stylePlans: plans }));
              setViewProcess(null); 
          }
      }
  };

  const getMergedSamples = () => {
     let allSamples = [];
     // 1. Samples from Gantt plans (isSample processes)
     (db.stylePlans || []).forEach(plan => {
        (plan.processes || []).filter(p => p.isSample).forEach(s => { 
           allSamples.push({ ...s, style: plan.style, planId: plan.id, source: 'Plan' });
        });
     });
     // 2. Samples from StyleRegistry (style.samples[] added on SR page)
     (db.mgmtStyles || []).forEach(style => {
        (style.samples || []).forEach(s => {
           // Avoid duplicates if already in plan
           const alreadyIn = allSamples.some(x => x.style === style.styleRef && x.name === s.type);
           if (!alreadyIn) {
              allSamples.push({
                 name: s.type,
                 planDate: s.date || '',
                 actualDate: s.actualDate || '',
                 qty: s.qty || '',
                 status: s.status || 'Pending',
                 approvalStatus: s.approvalStatus || '',
                 isSample: true,
                 style: style.styleRef,
                 brand: style.brand || style.buyer || '',
                 source: 'StyleRegistry'
              });
           }
        });
     });
     return allSamples.filter(s => 
        (trFilters.brands.length===0 || trFilters.brands.includes(s.brand || (db.mgmtStyles || []).find(x=>x.styleRef===s.style)?.brand)) &&
        (trFilters.styles.length===0 || trFilters.styles.includes(s.style))
     ).sort((a,b) => new Date(a.planDate) - new Date(b.planDate));
  };

  const selectOrderForAlloc = (style) => {
    // 1. Run Deep Scan to get accurate POs
    const extractedPOs = extractStylePOs(style);
    
    const targetRef = (style.styleRef || "").trim().toLowerCase();
    const existingPlan = (db.stylePlans || []).find(p => (p.style || "").trim().toLowerCase() === targetRef);
    let planExDate = "";
    if (existingPlan) {
        const exProc = (existingPlan.processes || []).find(p => p.name.toLowerCase().includes("ex-factory")); 
        if (exProc) planExDate = exProc.planDate;
    }

    // Fallback to earliest PO date if no plan
    const earliestPO = extractedPOs.length > 0 ? [...extractedPOs].sort((a,b) => new Date(a.date) - new Date(b.date))[0].date : "";
    const exDate = planExDate || earliestPO;
    const today = new Date().toISOString().split('T')[0];
    const sewEnd = exDate ? addDays(exDate, -5) : ""; 
    const finEnd = exDate ? addDays(exDate, -3) : "";
    
    let smv = 0;
    if (Array.isArray(style.workstationData?.aggregatedOps) && style.workstationData.aggregatedOps.length > 0) {
        smv = style.workstationData.aggregatedOps.reduce((s, op) => s + (parseFloat(op.smv)||0), 0);
    } else if (Array.isArray(style.aggregatedOps) && style.aggregatedOps.length > 0) {
        smv = style.aggregatedOps.reduce((s, op) => s + (parseFloat(op.smv)||0), 0);
    } else {
        smv = parseFloat(style.workstationData?.smv || style.smv || 20) || 20;
    }

    // 4. Update State with extracted POs
    // totalQty: use stored value, or sum from POs, or sum from poSizeTable in DB
    const derivedQty = extractedPOs.reduce((a, p) => a + (parseFloat(p.qty) || 0), 0);
    const totalQty = parseFloat(style.totalQty) > 0 
        ? parseFloat(style.totalQty)
        : derivedQty > 0 
            ? derivedQty
            : 0;
    setSelectedOrder({ ...style, computedSMV: smv, totalQty, pos: extractedPOs });
    
    const existingData = style.planningData || {};
    setPlanData({
      ...existingData,
      pcd: existingData.pcd || today,
      exFty: exDate,
      sewEndDate: sewEnd,
      finEndDate: finEnd,
      workingHours: existingData.workingHours || 8,
      targetEff: existingData.targetEff || 60,
      allocatedLines: existingData.allocatedLines || [],
      allocatedCutters: existingData.allocatedCutters || [],
      allocatedFinishers: existingData.allocatedFinishers || []
    });
    
    setAllocView("PLAN");
  };

  const calculateAnalysis = () => {
    if(!selectedOrder) return {};
    const sewDays = getWorkingDays(planData.pcd, planData.sewEndDate);
    const dailyTarget = sewDays > 0 ? (selectedOrder.totalQty / sewDays) : 0;
    const minsPerDay = planData.workingHours * 60;
    const outputPerMachine = selectedOrder.computedSMV > 0 ? (minsPerDay * (planData.targetEff/100)) / selectedOrder.computedSMV : 0;
    
    let totalMach = 0, totalLines = 0;
    // FETCH FACTORY CAPACITIES FROM SOURCING DATA (FIXED: CHECK BOTH sewingLines AND lines)
    getFactories().forEach(f => getFactoryLines(f.name).forEach(l => { totalMach += parseFloat(l.machines||0); totalLines++; }));
    
    const avgMachinesPerLine = totalLines > 0 ? Math.round(totalMach/totalLines) : 20; 
    const avgLineOutput = Math.floor(outputPerMachine * avgMachinesPerLine);
    const requiredLines = avgLineOutput > 0 ? Math.ceil(dailyTarget / avgLineOutput) : 0;
    const totalAllocated = planData.allocatedLines.reduce((acc, al) => acc + (parseFloat(al.plannedQty) || 0), 0);
    const balance = selectedOrder.totalQty - totalAllocated;
    
    // UPDATED: PO Breakdown with Balance Calculation & Supplier Lookup
    const poBreakdown = (selectedOrder.pos || []).map(po => {
        const allocatedForPO = planData.allocatedLines
            .filter(al => al.poId === po.num)
            .reduce((acc, al) => acc + (parseFloat(al.plannedQty) || 0), 0);
            
        // LOOKUP SUPPLIER FROM ALLOCATIONS TABLE
        const allocRecord = (db.allocations || []).find(a => a.poNo === po.num);
        const supplierName = allocRecord ? allocRecord.supplier : null;
        
        return { 
            poNum: po.num, 
            qty: po.qty, 
            supplier: supplierName,
            allocated: allocatedForPO, 
            balance: po.qty - allocatedForPO 
        };
    });
    
    const cutDailyTarget = sewDays > 0 ? (selectedOrder.totalQty / sewDays) * 1.05 : 0; 
    return { sewDays, dailyTarget, outputPerMachine, avgMachinesPerLine, avgLineOutput, requiredLines, totalAllocated, balance, poBreakdown, cutDailyTarget };
  };

  const analysis = calculateAnalysis();
  
  const allocateResource = (type, factoryId, resource) => {
    const calcCap = Math.floor((resource.machines||0) * analysis.outputPerMachine * analysis.sewDays);
    const facObj = getFactories().find(f => f.id === factoryId);
    // Look up the unique line UID from factory_lines master
    const masterLine = (db.factoryLines || []).find(fl =>
        String(fl.factory_id) === String(factoryId) &&
        fl.display_name === (resource.category || resource.name) &&
        fl.uid === resource.uid  // exact uid match if already enriched
    ) || (db.factoryLines || []).find(fl =>
        String(fl.factory_id) === String(factoryId) &&
        fl.display_name === (resource.category || resource.name)
    );
    // Cap at PO limit — can't plan more than what was ordered
    const _poLimit = getPOQtyLimit(selectedOrder?.styleRef, targetPO || null, null, null, db);
    const _alreadyPlanned = planData.allocatedLines
      .filter(al => !targetPO || al.poId === targetPO)
      .reduce((a, al) => a + (parseFloat(al.plannedQty) || 0), 0);
    const _maxForThis = _poLimit > 0 ? Math.max(0, _poLimit - _alreadyPlanned) : calcCap;
    const cappedQty = _poLimit > 0 ? Math.min(calcCap, _maxForThis) : calcCap;
    const newAlloc = {
      id: Date.now(), factoryId, resId: resource.id,
      name: resource.category || resource.name,
      line_uid: masterLine?.uid || resource.uid || null,  // ← UID for exact Board matching
      factoryName: facObj?.name || factoryId || 'Unknown Factory',
      machines: resource.machines || 0,
      startDate: planData.pcd,
      endDate: type === 'SEWING' ? planData.sewEndDate : planData.finEndDate,
      poId: targetPO || 'Global',
      plannedQty: cappedQty,
      poLimit: _poLimit || 0,
    };
    if(type === 'SEWING') setPlanData(p => ({...p, allocatedLines: [...p.allocatedLines, newAlloc]}));
    if(type === 'CUTTING') setPlanData(p => ({...p, allocatedCutters: [...p.allocatedCutters, newAlloc]}));
    if(type === 'FINISHING') setPlanData(p => ({...p, allocatedFinishers: [...p.allocatedFinishers, newAlloc]}));
  };
  
  const removeAlloc = (type, id) => {
     if(type === 'SEWING') setPlanData(p => ({...p, allocatedLines: p.allocatedLines.filter(x => x.id !== id)}));
     if(type === 'CUTTING') setPlanData(p => ({...p, allocatedCutters: p.allocatedCutters.filter(x => x.id !== id)}));
     if(type === 'FINISHING') setPlanData(p => ({...p, allocatedFinishers: p.allocatedFinishers.filter(x => x.id !== id)}));
  };
  
  const saveAllocationDB = async () => {
    if(!selectedOrder) return;
    const updatedStyle = { ...selectedOrder, planningData: planData, allocatedLines: planData.allocatedLines };
    
    // [DB CONNECT] Updates style record (keeps allocatedLines JSON for backward compat)
    const dbId = await saveToDatabase('style', updatedStyle);
    
    if(dbId) {
        setDb(prev => ({ ...prev, mgmtStyles: prev.mgmtStyles.map(s => s.id === selectedOrder.id ? updatedStyle : s) }));

        // Also write to line_allocations table (UID system) — enables exact Board matching
        const styleUid = (db.styleMaster || []).find(sm => sm.style_ref === selectedOrder.styleRef)?.uid || '';
        const API_BASE = window.API_BASE || '/api';
        await Promise.all(planData.allocatedLines
            .filter(al => al.line_uid)  // only write if UID is known
            .map(al => fetch(`${API_BASE}/save_universal.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    category: 'line_allocation',
                    data: {
                        styleUid:    styleUid,
                        styleRef:    selectedOrder.styleRef,
                        lineUid:     al.line_uid,
                        factoryId:   al.factoryId,
                        factoryName: al.factoryName || '',
                        plannedQty:  al.plannedQty  || 0,
                        startDate:   al.startDate   || null,
                        endDate:     al.endDate     || null,
                        poUid:       al.poId        || 'Global',
                    }
                })
            }))
        );

        alert("Allocation Saved Successfully!"); setAllocView("LIST");
    }
  };

  const saveCutting = async () => {
      const entry = { type: 'CUTTING_PLAN', name: `CUT-${Date.now()}`, style: selectedOrder.styleRef, details: `${cutForm.table} (${cutForm.layers} layers)`, qty: cutForm.totalPcs, start: planData.pcd, status: 'Planned' };
      const dbId = await saveToDatabase('common', entry);
      if(dbId) alert("Cutting Plan Issued!");
  };

  const saveFinishing = async () => {
      const entry = { type: 'FINISHING_PLAN', name: `FIN-${Date.now()}`, style: selectedOrder.styleRef, details: finForm.type, qty: finForm.qty, start: planData.sewEndDate, status: 'Planned' };
      const dbId = await saveToDatabase('common', entry);
      if(dbId) alert("Finishing Batch Planned!");
  };

  const getBoardRows = () => {
     const dates = getDaysArray(boardStart, boardDays);
     const allProdLogs = db.productionLogs || [];
     const rows = getFactories().filter(f => selectedFactories.length===0 || selectedFactories.includes(f.name)).map(fac => ({
        ...fac,
        lines: getFactoryLines(fac.name).map(line => {
           const lineName = line.category || line.name || '';
           const bookings = [];
           (db.mgmtStyles||[]).forEach(style => {
              const allAlloc = style.allocatedLines || style.planningData?.allocatedLines || [];
              // Also include allocations from the new line_allocations table for this style
              const dbAllocs = (db.lineAllocations || [])
                  .filter(la => la.style_ref === style.styleRef && String(la.factory_id) === String(fac.id) && la.line_uid === line.uid)
                  .map(la => ({ ...la, plannedQty: la.planned_qty, startDate: la.start_date, endDate: la.end_date, poId: la.po_uid, line_uid: la.line_uid }));
              // Merge: prefer db.lineAllocations (exact UID match), supplement with allocatedLines JSON
              const merged = dbAllocs.length > 0 ? dbAllocs : allAlloc.filter(al => {
                 // 1. Exact UID match (new system)
                 if (al.line_uid && line.uid) return al.line_uid === line.uid;
                 // 2. resId match with factory gate
                 if (al.resId) return al.resId === line.id && (!al.factoryId || String(al.factoryId) === String(fac.id));
                 // 3. Name match — factory gate required (legacy fallback only)
                 return al.name === lineName && (!al.factoryId || String(al.factoryId) === String(fac.id));
              });
              merged.forEach(al => {
                 // Compute actual production for this style on this line
                 const lineLogs = allProdLogs.filter(l =>
                    l.style === style.styleRef && (l.line === lineName || l.line === al.name) && l.factory === fac.name
                 );
                 const sewActual = lineLogs.reduce((a,b)=>a+parseFloat(b.sewing||0),0);
                 const cutActual = lineLogs.reduce((a,b)=>a+parseFloat(b.cutting||0),0);
                 const finActual = lineLogs.reduce((a,b)=>a+parseFloat(b.finishing||0),0);
                 const planned = parseFloat(al.plannedQty) || style.totalQty || 0;
                 const balance = Math.max(0, planned - sewActual);
                 // Days remaining
                 const today = new Date(); const endD = al.endDate ? new Date(al.endDate) : today;
                 const daysLeft = Math.max(0, Math.ceil((endD - today)/(1000*60*60*24)));
                 const dailyCap = al.machines ? Math.floor(al.machines * (20/60) * 8) : 0; // rough
                 const canFinish = daysLeft > 0 && (dailyCap * daysLeft) >= balance;
                 bookings.push({
                    ...al, styleRef: style.styleRef, qty: planned, color: getStyleColor(style.styleRef),
                    sewActual, cutActual, finActual, balance, daysLeft, canFinish, styleId: style.id
                 });
              });
           });
           return { ...line, name: lineName, bookings };
        })
     }));
     return { dates, rows };
  };

  const { dates, rows } = getBoardRows();

  // Board: Drag-drop move/split allocation
  const executeMoveAllocation = async (booking, targetLineId, targetFacId, targetLineName, qtyToMove) => {
     const srcStyleId = booking.styleId;
     const srcStyle = (db.mgmtStyles||[]).find(s => s.id === srcStyleId);
     if (!srcStyle) return;
     const allAlloc = [...(srcStyle.allocatedLines || srcStyle.planningData?.allocatedLines || [])];
     const idx = allAlloc.findIndex(al => al.id === booking.id);
     if (idx === -1) return;
     const remaining = parseFloat(booking.qty) - parseFloat(qtyToMove);
     if (remaining <= 0) {
        allAlloc.splice(idx, 1); // remove completely
     } else {
        allAlloc[idx] = { ...allAlloc[idx], plannedQty: remaining };
     }
     // Add new allocation on target line
     const targetFac = getFactories().find(f => f.id === targetFacId);
     const targetLine = getFactoryLines(targetFac?.name || '').find(l => l.id === targetLineId) || { id: targetLineId, name: targetLineName, machines: 20 };
     allAlloc.push({
        id: Date.now(), factoryId: targetFacId, resId: targetLineId,
        name: targetLineName, machines: targetLine.machines || 20,
        startDate: booking.startDate, endDate: booking.endDate,
        poId: booking.poId || 'Global', plannedQty: parseFloat(qtyToMove)
     });
     const updatedPlanData = { ...(srcStyle.planningData || {}), allocatedLines: allAlloc };
     const updatedStyle = { ...srcStyle, allocatedLines: allAlloc, planningData: updatedPlanData };
     await saveToDatabase('style', updatedStyle);
     setDb(prev => ({ ...prev, mgmtStyles: prev.mgmtStyles.map(s => s.id === srcStyleId ? updatedStyle : s) }));
     setShowMoveModal(null); setDragBooking(null);
     alert(`Moved ${qtyToMove} pcs of ${booking.styleRef} to ${targetLineName}`);
  };

  const getAIReallocationAdvice = async () => {
     setAiLoading(true); setAiAdvice(null);
     try {
        // Build context: for each booking check if it can finish
        const atRisk = [];
        rows.forEach(fac => {
           fac.lines.forEach(line => {
              line.bookings.forEach(b => {
                 if (!b.canFinish && b.balance > 0) {
                    atRisk.push({ style: b.styleRef, line: line.name, factory: fac.name, balance: b.balance, daysLeft: b.daysLeft, machines: b.machines || 20 });
                 }
              });
           });
        });
        if (atRisk.length === 0) { setAiAdvice("✅ All allocations are on track to complete within planned timeframes."); setAiLoading(false); return; }
        // Find available capacity
        const available = [];
        rows.forEach(fac => {
           fac.lines.forEach(line => {
              const totalBooked = line.bookings.reduce((a,b)=>a+b.balance,0);
              const lineCap = (line.machines||20) * 8 * 60 / 25; // rough daily cap
              if (totalBooked < lineCap * 10) { // 10 days buffer
                 available.push({ factory: fac.name, line: line.name, spareCap: Math.floor(lineCap * 10 - totalBooked) });
              }
           });
        });
        let advice = `🤖 AI REALLOCATION ADVICE

`;
        atRisk.forEach(risk => {
           const match = available.filter(av => av.factory === risk.factory && av.line !== risk.line && av.spareCap >= risk.balance * 0.3).sort((a,b)=>b.spareCap-a.spareCap)[0];
           advice += `⚠️ ${risk.style} on ${risk.line} (${risk.factory}):
`;
           advice += `   Balance: ${risk.balance} pcs | Days left: ${risk.daysLeft}
`;
           if (match) {
              advice += `   → SUGGEST: Move ${Math.min(risk.balance, match.spareCap)} pcs to ${match.line} (spare cap: ${match.spareCap} pcs)

`;
           } else {
              advice += `   → No available lines in same factory. Consider overtime or external sourcing.

`;
           }
        });
        setAiAdvice(advice);
     } catch(e) { setAiAdvice("Could not generate advice: " + e.message); }
     setAiLoading(false);
  };
  
  const saveProductionEntry = async () => {
     if(!prodFilters.style || !prodFilters.factory) return alert("Select Factory & Style");
     const newLog = { 
         id: Date.now(), date: prodDate, ...prodFilters, ...prodEntry,
         // Ensure these are explicitly set for DB column mapping
         size: prodEntry.size || '',
         color: prodEntry.color || '',
         po_no: prodFilters.po || ''
     };
     
     // [DB CONNECT] Uses 'planning' → production_logs table, now with size/color/po_no
     const dbId = await saveToDatabase('planning', { 
         type: 'DAILY_LOG', 
         ...newLog, 
         quantity: parseFloat(newLog.sewing) || 0,
         size: newLog.size,
         color: newLog.color,
         po: newLog.po_no
     });
     
     if(dbId) {
         setDb(prev => ({ ...prev, productionLogs: [...(prev.productionLogs || []), { ...newLog, id: dbId }] }));
         alert("Production Logged!");
         setProdEntry({ hour: "09:00-10:00", color: "", size: "", cutting: 0, sewing: 0, finishing: 0 });
     }
  };

  const getProdSummary = (forEntry = false) => {
     const { from, to } = prodDateRange;
     const logs = (db.productionLogs || []).filter(l => {
        if (forEntry) return l.date === prodDate; // entry mode uses single date
        const d = l.date || '';
        if (from && d < from) return false;
        if (to && d > to) return false;
        if (prodFilters.brand) {
           const styleObj = (db.mgmtStyles||[]).find(s=>s.styleRef===l.style);
           if (styleObj && styleObj.brand !== prodFilters.brand) return false;
        }
        if (prodFilters.season) {
           const styleObj = (db.mgmtStyles||[]).find(s=>s.styleRef===l.style);
           if (styleObj && styleObj.season !== prodFilters.season) return false;
        }
        if (prodFilters.style && l.style !== prodFilters.style) return false;
        if (prodFilters.po && l.po_no !== prodFilters.po) return false;
        if (prodFilters.factory && l.factory !== prodFilters.factory) return false;
        if (prodFilters.line && l.line !== prodFilters.line) return false;
        return true;
     });
     const cutTotal = logs.reduce((a,b)=>a+parseFloat(b.cutting||0),0);
     const sewTotal = logs.reduce((a,b)=>a+parseFloat(b.sewing||0),0);
     const finTotal = logs.reduce((a,b)=>a+parseFloat(b.finishing||0),0);
     // Per-line breakdown for board display
     const byLine = {};
     logs.forEach(l => {
        const key = `${l.factory}__${l.line}`;
        if (!byLine[key]) byLine[key] = { factory: l.factory, line: l.line, cut: 0, sew: 0, fin: 0 };
        byLine[key].cut += parseFloat(l.cutting||0);
        byLine[key].sew += parseFloat(l.sewing||0);
        byLine[key].fin += parseFloat(l.finishing||0);
     });
     return { cutTotal, sewTotal, finTotal, logs, byLine };
  };
  const prodSum = getProdSummary();

  // =================================================================================================
  // CUT PLAN LOGIC
  // =================================================================================================

  // Parse cut panels string like "S×2 M×3 L×3 XL×2" or "S:2 M:3 L:3 XL:2" or "S2M3L3XL2"
  const broadcastUpdate = (type, data) => {
    try { window.dispatchEvent(new CustomEvent('nirvikalp_update',{detail:{type,data,ts:Date.now()}})); } catch(e) {}
  };

  const parseCutPanels = (str) => {
    if (!str) return [];
    const result = [];
    // Try different formats
    const pattern = /([A-Z]+[0-9]?[XL]*)\s*[×x:*]?\s*(\d+)/gi;
    let match;
    while ((match = pattern.exec(str)) !== null) {
      result.push({ size: match[1].toUpperCase(), qty: parseInt(match[2]) });
    }
    return result;
  };

  // Scan rolls from db.materials matching cut plan criteria
  const cpScanRolls = () => {
    const { color, style, po } = cpSetup;
    const marker = cpMarkers[0]; // Use first marker for initial scan width/csv/shrinkage
    if (!marker) { setCpAlertMsg('Add at least one marker before scanning rolls.'); return; }
    if (!color) { setCpAlertMsg('Select a color before scanning rolls.'); return; }

    const rolls = (db.materials || []).filter(r => {
      const balQty = parseFloat(r.Balance_Qty) || 0;
      if (balQty <= 0) return false;
      if (r.Quality_Status === 'Rejected') return false;
      if (color && (r.Color || '').toLowerCase() !== color.toLowerCase()) return false;
      if (style && r.Style_Name && r.Style_Name !== style) return false;
      if (po && r.Order_No && r.Order_No !== po) return false;
      // Width: roll width must be >= marker width
      if (marker.width && parseFloat(r.Width) < parseFloat(marker.width)) return false;
      // CSV Group match
      if (marker.csvType && r.CSV_Group && r.CSV_Group !== marker.csvType) return false;
      // Shrinkage: if marker has shrinkage range, check roll's shrinkage group
      // (if blank, allow all)
      return true;
    });

    // Group by shade (if not ignoring shades)
    if (!cpIgnoreShade) {
      const shadeGroups = {};
      rolls.forEach(r => {
        const shade = r.Shade || 'Unknown';
        if (!shadeGroups[shade]) shadeGroups[shade] = [];
        shadeGroups[shade].push(r);
      });
      // Attach shade group info
      rolls.forEach(r => { r._shadeGroup = r.Shade || 'Unknown'; });
    }

    setCpMatchedRolls(rolls);
    if (rolls.length === 0) {
      setCpAlertMsg('⚠ No matching rolls found in inventory. Check color, width, or CSV filters.');
    } else {
      setCpAlertMsg(`✓ Found ${rolls.length} eligible rolls (${rolls.reduce((a,r) => a + (parseFloat(r.Balance_Qty)||0), 0).toFixed(1)}m total fabric).`);
      setCpStep(3);
    }
  };

  // CORE CUT PLAN ALGORITHM
  // Generates optimized cut options (markers + roll assignments)
  const cpRunAlgorithm = () => {
    if (cpMarkers.length === 0) { setCpAlertMsg('No markers defined.'); return; }
    if (cpMatchedRolls.length === 0) { setCpAlertMsg('No rolls scanned. Scan rolls first.'); return; }
    if (cpSizeBreakdown.length === 0) { setCpAlertMsg('No size breakdown found.'); return; }

    const required = {}; // {size: qty}
    cpSizeBreakdown.forEach(s => { required[s.size] = parseInt(s.qty) || 0; });
    const maxPlies = cpMaxPlies ? parseInt(cpMaxPlies) : 200;
    const deviation = parseInt(cpPliesDeviation) || 5;

    // For each marker, compute how it satisfies the size breakdown
    const options = [];

    cpMarkers.forEach(marker => {
      const panels = parseCutPanels(marker.cutPanels);
      if (panels.length === 0) return;

      const markerLen = parseFloat(marker.length) || 1;
      const consumption = parseFloat(marker.consumption) || markerLen;
      const wastage = parseFloat(marker.wastage) || 0;

      // How many plies needed to meet each size requirement?
      // pliesForSize[s] = ceil(required[s] / panelQty[s])
      const pliesNeeded = {};
      panels.forEach(p => {
        if (required[p.size] !== undefined) {
          pliesNeeded[p.size] = Math.ceil(required[p.size] / p.qty);
        }
      });
      const minPlies = Math.max(...Object.values(pliesNeeded), 1);
      const effectivePlies = Math.min(minPlies, maxPlies);

      // Total fabric needed (effectivePlies × markerLen)
      const totalFabricNeeded = effectivePlies * markerLen * (1 + wastage/100);

      // Total fabric available from matched rolls (grouped by shade if not ignoring)
      const rollGroups = {};
      if (cpIgnoreShade) {
        rollGroups['ALL'] = [...cpMatchedRolls];
      } else {
        cpMatchedRolls.forEach(r => {
          const shade = r.Shade || 'Unknown';
          if (!rollGroups[shade]) rollGroups[shade] = [];
          rollGroups[shade].push(r);
        });
      }

      // For each shade group, build a cut plan
      Object.entries(rollGroups).forEach(([shade, rolls]) => {
        const totalAvailable = rolls.reduce((a,r) => a + (parseFloat(r.Balance_Qty)||0), 0);
        if (totalAvailable < markerLen) return; // Not enough fabric

        // Roll assignment algorithm: assign rolls to lays, minimize end bits
        // Strategy: sort rolls to minimize per-roll end bits
        // Try two strategies: 1) shortest first 2) longest first → pick min total end bits
        const buildLays = (sortedRolls, targetPlies) => {
          const lays = [];
          let rollPool = sortedRolls.map(r => ({ ...r, remainingQty: parseFloat(r.Balance_Qty) || 0 }));
          let pliesCompleted = 0;
          let layNo = 1;
          let globalPlyNo = 0;

          while (pliesCompleted < targetPlies && rollPool.some(r => r.remainingQty >= markerLen)) {
            // Build a lay: as many plies as possible up to (maxPlies + deviation)
            const maxThisLay = Math.min(maxPlies + deviation, targetPlies - pliesCompleted);
            let layPlies = 0;
            const layRolls = [];

            for (let i = 0; i < rollPool.length && layPlies < maxThisLay; i++) {
              const roll = rollPool[i];
              if (roll.remainingQty < markerLen) continue;
              const pliesFromRoll = Math.min(Math.floor(roll.remainingQty / markerLen), maxThisLay - layPlies);
              if (pliesFromRoll > 0) {
                const used = pliesFromRoll * markerLen;
                const endBit = roll.remainingQty - used;
                layRolls.push({
                  rollId: roll.Roll_ID, rollDbId: roll.id,
                  rack: roll.Rack_No, cell: roll.Cell_No,
                  shade: roll.Shade, width: roll.Width,
                  totalLength: parseFloat(roll.Balance_Qty),
                  plies: pliesFromRoll, usedLength: used, endBit: endBit.toFixed(2),
                  fromPly: globalPlyNo + layPlies + 1,
                  toPly: globalPlyNo + layPlies + pliesFromRoll
                });
                rollPool[i].remainingQty -= used;
                layPlies += pliesFromRoll;
              }
            }

            if (layPlies === 0) break;

            // Build size output for this lay
            const layOutput = {};
            panels.forEach(p => { layOutput[p.size] = (p.qty * layPlies); });
            lays.push({ layNo: layNo++, rolls: layRolls, totalPlies: layPlies, sizeOutput: layOutput });
            globalPlyNo += layPlies;
            pliesCompleted += layPlies;
          }
          return lays;
        };

        // Strategy 1: Sort rolls shortest remainder first (minimize end bits)
        const sorted1 = [...rolls].sort((a,b) => {
          const aRem = (parseFloat(a.Balance_Qty) % markerLen);
          const bRem = (parseFloat(b.Balance_Qty) % markerLen);
          return aRem - bRem;
        });
        // Strategy 2: Sort rolls longest first (maximize ply continuity)
        const sorted2 = [...rolls].sort((a,b) => parseFloat(b.Balance_Qty) - parseFloat(a.Balance_Qty));

        const lays1 = buildLays(sorted1, effectivePlies);
        const lays2 = buildLays(sorted2, effectivePlies);

        const calcEndBits = (lays) => lays.reduce((a,lay) => a + lay.rolls.reduce((b,r) => b + parseFloat(r.endBit), 0), 0);
        const calcPieces = (lays) => {
          const total = {};
          lays.forEach(lay => { Object.entries(lay.sizeOutput).forEach(([s,q]) => { total[s] = (total[s]||0) + q; }); });
          return total;
        };

        [lays1, lays2].forEach((lays, idx) => {
          if (lays.length === 0) return;
          const totalEndBit = calcEndBits(lays);
          const totalPieces = calcPieces(lays);
          const totalFabricUsed = lays.reduce((a,lay) => a + lay.rolls.reduce((b,r) => b + r.usedLength, 0), 0);
          const totalFabricWithEndBit = totalFabricUsed + totalEndBit;
          const utilization = totalFabricWithEndBit > 0 ? (totalFabricUsed / totalFabricWithEndBit * 100) : 0;

          // Check if all sizes are met
          const meetsRequirements = Object.keys(required).every(s => (totalPieces[s] || 0) >= required[s]);

          options.push({
            id: options.length + 1,
            marker, shade, strategy: idx === 0 ? 'Min End-Bit' : 'Max Continuity',
            lays, totalEndBit: totalEndBit.toFixed(2),
            totalFabricUsed: totalFabricUsed.toFixed(2),
            utilization: utilization.toFixed(1),
            totalPieces, meetsRequirements,
            label: `Marker ${marker.markerNo} | Shade: ${shade} | ${idx===0?'Min End-Bit':'Max Continuity'}`,
            relaxInfo: null
          });
        });

        // Check RELAX option: if cutting 1-2% less of one size increases overall output 5-6%
        if (cpAllowRelax) {
          const sizes = Object.keys(required);
          sizes.forEach(relaxSize => {
            const relaxedRequired = { ...required };
            const relaxedQty = Math.ceil(required[relaxSize] * 0.98); // 2% less
            if (relaxedQty === required[relaxSize]) return;
            relaxedRequired[relaxSize] = relaxedQty;

            const relaxedPliesNeeded = {};
            panels.forEach(p => {
              if (relaxedRequired[p.size] !== undefined) {
                relaxedPliesNeeded[p.size] = Math.ceil(relaxedRequired[p.size] / p.qty);
              }
            });
            const relaxedMinPlies = Math.max(...Object.values(relaxedPliesNeeded), 1);
            const relaxedLays = buildLays(sorted1, Math.min(relaxedMinPlies, maxPlies));

            if (relaxedLays.length === 0) return;
            const relaxedPieces = relaxedLays.reduce((a,lay) => {
              Object.entries(lay.sizeOutput).forEach(([s,q]) => { a[s] = (a[s]||0) + q; }); return a;
            }, {});
            const originalTotal = Object.values(options[0]?.totalPieces || {}).reduce((a,b) => a+b, 0);
            const relaxedTotal = Object.values(relaxedPieces).reduce((a,b) => a+b, 0);
            const gainPct = originalTotal > 0 ? ((relaxedTotal - originalTotal) / originalTotal * 100) : 0;

            if (gainPct >= 5) {
              const relaxEndBit = relaxedLays.reduce((a,lay) => a + lay.rolls.reduce((b,r) => b + parseFloat(r.endBit), 0), 0);
              options.push({
                id: options.length + 1,
                marker, shade, strategy: 'Relax Option',
                lays: relaxedLays, totalEndBit: relaxEndBit.toFixed(2),
                totalFabricUsed: relaxedLays.reduce((a,lay) => a + lay.rolls.reduce((b,r) => b + r.usedLength, 0), 0).toFixed(2),
                utilization: 'N/A', totalPieces: relaxedPieces, meetsRequirements: false,
                label: `⚡ RELAX: Marker ${marker.markerNo} | ${relaxSize} -2% → Output +${gainPct.toFixed(1)}%`,
                relaxInfo: { size: relaxSize, reduction: 2, gainPct: gainPct.toFixed(1), originalQty: required[relaxSize], newQty: relaxedQty }
              });
            }
          });
        }
      });
    });

    // Sort: meets requirements first, then by lowest end bit
    options.sort((a,b) => {
      if (a.meetsRequirements && !b.meetsRequirements) return -1;
      if (!a.meetsRequirements && b.meetsRequirements) return 1;
      return parseFloat(a.totalEndBit) - parseFloat(b.totalEndBit);
    });

    setCpCutOptions(options);
    setCpSelectedOption(null);
    setCpExecutionPlan(null);
    if (options.length === 0) {
      setCpAlertMsg('⚠ No viable cut plan found. Check fabric availability vs requirements.');
    } else {
      setCpAlertMsg(`✓ Generated ${options.length} cut options. Select the best one below.`);
      setCpStep(4);
    }
  };

  // Build bundle list from selected option
  const cpBuildBundles = (opt) => {
    if (!opt) return [];
    const bundles = [];
    let globalPly = 0;
    let bundleNo = 1;

    opt.lays.forEach(lay => {
      const layPlies = lay.totalPlies;
      const sizePanels = parseCutPanels(opt.marker.cutPanels);
      let plyStart = globalPly + 1;
      let plyEnd = globalPly + layPlies;
      let plyPos = plyStart;

      while (plyPos <= plyEnd) {
        const remaining = plyEnd - plyPos + 1;
        const bSize = Math.min(cpBundleSize, remaining);
        bundles.push({
          bundleNo: bundleNo++, layNo: lay.layNo,
          fromPly: plyPos, toPly: plyPos + bSize - 1,
          qty: bSize, sizes: sizePanels,
          marker: opt.marker, shade: opt.shade
        });
        plyPos += bSize;
      }
      globalPly += layPlies;
    });

    // Handle last bundle split option
    if (cpSplitLastBundle && bundles.length > 0) {
      const last = bundles[bundles.length - 1];
      if (last.qty > cpSplitQty && last.qty !== cpBundleSize) {
        const split1 = { ...last, toPly: last.fromPly + cpSplitQty - 1, qty: cpSplitQty, bundleNo: last.bundleNo };
        const split2 = { ...last, fromPly: last.fromPly + cpSplitQty, qty: last.qty - cpSplitQty, bundleNo: last.bundleNo + 0.1 };
        bundles.splice(bundles.length - 1, 1, split1, split2);
      }
    }
    return bundles;
  };

  // Finalize selected option into execution plan
  const cpFinalizeOption = (opt) => {
    if (!opt) return;
    setCpSelectedOption(opt);
    const bundles = cpBuildBundles(opt);
    const totalPlies = opt.lays.reduce((a,l) => a + l.totalPlies, 0);
    setCpExecutionPlan({ ...opt, bundles, totalPlies, generatedAt: new Date().toLocaleString() });
    setCpAlertMsg('✓ Execution plan ready. View Bundle List and Lay Slip below.');
  };

  // Auto-populate size breakdown from style / PO data
  const cpLoadSizeBreakdown = (styleName, poNo) => {
    const styleObj = (db.mgmtStyles || []).find(s => s.styleRef === styleName);
    if (!styleObj) return;
    // Try to find size breakdown from PO data
    const pos = extractStylePOs(styleObj);
    const targetPO = pos.find(p => p.num === poNo) || pos[0];
    if (targetPO?.data) {
      const poData = targetPO.data;
      // Look for size columns
      const sizes = ['XXS','XS','S','M','L','XL','XXL','2XL','3XL','4XL','5XL','6XL','7XL','8XL'];
      const breakdown = [];
      sizes.forEach(sz => {
        const val = poData[sz] || poData[sz.toLowerCase()] || poData[`Qty_${sz}`];
        if (val && parseFloat(val) > 0) breakdown.push({ size: sz, qty: parseFloat(val) });
      });
      if (breakdown.length > 0) { setCpSizeBreakdown(breakdown); return; }
    }
    // Fallback: use total qty evenly split
    if (targetPO) {
      setCpSizeBreakdown([
        {size:'S', qty: Math.round(targetPO.qty * 0.15)},
        {size:'M', qty: Math.round(targetPO.qty * 0.30)},
        {size:'L', qty: Math.round(targetPO.qty * 0.30)},
        {size:'XL', qty: Math.round(targetPO.qty * 0.25)}
      ]);
    }
  };

  // =====================================================================================
  // AUTO PLAN LOGIC ENGINE
  // =====================================================================================

  // ---------- HELPERS ----------
  const AP_SIZES = ['XXS','XS','S','M','L','XL','2XL','3XL','4XL'];

  const apWorkingDays = (start, end) => {
    // Working days excluding Sundays and holidays
    if (!start || !end) return 0;
    let count = 0;
    let cur = new Date(start);
    const fin = new Date(end);
    while (cur <= fin) {
      if (cur.getDay() !== 0 && !apHolidays.includes(cur.toISOString().split('T')[0])) count++;
      cur.setDate(cur.getDate() + 1);
    }
    return count;
  };

  const apAddWorkDays = (start, n) => {
    if (!start || n <= 0) return start;
    let cur = new Date(start);
    let added = 0;
    while (added < n) {
      cur.setDate(cur.getDate() + 1);
      if (cur.getDay() !== 0 && !apHolidays.includes(cur.toISOString().split('T')[0])) added++;
    }
    return cur.toISOString().split('T')[0];
  };

  // Find closest learning curve match for parameters
  const apMatchLearning = (dept, params) => {
    const rows = apLearning.filter(r => r.dept === dept);
    if (rows.length === 0) return null;
    // Score each row by how close parameters are
    const scored = rows.map(r => {
      let score = 0;
      if (params.fabricType && r.fabricType && r.fabricType.toLowerCase() === params.fabricType.toLowerCase()) score += 3;
      if (params.content && r.content && r.content.toLowerCase().includes(params.content.toLowerCase())) score += 2;
      if (params.diffIndex && r.diffIndex) score += 3 - Math.abs(parseFloat(r.diffIndex) - parseFloat(params.diffIndex));
      if (params.plies && r.plies) score += 2 - Math.min(2, Math.abs(parseInt(r.plies) - parseInt(params.plies)) / 50);
      if (params.parts && r.parts) score += 2 - Math.min(2, Math.abs(parseInt(r.parts) - parseInt(params.parts)) / 10);
      return { ...r, score };
    });
    scored.sort((a,b) => b.score - a.score);
    return scored[0];
  };

  // ---------- READINESS CHECK ----------
  const apCheckReadiness = (poEntry) => {
    const { styleRef, poNo, color } = poEntry;
    const styleObj = (db.mgmtStyles || []).find(s => s.styleRef === styleRef);
    const markers = (db.cutPlanMarkers || []).filter(m => m.style === styleRef && m.po === poNo);
    const rolls = (db.materials || []).filter(r => r.Order_No === poNo && (r.Color||'').toLowerCase() === (color||'').toLowerCase() && parseFloat(r.Balance_Qty) > 0);
    const _apCostObj = (db.costings||[]).find(co=>co.styleId===styleObj?.id);
    const smv = getEffectiveSMV(styleObj, _apCostObj);

    // Check sizes from: (a) poSizeTable rows, (b) sizeBreakdown string on PO, (c) sizes[] with qty objects
    const sizeTableRows = (db.poSizeTable || []).filter(r =>
      r.style_ref === styleRef && (!poNo || r.po_no === poNo)
    );
    const poObj = (styleObj ? extractStylePOs(styleObj) : []).find(p => p.num === poNo);
    const sbStr = poObj?.sizeBreakdown || '';
    const parsedSBSizes = typeof sbStr === 'string' && sbStr.trim()
      ? sbStr.split(',').map(p => { const [sz, q] = p.split(':'); return { size: sz?.trim(), qty: parseInt(q) || 0 }; }).filter(s => s.size && s.qty > 0)
      : (sbStr && typeof sbStr === 'object' ? Object.entries(sbStr).map(([k,v])=>({size:k,qty:parseInt(v)||0})).filter(s=>s.qty>0) : []);
    const hasSizeBreakdown = sizeTableRows.length > 0 || parsedSBSizes.length > 0 ||
      (poEntry.sizes || []).some(s => typeof s === 'object' ? (s.qty > 0) : false);

    return {
      hasSizeBreakdown,
      hasMarkerPlan: markers.length > 0 || (db.cpMarkers || []).some(m => m.styleRef === styleRef),
      hasSpecSheet: !!(styleObj?.specSheet || styleObj?.techPack || styleObj?.bom),
      hasPCD: !!poEntry.pcd,
      hasExFty: !!poEntry.exFty,
      hasFIDate: !!poEntry.fiDate,
      hasFabric: rolls.length > 0,
      hasSMV: smv > 0,
      hasNoOfPanels: !!(styleObj?.panels || styleObj?.noOfPanels || styleObj?.partCount),
    };
  };

  const apReadinessScore = (checks) => {
    const vals = Object.values(checks);
    return Math.round((vals.filter(Boolean).length / vals.length) * 100);
  };

  // ---------- CORE AUTO-PLAN GENERATOR ----------
  const apGeneratePlan = (poEntries) => {
    const today = new Date().toISOString().split('T')[0];
    const plans = {};

    poEntries.forEach(po => {
      if (!po.confirmed) return;
      const { key, styleRef, poNo, color, sizes, totalQty, pcd, exFty, fiDate } = po;
      const styleObj = (db.mgmtStyles || []).find(s => s.styleRef === styleRef);
      const smv = parseFloat(styleObj?.workstationData?.smv || styleObj?.smv || 20) || 20;
      const workHours = 8;
      const targetEff = 60; // default efficiency %, overridden by learning curve
      const minsPerDay = workHours * 60;

      // ---- SMV-BASED COMPLEXITY TIER ----
      // Higher SMV → more complex → longer lag before next dept can start safely
      // SMV < 15 : simple (basic shirt, knit)    → sewing lag 2 days
      // SMV 15–25: moderate (cargo, structured)  → sewing lag 3 days
      // SMV > 25 : complex (multi-panel, woven)  → sewing lag 4 days
      const sewLagFromCutStart  = smv > 25 ? 4 : smv > 15 ? 3 : 2;  // cut→sew overlap lag
      const finLagFromSewStart  = smv > 25 ? 4 : 3;                  // sew→fin overlap lag
      const pkLagFromFinStart   = 3;                                  // fin→packing overlap lag

      // ---- LEARNING CURVE MATCHING ----
      const lcCut = apMatchLearning('cutting', {
        fabricType: styleObj?.fabric || '',
        content: styleObj?.content || '',
        diffIndex: (db.fabricDifficulties || []).find(f => (styleObj?.fabric||'').toLowerCase().includes(f.fabric?.toLowerCase() || ''))?.factor || 3
      });
      const cutOutputPerDay = lcCut ? parseInt(lcCut.output) : 500;

      const lcSew = apMatchLearning('sewing', { fabricType: styleObj?.fabric || '' });
      const sewEff = lcSew ? parseFloat(lcSew.efficiency || targetEff) : targetEff;
      const lineOutput = smv > 0 ? Math.floor((minsPerDay * sewEff / 100) / smv * 20) : 300; // assume 20 machines/line

      // ---- BUFFER RULE ----
      // Cutting must produce ≥ 2× lineOutput before sewing starts to protect next-day feed
      const bufferPcs = 2 * Math.max(lineOutput, 100);
      const bufferDaysNeeded = Math.ceil(bufferPcs / Math.max(cutOutputPerDay, 100));
      const actualSewLag = Math.max(sewLagFromCutStart, bufferDaysNeeded);

      // ---- CUTTING ----
      const cutStart = today;
      const cutDaysNeeded = Math.max(5, Math.ceil(totalQty / Math.max(cutOutputPerDay, 1) * (1 + apBuffer / 100)));
      const cutEnd = pcd ? addDays(pcd, -1) : apAddWorkDays(cutStart, cutDaysNeeded);
      const cutDays = Math.max(1, apWorkingDays(cutStart, cutEnd));
      const dailyCutTarget = Math.ceil(totalQty / cutDays * (1 + apBuffer / 100));

      const cutSchedule = [];
      let remaining = totalQty;
      let d = new Date(cutStart);
      const cutEndDate = new Date(cutEnd);
      while (remaining > 0 && d <= cutEndDate) {
        const ds = d.toISOString().split('T')[0];
        if (d.getDay() !== 0 && !apHolidays.includes(ds)) {
          const dayQty = Math.min(remaining, Math.min(dailyCutTarget, cutOutputPerDay));
          cutSchedule.push({ date: ds, target: dayQty, actual: 0 });
          remaining -= dayQty;
        }
        d.setDate(d.getDate() + 1);
      }

      // ---- SEWING — starts actualSewLag working days after cut start (OVERLAPPING) ----
      const sewStart = apAddWorkDays(cutStart, actualSewLag);
      const sewDaysNeeded = Math.max(10, Math.ceil(totalQty / Math.max(lineOutput, 1) * (1 + apBuffer / 100)));
      // Sewing ends 5 days before ex-fty (time for finishing+packing tail), or 3 before FI
      const sewEnd = fiDate
        ? addDays(fiDate, -(finLagFromSewStart + 2))
        : (exFty ? addDays(exFty, -6) : apAddWorkDays(sewStart, sewDaysNeeded));
      const sewDays = Math.max(1, apWorkingDays(sewStart, sewEnd));
      const dailySewTarget = Math.ceil(totalQty / sewDays * (1 + apBuffer / 100));
      const linesNeeded = Math.ceil(dailySewTarget / Math.max(lineOutput, 1));

      const sewSchedule = [];
      let sewRem = totalQty;
      let sd = new Date(sewStart);
      const sewEndDate = new Date(sewEnd);
      while (sewRem > 0 && sd <= sewEndDate) {
        const ds = sd.toISOString().split('T')[0];
        if (sd.getDay() !== 0 && !apHolidays.includes(ds)) {
          const dayQty = Math.min(sewRem, dailySewTarget);
          sewSchedule.push({ date: ds, target: dayQty, actual: 0, lines: linesNeeded });
          sewRem -= dayQty;
        }
        sd.setDate(sd.getDate() + 1);
      }

      // ---- FINISHING — starts finLagFromSewStart working days after SEW START (OVERLAPPING) ----
      const finStart = apAddWorkDays(sewStart, finLagFromSewStart);
      // Finishing ends 2 days before ex-fty (packing tail)
      const finEnd = fiDate
        ? addDays(fiDate, -1)
        : (exFty ? addDays(exFty, -3) : apAddWorkDays(finStart, Math.max(7, Math.ceil(totalQty / 400 * (1 + apBuffer / 100)))));
      const finDays = Math.max(1, apWorkingDays(finStart, finEnd));
      const dailyFinTarget = Math.ceil(totalQty / finDays * (1 + apBuffer / 100));

      const finSchedule = [];
      let finRem = totalQty;
      let fd = new Date(finStart);
      const finEndDate = new Date(finEnd);
      while (finRem > 0 && fd <= finEndDate) {
        const ds = fd.toISOString().split('T')[0];
        if (fd.getDay() !== 0 && !apHolidays.includes(ds)) {
          const dayQty = Math.min(finRem, dailyFinTarget);
          finSchedule.push({ date: ds, target: dayQty, actual: 0 });
          finRem -= dayQty;
        }
        fd.setDate(fd.getDate() + 1);
      }

      // ---- PACKING — starts pkLagFromFinStart working days after FIN START (OVERLAPPING) ----
      const pkStart = apAddWorkDays(finStart, pkLagFromFinStart);
      const pkEnd = exFty ? addDays(exFty, -1) : apAddWorkDays(pkStart, Math.max(3, Math.ceil(totalQty / 600 * (1 + apBuffer / 100))));
      const pkDays = Math.max(1, apWorkingDays(pkStart, pkEnd));
      const dailyPkTarget = Math.ceil(totalQty / pkDays * (1 + apBuffer / 100));

      const pkSchedule = [];
      let pkRem = totalQty;
      let pd2 = new Date(pkStart);
      const pkEndDate = new Date(pkEnd);
      while (pkRem > 0 && pd2 <= pkEndDate) {
        const ds = pd2.toISOString().split('T')[0];
        if (pd2.getDay() !== 0 && !apHolidays.includes(ds)) {
          const dayQty = Math.min(pkRem, dailyPkTarget);
          pkSchedule.push({ date: ds, target: dayQty, actual: 0 });
          pkRem -= dayQty;
        }
        pd2.setDate(pd2.getDate() + 1);
      }

      const computedExFty = pkEnd || exFty || 'N/A';

      plans[key] = {
        poKey: key, styleRef, poNo, color, sizes, totalQty,
        pcd, exFty, fiDate, computedExFty,
        smv, linesNeeded, sewEff,
        // Simultaneous scheduling metadata
        sewLagDays: actualSewLag, finLagDays: finLagFromSewStart, pkLagDays: pkLagFromFinStart,
        bufferPcs, cutOutputPerDay, lineOutput,
        cutting: { start: cutStart, end: cutEnd, schedule: cutSchedule, dailyTarget: dailyCutTarget },
        sewing: { start: sewStart, end: sewEnd, schedule: sewSchedule, dailyTarget: dailySewTarget, linesNeeded },
        finishing: { start: finStart, end: finEnd, schedule: finSchedule, dailyTarget: dailyFinTarget },
        packing: { start: pkStart, end: pkEnd, schedule: pkSchedule, dailyTarget: dailyPkTarget },
        generatedAt: new Date().toISOString()
      };
    });

    // Detect conflicts (overlapping POs on same lines/dates)
    const allSewDates = {};
    Object.values(plans).forEach(plan => {
      plan.sewing.schedule.forEach(d => {
        if (!allSewDates[d.date]) allSewDates[d.date] = [];
        allSewDates[d.date].push({ key: plan.poKey, lines: d.lines });
      });
    });
    const conflicts = new Set();
    Object.entries(allSewDates).forEach(([date, entries]) => {
      const totalLines = entries.reduce((a, e) => a + e.lines, 0);
      const availLines = getFactories().reduce((a, f) => a + getFactoryLines(f.name).length, 0) || 10;
      if (totalLines > availLines) entries.forEach(e => conflicts.add(e.key));
    });

    Object.keys(plans).forEach(k => { plans[k].hasConflict = conflicts.has(k); });

    return { plans, generatedAt: new Date().toISOString(), conflictKeys: [...conflicts] };
  };

  // ---------- SAVE LEARNING CURVE ENTRY ----------
  const apSaveLcEntry = async () => {
    if (!apLcEntry.output && !apLcEntry.efficiency) return alert('Enter output or efficiency value');
    const entry = { ...apLcEntry, id: Date.now(), savedAt: new Date().toISOString() };
    const newList = [...apLearning, entry];
    setApLearning(newList);
    await saveToDatabase('planning', { type: 'LEARNING_CURVE', ...entry });
    setApLcEntry({ dept: apLcDept, plies:'', layLen:'', width:'', parts:'', fabricType:'', diffIndex:'', weight:'', content:'', smv:'', efficiency:'', country:'', zone:'', qty:'', output:'' });
    alert('Learning curve entry saved!');
  };

  // ---------- ADD PO TO PLAN LIST ----------
  const apAddPO = () => {
    if (!apPoForm.styleRef || !apPoForm.poNo) return alert('Style and PO number required');
    const key = `${apPoForm.styleRef}::${apPoForm.poNo}::${apPoForm.color}`;
    if (apPoList.find(p => p.key === key)) return alert('PO already in list');
    // Auto-fill missing fields from style data
    const sd = getStyleData(apPoForm.styleRef, db);
    const poObj = sd?.pos.find(p => p.num === apPoForm.poNo);

    // Resolve sizes with qty — priority: manual form input → poSizeTable → sizeBreakdown string → style sizes (no qty)
    let resolvedSizes = [];
    if (apPoForm.sizes?.length > 0 && apPoForm.sizes.some(s => typeof s === 'object' && s.qty > 0)) {
      resolvedSizes = apPoForm.sizes;
    } else {
      const sizeRows = (db.poSizeTable || []).filter(r => r.style_ref === apPoForm.styleRef && r.po_no === apPoForm.poNo);
      if (sizeRows.length > 0) {
        resolvedSizes = sizeRows.map(r => ({ size: r.size, qty: parseInt(r.qty) || 0 })).filter(s => s.size);
      } else {
        const sbStr = poObj?.sizeBreakdown || '';
        if (typeof sbStr === 'string' && sbStr.trim()) {
          resolvedSizes = sbStr.split(',').map(p => {
            const [sz, q] = p.split(':');
            return { size: sz?.trim(), qty: parseInt(q) || 0 };
          }).filter(s => s.size && s.qty > 0);
        } else if (sbStr && typeof sbStr === 'object') {
          resolvedSizes = Object.entries(sbStr).map(([k,v]) => ({ size: k, qty: parseInt(v) || 0 })).filter(s => s.qty > 0);
        }
        if (resolvedSizes.length === 0) {
          resolvedSizes = (sd?.sizes || []).map(s => ({ size: s, qty: 0 }));
        }
      }
    }

    const entry = {
      ...apPoForm,
      key,
      color:    apPoForm.color    || poObj?.col  || '',
      totalQty: apPoForm.totalQty || poObj?.qty  || sd?.totalQty || 0,
      exFty:    apPoForm.exFty    || poObj?.date || sd?.exFty    || '',
      sizes:    resolvedSizes,
      confirmed: false, postponed: false, readiness: {}
    };
    setApPoList(prev => [...prev, entry]);
    setApPoForm({ styleRef:'', poNo:'', color:'', sizes:[], totalQty:'', pcd:'', exFty:'', fiDate:'' });
  };

  // ---------- APPLY MANUAL EDIT ----------
  const apApplyManualEdit = (poKey, dept, dayIdx, newTarget) => {
    setApManualEdits(prev => ({
      ...prev,
      [poKey]: { ...(prev[poKey]||{}), [dept]: { ...((prev[poKey]||{})[dept]||{}), [dayIdx]: newTarget }}
    }));
  };

  // Merge manual edits into a plan for display
  const apMergedPlan = (plan) => {
    if (!plan || !apManualMode) return plan;
    const edits = apManualEdits[plan.poKey] || {};
    const mergeDept = (dept) => ({
      ...plan[dept],
      schedule: plan[dept].schedule.map((d, i) => ({
        ...d,
        target: edits[dept]?.[i] !== undefined ? edits[dept][i] : d.target,
        isManual: edits[dept]?.[i] !== undefined
      }))
    });
    return { ...plan, cutting: mergeDept('cutting'), sewing: mergeDept('sewing'), finishing: mergeDept('finishing'), packing: mergeDept('packing') };
  };

  const styles = {
    container: { display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden', fontFamily: 'sans-serif', background: '#f0f1f4' },
    topNav: { background: 'white', borderBottom: '1px solid #e6e9ef', padding: '0 20px', display: 'flex', alignItems: 'center', gap: '20px', height: '60px', flexShrink: 0, overflowX: 'auto' },
    navBtn: (active) => ({
      padding: '10px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.9rem', whiteSpace: 'nowrap',
      color: active ? '#0073ea' : '#676879', borderBottom: active ? '3px solid #0073ea' : '3px solid transparent', fontWeight: active ? 'bold' : 'normal'
    }),
    content: { flex: 1, padding: '20px', overflowY: 'auto' },
    card: { background: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '20px' },
    title: { marginTop: 0, marginBottom: '20px', fontSize: '1.1rem', color: '#323338', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 'bold' },
    grid3: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' },
    grid4: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' },
    field: { display: 'flex', flexDirection: 'column', marginBottom: '15px' },
    input: { padding: '8px', borderRadius: '6px', border: '1px solid #c5c7d4', fontSize: '0.9rem' },
    label: { fontSize: '0.8rem', fontWeight: 'bold', color: '#676879', marginBottom: '4px' },
    btnPrimary: { background: '#0073ea', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' },
    th: { textAlign: 'left', padding: '12px', borderBottom: '2px solid #e6e9ef', color: '#676879', fontSize: '0.8rem', textTransform: 'uppercase' },
    td: { padding: '12px', borderBottom: '1px solid #f0f1f4', verticalAlign: 'middle' }
  };

  const ProcessModal = () => {
      const [localP, setLocalP] = useState(viewProcess?.process || {});
      if (!viewProcess) return null;
      const p = viewProcess.process;

      const handleFile = (e, field) => {
          const file = e.target.files[0];
          if(file) {
              const reader = new FileReader();
              reader.onload = (x) => setLocalP({...localP, [field]: x.target.result});
              reader.readAsDataURL(file);
          }
      };

      const goToSpecificPlan = (type) => {
          setViewProcess(null);
          
          if(type === 'SHIPPING') {
              setShippingView({ style: viewProcess.planStyle, planId: viewProcess.planId });
              return;
          }

          setSub("ALLOCATION");
          setAllocView("LIST");
          const styleObj = db.mgmtStyles.find(s => s.styleRef === viewProcess.planStyle);
          if (styleObj) {
              selectOrderForAlloc(styleObj);
              setPlanTab(type);
          } else {
              alert("Style Master data not found for allocations.");
          }
      };

      const isCutting = p.name.toLowerCase().includes('cutting') || p.name.toLowerCase().includes('pcd');
      const isSewing = p.name.toLowerCase().includes('sewing');
      const isFinishing = p.name.toLowerCase().includes('finishing');
      const isExFactory = p.name.toLowerCase().includes('ex-factory');

      return (
          <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999}}>
              <div style={{background:'white', width:'500px', borderRadius:'12px', padding:'25px', boxShadow:'0 10px 25px rgba(0,0,0,0.2)'}}>
                  <div style={{display:'flex', justifyContent:'space-between', borderBottom:'1px solid #eee', paddingBottom:'15px', marginBottom:'20px'}}>
                      <h3 style={{margin:0, color:'#1e3a8a'}}>{p.name}</h3>
                      <button onClick={()=>setViewProcess(null)} style={{border:'none', background:'transparent', fontSize:'1.2rem', cursor:'pointer'}}>×</button>
                  </div>
                  
                  <div style={{marginBottom:'20px'}}>
                      {isCutting && <button onClick={()=>goToSpecificPlan('CUTTING')} style={{width:'100%', background:'#e0f2fe', color:'#0369a1', border:'1px solid #bae6fd', padding:'10px', borderRadius:'6px', cursor:'pointer', fontWeight:'bold'}}> Go to Cutting Plan</button>}
                      {isSewing && <button onClick={()=>goToSpecificPlan('SEWING')} style={{width:'100%', background:'#ffedd5', color:'#9a3412', border:'1px solid #fed7aa', padding:'10px', borderRadius:'6px', cursor:'pointer', fontWeight:'bold'}}> Go to Sewing Plan</button>}
                      {isFinishing && <button onClick={()=>goToSpecificPlan('FINISHING')} style={{width:'100%', background:'#f3e8ff', color:'#6b21a8', border:'1px solid #d8b4fe', padding:'10px', borderRadius:'6px', cursor:'pointer', fontWeight:'bold'}}> Go to Finishing Plan</button>}
                      {isExFactory && <button onClick={()=>goToSpecificPlan('SHIPPING')} style={{width:'100%', background:'#e3faf0', color:'#166534', border:'1px solid #86efac', padding:'10px', borderRadius:'6px', cursor:'pointer', fontWeight:'bold'}}> Open Shipping & Invoice Module</button>}
                  </div>

                  <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px', marginBottom:'20px'}}>
                      <div><label style={styles.label}>Plan Date</label><div style={{fontWeight:'bold'}}>{p.planDate}</div></div>
                      <div><label style={styles.label}>Actual Date</label><input type="date" value={localP.actualDate || ""} onChange={e=>setLocalP({...localP, actualDate:e.target.value})} style={styles.input}/></div>
                  </div>

                  <div style={styles.field}>
                      <label style={styles.label}>Approval Status</label>
                      <select style={styles.input} value={localP.approvalStatus || ""} onChange={e=>setLocalP({...localP, approvalStatus:e.target.value})}>
                          <option value="">Pending</option>
                          <option value="Submitted">Submitted</option>
                          <option value="Approved">Approved</option>
                          <option value="Rejected">Rejected</option>
                          <option value="Resubmitted">Resubmitted</option>
                      </select>
                  </div>

                  {localP.approvalStatus === "Rejected" && (
                      <div style={styles.field}>
                          <label style={{...styles.label, color:'#c93652'}}>Resubmission Date</label>
                          <input type="date" style={styles.input} value={localP.resubmissionDate || ""} onChange={e=>setLocalP({...localP, resubmissionDate:e.target.value})}/>
                      </div>
                  )}

                  <div style={styles.field}>
                      <label style={styles.label}>Comments / Remarks</label>
                      <textarea style={{...styles.input, height:'60px'}} value={localP.comments || ""} onChange={e=>setLocalP({...localP, comments:e.target.value})} placeholder="Enter comments here..."></textarea>
                  </div>

                  <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px', marginBottom:'20px'}}>
                      <div style={styles.field}>
                          <label style={styles.label}>Upload Document</label>
                          <input type="file" onChange={(e)=>handleFile(e, 'docUrl')} style={{fontSize:'0.8rem'}}/>
                          {localP.docUrl && <div style={{marginTop:'5px'}}><a href={localP.docUrl} download={`Doc_${p.name}`} style={{fontSize:'0.8rem', color:'#0073ea'}}> Download Attached File</a></div>}
                      </div>
                      <div style={styles.field}>
                          <label style={styles.label}>Upload Image</label>
                          <input type="file" accept="image/*" onChange={(e)=>handleFile(e, 'imgUrl')} style={{fontSize:'0.8rem'}}/>
                      </div>
                  </div>

                  {localP.imgUrl && (
                      <div style={{marginBottom:'20px', textAlign:'center', background:'#f5f6f8', padding:'10px', borderRadius:'8px'}}>
                          <img src={localP.imgUrl} style={{maxHeight:'100px', maxWidth:'100%'}} alt="Preview"/>
                          <div style={{marginTop:'5px'}}><a href={localP.imgUrl} download={`Img_${p.name}`} style={{fontSize:'0.7rem', color:'#676879'}}>Download Image</a></div>
                      </div>
                  )}

                  <div style={{display:'flex', justifyContent:'flex-end', gap:'10px'}}>
                      <button onClick={()=>setViewProcess(null)} style={{...styles.btnPrimary, background:'#676879'}}>Cancel</button>
                      <button onClick={()=>saveProcessChanges(localP)} style={{...styles.btnPrimary, background:'#059669'}}>Save Updates</button>
                  </div>
              </div>
          </div>
      );
  };

  const ShippingModule = () => {
      const [invoice, setInvoice] = useState({ no: 'INV-2026-001', date: new Date().toISOString().split('T')[0], items: [] });
      const [localShipForm, setLocalShipForm] = useState({ po_no: '', shipped_qty: 0, shipped_date: '', actual_exfty_date: '', invoice_no: '', awb_no: '', notes: '', documents: [] });
      const [savedShipLogs, setSavedShipLogs] = useState([]);
      if (!shippingView) return null;
      const currentLogs = (db.shipmentLogs || []).filter(l => l.style_ref === shippingView.style);
      const styleObj = (db.mgmtStyles || []).find(s => s.styleRef === shippingView.style);
      const _shipSD = getStyleData(shippingView.style, db);
      const stylePOs = _shipSD?.pos?.length > 0 ? _shipSD.pos : extractStylePOs(styleObj || {});
      const shipExFty = _shipSD?.exFty || '';
      const shipFinalInsp = _shipSD?.finalInspDate || '';

      const handleHandoverDoc = (e) => {
          const files = Array.from(e.target.files);
          files.forEach(file => {
              const reader = new FileReader();
              reader.onload = (x) => setLocalShipForm(prev => ({ ...prev, documents: [...(prev.documents || []), { name: file.name, url: x.target.result }] }));
              reader.readAsDataURL(file);
          });
      };

      const saveShipmentLog = async () => {
          if (!localShipForm.po_no) return alert('Select a PO number.');
          if (!localShipForm.shipped_date) return alert('Enter the actual shipped date.');
          // Cross-validate ship qty against finished qty
          if (localShipForm.shipped_qty && parseFloat(localShipForm.shipped_qty) > 0) {
            const _sv = validateStageQty('SHIPPING', shippingView.style, localShipForm.po_no, null, null, parseFloat(localShipForm.shipped_qty), db);
            if (!_sv.ok) return alert(`❌ Ship Qty Blocked\n\n${_sv.reason}`);
          }
          try {
              const payload = { category: 'shipment_log', data: { styleRef: shippingView.style, po_no: localShipForm.po_no, shipped_qty: parseFloat(localShipForm.shipped_qty)||0, shipped_date: localShipForm.shipped_date || null, actual_exfty_date: localShipForm.actual_exfty_date || localShipForm.shipped_date || null, invoice_no: localShipForm.invoice_no, awb_no: localShipForm.awb_no, notes: localShipForm.notes, documents: localShipForm.documents } };
              const response = await fetch(`${API_BASE}/save_universal.php`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
              const result = await response.json();
              if (result.success) {
                  const newLog = { ...payload.data, id: result.id, style_ref: shippingView.style };
                  setSavedShipLogs(prev => [...prev.filter(l => l.po_no !== localShipForm.po_no), newLog]);
                  setDb(prev => ({ ...prev, shipmentLogs: [...(prev.shipmentLogs || []).filter(l => !(l.style_ref === shippingView.style && l.po_no === localShipForm.po_no)), newLog] }));
                  alert("Shipment log saved! Actual Ex-Factory date recorded.");
                  setLocalShipForm({ po_no: '', shipped_qty: 0, shipped_date: '', actual_exfty_date: '', invoice_no: '', awb_no: '', notes: '', documents: [] });
              }
          } catch(err) { alert("Failed to save: " + err.message); }
      };

      return (
          <div style={{position:'fixed', inset:0, background:'#f0f1f4', zIndex:9999, display:'flex', flexDirection:'column'}}>
              <div style={{background:'#1f1f1f', padding:'15px 25px', color:'white', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                  <h3 style={{margin:0}}>SHIPPING & HANDOVER: {shippingView.style}</h3>
                  <button onClick={()=>setShippingView(null)} style={{background:'#e2445c', color:'white', border:'none', padding:'8px 15px', borderRadius:'4px', cursor:'pointer'}}>CLOSE</button>
              </div>
              <div style={{flex:1, display:'flex', padding:'20px', gap:'20px', overflowY:'auto'}}>
                  <div style={{flex:1, display:'flex', flexDirection:'column', gap:'20px'}}>
                      <div style={{...styles.card, borderTop:'4px solid #00a65e'}}>
                          <h4 style={{marginTop:0, color:'#14532d'}}>LOG ACTUAL SHIPMENT (Per PO)</h4>
                          <div style={{background:'#f0fdf4', border:'1px solid #86efac', borderRadius:'6px', padding:'10px', marginBottom:'15px', fontSize:'0.82rem', color:'#166534'}}>
                              <strong>Why this matters:</strong> Actual shipped date per PO is used to calculate true lead times for AI training and supplier performance scoring.
                          </div>
                          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px', marginBottom:'15px'}}>
                              <div style={styles.field}>
                            <label style={styles.label}>Select PO</label>
                            <select style={styles.input} value={localShipForm.po_no} onChange={e => {
                              const po = stylePOs.find(p=>p.num===e.target.value);
                              setLocalShipForm({...localShipForm, po_no:e.target.value, shipped_qty: po?.qty||0});
                            }}>
                              <option value="">-- Select PO --</option>
                              {stylePOs.map(po => {
                                const led = getPOLedger(shippingView.style, po.num, null, null, db);
                                const remaining = (po.qty||0) - (led.shipped||0);
                                return <option key={po.num} value={po.num}>{po.num}{po.col?` · ${po.col}`:''} — {(po.qty||0).toLocaleString()} pcs {remaining < (po.qty||0) ? `(${remaining.toLocaleString()} remaining)` : ''}</option>;
                              })}
                            </select>
                            {/* Show live ledger for selected PO */}
                            {localShipForm.po_no && (() => {
                              const _led = getPOLedger(shippingView.style, localShipForm.po_no, null, null, db);
                              const _po = stylePOs.find(p=>p.num===localShipForm.po_no);
                              const _limit = _po?.qty||0;
                              return (
                                <div style={{marginTop:'6px', background:'#f5f6f8', border:'1px solid #e6e9ef', borderRadius:'4px', padding:'6px 8px', fontSize:'0.72rem'}}>
                                  <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'4px', textAlign:'center'}}>
                                    {[['PO Qty', _limit, '#676879'],['Cut', _led.cut, '#0073ea'],['Sew', _led.sew, '#00a65e'],['Finish', _led.finish, '#9333ea']].map(([lbl,val,col])=>(
                                      <div key={lbl} style={{background:'white', borderRadius:'3px', padding:'4px'}}>
                                        <div style={{color:'#9699a6', fontSize:'0.62rem'}}>{lbl}</div>
                                        <div style={{fontWeight:'bold', color:col}}>{(val||0).toLocaleString()}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                          <div style={styles.field}>
                            <label style={{...styles.label, color:'#1e3a8a'}}>Ship Qty ✱</label>
                            <input type="number" min={0} style={{...styles.input, border:'2px solid #1e3a8a'}}
                              value={localShipForm.shipped_qty}
                              onChange={e => {
                                const raw = parseFloat(e.target.value)||0;
                                const _sv = validateStageQty('SHIPPING', shippingView.style, localShipForm.po_no, null, null, raw, db);
                                if (!_sv.ok && raw > 0) alert(`⚠️ ${_sv.reason}`);
                                setLocalShipForm({...localShipForm, shipped_qty: raw});
                              }}
                              placeholder="Qty being shipped"
                            />
                          </div>
                              <div style={styles.field}><label style={{...styles.label, color:'#00a65e'}}>Actual Shipped Date ✱</label><input type="date" style={{...styles.input, border:'2px solid #00a65e'}} value={localShipForm.shipped_date} onChange={e=>setLocalShipForm({...localShipForm, shipped_date:e.target.value, actual_exfty_date:e.target.value})}/></div>
                              <div style={styles.field}><label style={styles.label}>Actual Ex-Factory Date</label><input type="date" style={styles.input} value={localShipForm.actual_exfty_date} onChange={e=>setLocalShipForm({...localShipForm, actual_exfty_date:e.target.value})}/><small style={{color:'#9699a6', fontSize:'0.7rem'}}>If different from shipped date</small></div>
                              <div style={styles.field}><label style={styles.label}>Invoice No.</label><input style={styles.input} value={localShipForm.invoice_no} onChange={e=>setLocalShipForm({...localShipForm, invoice_no:e.target.value})} placeholder="INV-2026-001"/></div>
                              <div style={styles.field}><label style={styles.label}>AWB / B/L No.</label><input style={styles.input} value={localShipForm.awb_no} onChange={e=>setLocalShipForm({...localShipForm, awb_no:e.target.value})} placeholder="AWB or B/L number"/></div>
                              <div style={styles.field}><label style={styles.label}>Notes</label><textarea style={{...styles.input, height:'60px'}} value={localShipForm.notes} onChange={e=>setLocalShipForm({...localShipForm, notes:e.target.value})} placeholder="Delay reason, partial shipment notes..."></textarea></div>
                          </div>
                          <div style={{background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:'8px', padding:'12px', marginBottom:'15px'}}>
                              <h5 style={{margin:'0 0 8px 0', color:'#9a3412'}}>UPLOAD HANDOVER DOCUMENTS</h5>
                              <p style={{margin:'0 0 8px 0', fontSize:'0.78rem', color:'#78350f'}}>Packing list, commercial invoice, B/L, inspection report, etc.</p>
                              <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={handleHandoverDoc} style={{fontSize:'0.82rem'}}/>
                              {localShipForm.documents && localShipForm.documents.length > 0 && (
                                  <div style={{marginTop:'10px', display:'flex', flexWrap:'wrap', gap:'6px'}}>
                                      {localShipForm.documents.map((doc,i)=>(
                                          <div key={i} style={{background:'white', border:'1px solid #e6e9ef', borderRadius:'4px', padding:'4px 8px', fontSize:'0.72rem', display:'flex', alignItems:'center', gap:'6px'}}>
                                              <span>{doc.name}</span>
                                              <a href={doc.url} download={doc.name} style={{color:'#0073ea'}}>↓</a>
                                              <button onClick={()=>setLocalShipForm(prev=>({...prev, documents:prev.documents.filter((_,j)=>j!==i)}))} style={{border:'none', background:'none', color:'#e2445c', cursor:'pointer'}}>×</button>
                                          </div>
                                      ))}
                                  </div>
                              )}
                          </div>
                          <button onClick={saveShipmentLog} style={{...styles.btnPrimary, background:'#00a65e', width:'100%', padding:'12px'}}>SAVE SHIPMENT LOG & ACTUAL EX-FACTORY DATE</button>
                      </div>
                      {currentLogs.length > 0 && (
                          <div style={styles.card}>
                              <h4 style={{marginTop:0}}>RECORDED SHIPMENTS</h4>
                              <table style={styles.table}>
                                  <thead><tr style={{background:'#f5f6f8'}}><th style={styles.th}>PO</th><th style={styles.th}>Shipped</th><th style={styles.th}>Actual Ex-Fty</th><th style={styles.th}>Invoice</th><th style={styles.th}>AWB/BL</th><th style={styles.th}>Notes</th></tr></thead>
                                  <tbody>{currentLogs.map((log,i)=>(
                                      <tr key={i} style={{borderBottom:'1px solid #f0f1f4'}}>
                                          <td style={{...styles.td,fontWeight:'bold',color:'#0073ea'}}>{log.po_no}</td>
                                          <td style={{...styles.td,color:'#00a65e',fontWeight:'bold'}}>{log.shipped_date||'—'}</td>
                                          <td style={styles.td}>{log.actual_exfty_date||'—'}</td>
                                          <td style={styles.td}>{log.invoice_no||'—'}</td>
                                          <td style={styles.td}>{log.awb_no||'—'}</td>
                                          <td style={{...styles.td,fontSize:'0.8rem',color:'#676879'}}>{log.notes||'—'}</td>
                                      </tr>
                                  ))}</tbody>
                              </table>
                          </div>
                      )}
                  </div>
                  <div style={{width:'380px', flexShrink:0}}>
                      <div style={{...styles.card, borderTop:'4px solid #0073ea'}}>
                          <h4 style={{marginTop:0, color:'#1e3a8a'}}>COMMERCIAL INVOICE</h4>
                          <div style={styles.grid3}>
                              <div style={styles.field}><label style={styles.label}>Invoice No</label><input style={styles.input} value={invoice.no} onChange={e=>setInvoice({...invoice, no:e.target.value})}/></div>
                              <div style={styles.field}><label style={styles.label}>Date</label><input type="date" style={styles.input} value={invoice.date} onChange={e=>setInvoice({...invoice, date:e.target.value})}/></div>
                              <div style={styles.field}><label style={styles.label}>Total ($)</label><input style={{...styles.input, background:'#f0fdf4', fontWeight:'bold'}} value={invoice.items.reduce((a,b)=>a+(b.qty*b.rate),0).toFixed(2)} disabled/></div>
                          </div>
                          <div style={{marginTop:'12px', padding:'10px', background:'#f5f6f8', borderRadius:'6px', fontSize:'0.8rem'}}>
                              <strong>PO Summary:</strong>
                              {stylePOs.map((po,i)=>(
                                  <div key={i} style={{display:'flex', justifyContent:'space-between', padding:'3px 0', borderBottom:'1px solid #e6e9ef'}}>
                                      <span>{po.num} — {po.col}</span><span style={{fontWeight:'bold'}}>{po.qty?.toLocaleString()} pcs</span>
                                  </div>
                              ))}
                          </div>
                      </div>
                  </div>
              </div>
          </div>
      );
  };


  return (
    <div style={styles.container}>
      <ProcessModal />
      <ShippingModule />

      {/* TOP NAVIGATION */}
      <div style={styles.topNav}>
         <div style={{fontWeight:'bold', color:'#323338', marginRight:'10px', fontSize:'0.85rem'}}>PLANNING:</div>
         <button style={styles.navBtn(sub==="TIMELINES")} onClick={()=>setSub("TIMELINES")}>1. TIMELINES</button>
         <button style={styles.navBtn(sub==="GANTT")} onClick={()=>setSub("GANTT")}>2. GENERATOR</button>
         <button style={styles.navBtn(sub==="DASHBOARD")} onClick={()=>setSub("DASHBOARD")}>3. DASHBOARD</button>
         <button style={styles.navBtn(sub==="TRACKER")} onClick={()=>setSub("TRACKER")}>4. TRACKER</button>
         <div style={{borderLeft:'1px solid #c5c7d4', height:'30px', margin:'0 10px'}}></div>
         <div style={{fontWeight:'bold', color:'#323338', marginRight:'10px', fontSize:'0.85rem'}}>EXECUTION:</div>
         <button style={styles.navBtn(sub==="ALLOCATION")} onClick={()=>setSub("ALLOCATION")}>5. ALLOCATION</button>
         <button style={styles.navBtn(sub==="BOARD")} onClick={()=>setSub("BOARD")}>6. BOARD</button>
         <button style={styles.navBtn(sub==="FOLLOWUP")} onClick={()=>setSub("FOLLOWUP")}>7. FOLLOWUP</button>
         <div style={{borderLeft:'1px solid #c5c7d4', height:'30px', margin:'0 10px'}}></div>
         <button style={{...styles.navBtn(sub==="CUTPLAN"), background: sub==="CUTPLAN"?'#e8f0ff':'transparent', borderBottom: sub==="CUTPLAN"?'3px solid #0891b2':'3px solid transparent', color: sub==="CUTPLAN"?'#0891b2':'#676879'}} onClick={()=>setSub("CUTPLAN")}>✂ 8. CUT PLAN</button>
         <button style={{...styles.navBtn(sub==="AUTOPLAN"), background: sub==="AUTOPLAN"?'#fdf4ff':'transparent', borderBottom: sub==="AUTOPLAN"?'3px solid #a25ddc':'3px solid transparent', color: sub==="AUTOPLAN"?'#a25ddc':'#676879', fontWeight: sub==="AUTOPLAN"?'bold':'normal'}} onClick={()=>setSub("AUTOPLAN")}>⚡ 9. AUTO PLAN</button>
      </div>

      <div style={styles.content}>

         {/* --- TAB 1: TIMELINE SETUP --- */}
         {sub === "TIMELINES" && (
            <div style={styles.card}>
               <h4 style={styles.title}>TIMELINE CONFIGURATION</h4>
               <div style={{marginBottom:'20px'}}>
                  <label style={styles.label}>Select Brand for Template</label>
                  <select style={{...styles.input, width:'100%'}} value={tlBrand} onChange={e=>loadTimeline(e.target.value)}>
                     <option value="">-- Select Brand --</option>
                     {db.brands?.map(b=><option key={b.id} value={b.name}>{b.name}</option>)}
                  </select>
               </div>
               
               {tlBrand && (
                  <div style={{background:'#f5f6f8', padding:'20px', borderRadius:'8px', border:'1px solid #e6e9ef'}}>
                     <div style={{display:'flex', justifyContent:'space-between', marginBottom:'15px'}}>
                        <h5 style={{margin:0, color:'#323338'}}>Process Definition</h5>
                        {tlProcs.length === 0 && <button style={{...styles.btnPrimary, background:'#fdab3d'}} onClick={loadStandardTemplate}> Load Standard TNA</button>}
                     </div>

                     <div style={styles.grid4}>
                        <div style={styles.field}><label style={styles.label}>Process Name</label><input style={styles.input} value={procIn.name} onChange={e=>setProcIn({...procIn, name:e.target.value})} placeholder="e.g. Sewing"/></div>
                        <div style={styles.field}><label style={styles.label}>Type</label><select style={styles.input} value={procIn.type} onChange={e=>setProcIn({...procIn, type:e.target.value})}><option>Relative</option><option>Root</option></select></div>
                        {procIn.type==="Relative" && (<div style={styles.field}><label style={styles.label}>Anchor (Link To)</label><select style={styles.input} value={procIn.anchor} onChange={e=>setProcIn({...procIn, anchor:e.target.value})}><option value="">--Select--</option>{tlProcs.map(p=><option key={p.id}>{p.name}</option>)}</select></div>)}
                        <div style={styles.field}><label style={styles.label}>Days (+/-)</label><input type="number" style={styles.input} value={procIn.days} onChange={e=>setProcIn({...procIn, days:e.target.value})}/></div>
                     </div>
                     
                     <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'20px'}}>
                         <label style={{display:'flex', alignItems:'center', fontSize:'0.9rem', cursor:'pointer'}}>
                             <input type="checkbox" checked={procIn.isSample} onChange={e=>setProcIn({...procIn, isSample:e.target.checked})} style={{marginRight:'8px', width:'18px', height:'18px'}}/> Is Sample Stage?
                         </label>
                        <button style={styles.btnPrimary} onClick={saveProcess}>{procIn.id ? "UPDATE PROCESS" : "ADD PROCESS"}</button>
                     </div>

                     <table style={styles.table}>
                        <thead><tr><th style={styles.th}>Name</th><th style={styles.th}>Type</th><th style={styles.th}>Anchor</th><th style={styles.th}>Offset</th><th style={styles.th}>Actions</th></tr></thead>
                        <tbody>
                           {tlProcs.map((p,i)=> (
                              <tr key={i}>
                                 <td style={styles.td}>{p.name}</td><td style={styles.td}>{p.type}</td><td style={styles.td}>{p.anchor || "-"}</td>
                                 <td style={{...styles.td, color: p.days < 0 ? 'red' : 'green', fontWeight:'bold'}}>{p.days}</td>
                                 <td style={styles.td}><button style={{color:'#e2445c', background:'none', border:'none', cursor:'pointer'}} onClick={()=>deleteProcess(p.id)}>Del</button></td>
                              </tr>
                           ))}
                        </tbody>
                     </table>
                     <button style={{...styles.btnPrimary, background:'#059669', width:'100%', marginTop:'20px', fontSize:'1rem'}} onClick={saveTimeline}>SAVE TIMELINE TEMPLATE</button>
                  </div>
               )}
            </div>
         )}
         
         {/* --- TAB 2: GENERATOR --- */}
         {sub === "GANTT" && (
             <div style={styles.card}>
                 <h4 style={styles.title}>GENERATE PLAN (MULTI-ROOT)</h4>
                 <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', marginBottom:'20px'}}>
                    <div style={styles.field}><label style={styles.label}>Select Brand</label><select style={styles.input} value={cpFilters.brand} onChange={e=>setCpFilters({...cpFilters, brand:e.target.value, styles:[]})}><option value="">--Select--</option>{db.brands?.map(b=><option key={b.id}>{b.name}</option>)}</select></div>
                     <div style={styles.field}><label style={styles.label}>Select Styles (Multi)</label>
                     <MultiSelect 
                        options={(db.mgmtStyles || []).filter(s=>(s.brand||'').toLowerCase() === (cpFilters.brand||'').toLowerCase()).map(s=>s.styleRef)} 
                        selected={cpFilters.styles} 
                        onChange={s=>setCpFilters({...cpFilters, styles:s})}
                     />
                     </div>
                 </div>
                 
                 {cpFilters.brand && 
                  (db.timelines || []).find(t => t.brand === cpFilters.brand)?.processes && 
                  (db.timelines || []).find(t => t.brand === cpFilters.brand).processes.filter(p => p.type === "Root").length > 0 && (
                    <div style={{background:'#f0fdf4', border:'1px solid #86efac', padding:'15px', borderRadius:'8px', marginBottom:'20px'}}>
                       <h5 style={{margin:'0 0 10px 0', color:'#166534'}}>Set Dates for Root Processes:</h5>
                       <div style={styles.grid3}>
                          {(db.timelines || []).find(t => t.brand === cpFilters.brand).processes.filter(p => p.type === "Root").map(rp => (
                             <div key={rp.id} style={styles.field}><label style={{...styles.label, color:'#15803d'}}>{rp.name}</label><input type="date" value={rootDates[rp.name]||""} onChange={e=>setRootDates({...rootDates, [rp.name]:e.target.value})} style={{...styles.input, border:'2px solid #00c875', fontWeight:'bold'}}/></div>
                          ))}
                       </div>
                    </div>
                 )}
                 <button style={{...styles.btnPrimary, width:'100%', padding:'15px', fontSize:'1.1rem'}} onClick={generatePlan}>GENERATE PLANS</button>

                 {generatedPlans.length > 0 && (
                     <div style={{marginTop:'30px', borderTop:'1px solid #eee', paddingTop:'20px'}}>
                         <h5 style={{color:'#323338'}}>New Plans Generated:</h5>
                         {generatedPlans.map(plan => (
                             <div key={plan.id} style={{marginBottom:'20px', background:'#f5f6f8', borderRadius:'8px', padding:'15px', border:'1px solid #e6e9ef'}}>
                                 <h4 style={{margin:'0 0 10px 0', color:'#0073ea'}}>{plan.style} ({plan.brand})</h4>
                                 <table style={styles.table}>
                                     <thead><tr><th>Task</th><th>Date</th><th>Status</th><th>Action</th></tr></thead>
                                     <tbody>
                                         {(plan.processes || []).map((proc, idx) => (
                                             <tr key={idx} style={{background:'white', borderBottom:'1px solid #f0f1f4'}}>
                                                 <td style={styles.td}>{proc.name}</td>
                                                 <td style={{...styles.td, fontFamily:'monospace'}}>{proc.planDate}</td>
                                                 <td style={styles.td}>
                                                     <span style={{padding:'2px 6px', borderRadius:'4px', fontSize:'0.75rem', background: proc.approvalStatus==='Approved'?'#e3faf0':proc.approvalStatus==='Rejected'?'#fde8eb':'#f0f1f4', color: proc.approvalStatus==='Approved'?'#166534':proc.approvalStatus==='Rejected'?'#991b1b':'#676879'}}>
                                                         {proc.approvalStatus || "Pending"}
                                                     </span>
                                                 </td>
                                                 <td style={styles.td}>
                                                     <button 
                                                         onClick={() => setViewProcess({ planId: plan.id, processIndex: idx, process: proc, planStyle: plan.style })}
                                                         style={{background:'#fff', border:'1px solid #c5c7d4', borderRadius:'4px', padding:'4px 8px', fontSize:'0.8rem', cursor:'pointer', color:'#323338'}}
                                                     >
                                                         Manage
                                                     </button>
                                                 </td>
                                             </tr>
                                         ))}
                                     </tbody>
                                 </table>
                             </div>
                         ))}
                     </div>
                 )}
             </div>
         )}

        {/* --- TAB 3: DASHBOARD --- */}
      {sub === "DASHBOARD" && (
        <div style={styles.card}>
          <h4 style={styles.title}>Planning Dashboard</h4>
          <table style={styles.table}>
            <thead><tr><th style={styles.th}>Style</th><th style={styles.th}>Brand</th><th style={styles.th}>Key Dates</th><th style={styles.th}>Status</th><th style={styles.th}>Action</th></tr></thead>
            <tbody>
              {(db.stylePlans || []).map((plan) => {
                const styleObj = db.mgmtStyles?.find((s) => s.styleRef === plan.style);
                const roots = (plan.processes || []).filter(p => p.name.includes("Ex-Factory") || p.name.includes("Start") || p.name.includes("Sewing"));
                return (
                  <React.Fragment key={plan.id}>
                    <tr style={{background:'#fff', borderBottom:'1px solid #f0f1f4'}}>
                      <td style={{...styles.td, fontWeight:'bold', color:'#0073ea', cursor:'pointer'}} onClick={() => styleObj && openStylePassport(styleObj)}>{plan.style}</td>
                      <td style={styles.td}>{plan.brand}</td>
                      <td style={styles.td}>{roots.slice(0, 3).map((r, i) => (<div key={i} style={{fontSize:'0.8rem'}}>{r.name}: <b>{r.planDate}</b></div>))}</td>
                      <td style={styles.td}><span style={{background:'#d0e4ff', color:'#1e40af', padding:'4px 8px', borderRadius:'12px', fontSize:'0.75rem', fontWeight:'bold'}}>Active</span></td>
                      <td style={styles.td}><button style={{border:'1px solid #c5c7d4', background: showVisual === plan.id ? '#323338' : '#fff', color: showVisual === plan.id ? '#fff' : '#000', borderRadius:'4px', padding:'4px 8px', fontSize:'0.8rem', cursor:'pointer'}} onClick={() => setShowVisual(showVisual === plan.id ? null : plan.id)}>{showVisual === plan.id ? "Hide Gantt" : "Visual Gantt"}</button></td>
                    </tr>
                    {showVisual === plan.id && (
                      <tr><td colSpan="5" style={{padding:'20px', background:'#f5f6f8', borderBottom:'1px solid #e6e9ef'}}>
                          <div style={{border:'1px solid #e6e9ef', background:'#fff', padding:'20px', borderRadius:'8px'}}>
                            <h5 style={{margin:'0 0 15px 0', color:'#323338'}}>Visual Gantt Chart: {plan.style}</h5>
                            <div style={{overflowX: 'auto', border:'1px solid #f0f1f4', borderRadius:'6px', padding:'10px'}}>
                                <div style={{minWidth:'800px'}}>
                                 <div style={{display:'flex', fontSize:'0.75rem', color:'#9699a6', borderBottom:'1px solid #f0f1f4', paddingBottom:'5px', marginBottom:'10px'}}><span style={{width:'200px'}}>Task Name</span><span style={{flex:1, textAlign:'center'}}>Timeline</span><span style={{width:'100px', textAlign:'right'}}>Date</span></div>
                                 {(plan.processes || []).map((proc, idx) => (
                                   <div key={idx} style={{display:'flex', alignItems:'center', marginBottom:'8px', fontSize:'0.85rem', padding:'4px', background:'#fff'}}>
                                        <div 
                                            onClick={() => setViewProcess({ planId: plan.id, processIndex: idx, process: proc, planStyle: plan.style })}
                                            style={{width:'200px', fontWeight:'bold', color:'#0073ea', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', cursor:'pointer', textDecoration:'underline'}}
                                        >
                                            {proc.isSample && " "}{proc.name}
                                        </div>
                                        <div style={{flex:1, margin:'0 15px', height:'24px', background:'#f0f1f4', borderRadius:'4px', position:'relative'}}>
                                            <div style={{height:'100%', borderRadius:'4px', background: proc.isSample ? '#fdab3d' : '#0073ea', width: '120px', marginLeft: `${(idx * 20)}px`}}></div>
                                        </div>
                                        <div style={{width:'100px', textAlign:'right', fontFamily:'monospace', color:'#676879'}}>{proc.planDate}</div>
                                   </div>
                                 ))}
                               </div>
                            </div>
                          </div>
                        </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
              {db.stylePlans.length === 0 && <tr><td colSpan="5" style={{padding:'30px', textAlign:'center', color:'#9699a6'}}>No Active Plans found. Generate one first.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

         {/* --- TAB 4: TRACKER --- */}
         {sub === "TRACKER" && (
            <div style={styles.card}>
               <h4 style={styles.title}>SAMPLE & PRODUCTION TRACKER</h4>
               <div style={{display:'flex', gap:'20px', marginBottom:'20px'}}>
                  <div style={{flex:1}}><label style={styles.label}>Filter Brands</label><MultiSelect options={db.brands?.map(b=>b.name)} selected={trFilters.brands} onChange={s=>setTrFilters({...trFilters, brands:s})}/></div>
                  <div style={{flex:1}}><label style={styles.label}>Filter Styles</label><MultiSelect options={db.mgmtStyles?.map(s=>s.styleRef)} selected={trFilters.styles} onChange={s=>setTrFilters({...trFilters, styles:s})}/></div>
               </div>
               
               <div style={{overflowX:'auto', border:'1px solid #e6e9ef', borderRadius:'6px'}}>
                  <table style={styles.table}>
                     <thead><tr style={{background:'#f5f6f8'}}><th style={styles.th}>SRC</th><th style={styles.th}>BRAND</th><th style={styles.th}>STYLE</th><th style={styles.th}>TASK</th><th style={styles.th}>EX-FTY</th><th style={styles.th}>QTY</th><th style={styles.th}>PLANNED</th><th style={styles.th}>ACTUAL</th><th style={styles.th}>STATUS</th><th style={styles.th}>SAVE</th></tr></thead>
                     <tbody>
                        {getMergedSamples().map((s,i) => {
                           const isSR = s.source === 'StyleRegistry';
                           const srcColor = s.source === 'Plan' ? '#0073ea' : isSR ? '#fdab3d' : '#9699a6';

                           // Universal field updater — routes to stylePlans OR mgmtStyles depending on source
                           const updateField = (field, value) => {
                             if (isSR) {
                               // Update sample in the style's samples[] array
                               setDb(prev => ({
                                 ...prev,
                                 mgmtStyles: prev.mgmtStyles.map(st => {
                                   if (st.styleRef !== s.style) return st;
                                   return { ...st, samples: (st.samples||[]).map(sm =>
                                     sm.type === s.name ? { ...sm, [field]: value } : sm
                                   )};
                                 })
                               }));
                             } else if (s.planId) {
                               // Update process in stylePlans
                               const newPlans = [...db.stylePlans];
                               const plan = newPlans.find(x => x.id === s.planId);
                               if (plan) {
                                 const proc = plan.processes.find(y => y.name === s.name);
                                 if (proc) proc[field] = value;
                                 setDb(prev => ({ ...prev, stylePlans: newPlans }));
                               }
                             }
                           };

                           return (
                             <tr key={i} style={{background: isSR ? '#fffbeb' : '#fff', borderBottom:'1px solid #f0f1f4'}}>
                               <td style={styles.td}>
                                 <span style={{padding:'2px 6px', borderRadius:'4px', fontSize:'0.7rem', color:'#fff', background: srcColor}}>
                                   {s.source || 'Plan'}
                                 </span>
                               </td>
                               <td style={{...styles.td, fontSize:'0.75rem', color:'#1e3a8a', fontWeight:'bold'}}>
                               {(() => { const tsd = getStyleData(s.style, db); return tsd?.brand || s.brand || '—'; })()}
                             </td>
                             <td style={{...styles.td, fontWeight:'bold', color:'#1e3a8a'}}>{s.style}</td>
                             <td style={styles.td}>
                                 {s.name}
                               </td>
                               <td style={{...styles.td, fontFamily:'monospace', fontSize:'0.75rem', color: (() => { const tsd=getStyleData(s.style,db); const d=tsd?.exFty; if(!d)return'#9699a6'; const diff=Math.ceil((new Date(d)-new Date())/(86400000)); return diff<0?'#e2445c':diff<14?'#fdab3d':'#00a65e'; })()}}>
                                 {(() => { const tsd=getStyleData(s.style,db); return tsd?.exFty||'—'; })()}
                               </td>
                               <td style={styles.td}>
                                 <input value={s.qty || ''} placeholder="Qty"
                                   onChange={e => updateField('qty', e.target.value)}
                                   style={{width:'60px', padding:'4px', border:'1px solid #c5c7d4', borderRadius:'4px'}}/>
                               </td>
                               <td style={{...styles.td, fontFamily:'monospace'}}>{s.planDate || '—'}</td>
                               <td style={styles.td}>
                                 <input type="date" value={s.actualDate || ''}
                                   onChange={e => updateField('actualDate', e.target.value)}
                                   style={{border:'1px solid #c5c7d4', padding:'4px', borderRadius:'4px'}}/>
                               </td>
                               <td style={styles.td}>
                                 <input value={s.awb || ''} placeholder="AWB #"
                                   onChange={e => updateField('awb', e.target.value)}
                                   style={{width:'100px', padding:'4px', border:'1px solid #c5c7d4', borderRadius:'4px'}}/>
                               </td>
                               <td style={styles.td}>
                                 <select value={s.status || 'Pending'}
                                   onChange={e => updateField('status', e.target.value)}
                                   style={{border:'1px solid #c5c7d4', padding:'4px', borderRadius:'4px'}}>
                                   <option>Pending</option><option>Sent</option><option>Approved</option><option>Rejected</option>
                                 </select>
                               </td>
                               <td style={styles.td}>
                                 <button style={{background:'#00a65e', color:'#fff', border:'none', borderRadius:'4px', padding:'4px 8px', cursor:'pointer'}}
                                   onClick={async () => {
                                     if (isSR) {
                                       const styleObj = (db.mgmtStyles||[]).find(st => st.styleRef === s.style);
                                       if (styleObj) await saveToDatabase('style', styleObj);
                                       alert('Saved!');
                                     } else if (s.planId) {
                                       const plan = (db.stylePlans||[]).find(x => x.id === s.planId);
                                       if (plan) await saveToDatabase('common', { ...plan, type: 'GANTT_PLAN' });
                                       alert('Saved!');
                                     }
                                   }}>✓ Save</button>
                               </td>
                             </tr>
                           );
                        })}
                        {getMergedSamples().length === 0 && <tr><td colSpan="9" style={{padding:'20px', textAlign:'center', color:'#9699a6'}}>No Samples found. Add samples in Style Registry or create a plan in Gantt Generator.</td></tr>}
                     </tbody>
                  </table>
               </div>
            </div>
         )}

         {/* --- TAB 5: LINE ALLOCATION --- */}
         {sub === "ALLOCATION" && (
            <>
               {allocView === "LIST" && (
                  <div style={styles.card}>
                     <h4 style={styles.title}>LINE ALLOCATION HUB — Click Any Style to Plan or Edit</h4>
                     <div style={styles.grid4}>
                        {(db.mgmtStyles || []).map(s => {
                           const alloc = s.allocatedLines || s.planningData?.allocatedLines || [];
                           const isAllocated = alloc.length > 0;
                           const totalPlanned = alloc.reduce((a,b)=>a+(parseFloat(b.plannedQty)||0),0);
                           return (
                              <div key={s.id}
                                 onClick={() => { try { selectOrderForAlloc(s); } catch(e) { console.error('Alloc error', e); alert('Error loading style: ' + e.message); } }}
                                 style={{border:`2px solid ${isAllocated ? '#86efac' : '#e6e9ef'}`, padding:'15px', borderRadius:'8px', cursor:'pointer', background: isAllocated ? '#f0fdf4' : '#fff', transition:'box-shadow 0.15s'}}
                                 onMouseEnter={e=>e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,0.12)'}
                                 onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}
                              >
                                 <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'6px'}}>
                                    <h5 style={{margin:0, color:'#1e3a8a', fontSize:'0.9rem'}}>{s.styleRef}</h5>
                                    {isAllocated
                                       ? <span style={{background:'#e3faf0', color:'#166534', padding:'2px 6px', borderRadius:'4px', fontSize:'0.68rem', fontWeight:'bold'}}>✏ EDIT</span>
                                       : <span style={{background:'#fef2f2', color:'#c93652', padding:'2px 6px', borderRadius:'4px', fontSize:'0.68rem', fontWeight:'bold'}}>+ PLAN</span>
                                    }
                                 </div>
                                 <div style={{fontSize:'0.78rem', color:'#676879', marginBottom:'4px'}}>{s.buyer} | {s.season}</div>
                                 {/* Show real PO count — from pos[] or poSizeTable */}
                                 {(() => {
                                   const stylePOs = extractStylePOs(s);
                                   const poTableQty = (db.poSizeTable||[]).filter(r=>r.style_ref===s.styleRef).reduce((a,r)=>a+(parseInt(r.qty)||0),0);
                                   const displayQty = s.totalQty || stylePOs.reduce((a,p)=>a+(parseFloat(p.qty)||0),0) || poTableQty;
                                   const poCount = stylePOs.length || [...new Set((db.poSizeTable||[]).filter(r=>r.style_ref===s.styleRef).map(r=>r.po_no))].length;
                                   return (
                                     <>
                                       <div style={{fontSize:'0.78rem', color:'#323338'}}>Order Qty: <strong>{displayQty.toLocaleString()}</strong></div>
                                       {poCount > 0 && <div style={{fontSize:'0.72rem', color:'#0073ea', marginTop:'2px'}}>📋 {poCount} PO{poCount>1?'s':''}</div>}
                                     </>
                                   );
                                 })()}
                                 {isAllocated && (
                                    <div style={{marginTop:'6px', fontSize:'0.75rem'}}>
                                       <div style={{color:'#00a65e'}}>✓ Planned: {totalPlanned.toLocaleString()} pcs on {alloc.length} line{alloc.length>1?'s':''}</div>
                                       <div style={{background:'#e6e9ef', height:'4px', borderRadius:'2px', marginTop:'4px'}}>
                                          <div style={{background:'#00a65e', height:'4px', borderRadius:'2px', width: `${Math.min(100, (totalPlanned/(s.totalQty||1))*100)}%`}}></div>
                                       </div>
                                    </div>
                                 )}
                              </div>
                           );
                        })}
                        {(db.mgmtStyles||[]).length === 0 && <div style={{gridColumn:'1/-1', textAlign:'center', padding:'30px', color:'#9699a6'}}>No styles found. Add styles in the Style Registry.</div>}
                     </div>
                  </div>
               )}

               {allocView === "PLAN" && selectedOrder && (
                  <div style={{display:'flex', flexDirection:'column', gap:'20px'}}>
                     <div style={{display:'flex', justifyContent:'space-between'}}>
                        <button style={{...styles.btnPrimary, background:'#676879'}} onClick={()=>setAllocView("LIST")}> BACK</button>
                        <h4 style={{margin:0, alignSelf:'center'}}>PLAN: {selectedOrder.styleRef} ({selectedOrder.totalQty} pcs)</h4>
                        <button style={{...styles.btnPrimary, background:'#059669'}} onClick={saveAllocationDB}>CONFIRM PLAN</button>
                     </div>

                     <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', marginBottom:'20px'}}>
                        <div style={{...styles.card, background:'#e8f0ff', borderLeft:'4px solid #0073ea'}}>
                           <h5 style={{margin:'0 0 10px 0'}}>STYLE BALANCE</h5>
                           <div style={{display:'flex', justifyContent:'space-between', textAlign:'center'}}>
                              <div><div style={styles.label}>TOTAL</div><strong>{selectedOrder.totalQty}</strong></div>
                              <div><div style={styles.label}>PLANNED</div><strong style={{color:'#00a65e'}}>{analysis.totalAllocated}</strong></div>
                              <div><div style={styles.label}>BALANCE</div><strong style={{color:'#c93652'}}>{analysis.balance}</strong></div>
                           </div>
                        </div>
                        <div style={styles.card}>
                           <h5 style={{margin:'0 0 10px 0'}}>PO & SUPPLIER BREAKDOWN</h5>
                           <div style={{maxHeight:'100px', overflowY:'auto'}}>
                              <table style={styles.table}>
                                 <thead><tr><th>PO</th><th>Qty</th><th>Supplier</th><th>Bal</th></tr></thead>
                                 <tbody>
                                    {analysis.poBreakdown?.map((po, i) => (
                                       <tr key={i}>
                                           <td style={styles.td}>{po.poNum}</td>
                                           <td style={styles.td}>{po.qty}</td>
                                           <td style={styles.td}>
                                               {po.supplier ? (
                                                   <span style={{color:'#166534', fontWeight:'bold'}}>{po.supplier}</span>
                                               ) : (
                                                   <span style={{color:'#c93652', fontSize:'0.8rem'}}>Pending Allocation</span>
                                               )}
                                           </td>
                                           <td style={{...styles.td, color: po.balance>0?'red':'green', fontWeight:'bold'}}>{po.balance}</td>
                                       </tr>
                                    ))}
                                    {(!analysis.poBreakdown || analysis.poBreakdown.length === 0) && (() => {
                                       const dbPOs = [...new Set((db.poSizeTable||[]).filter(r=>r.style_ref===selectedOrder?.styleRef).map(r=>r.po_no))];
                                       if (dbPOs.length > 0) return dbPOs.map(pn => {
                                           const poRows = (db.poSizeTable||[]).filter(r=>r.style_ref===selectedOrder.styleRef && r.po_no===pn);
                                           const poQty = poRows.reduce((a,r)=>a+(parseInt(r.qty)||0),0);
                                           const led = getPOLedger(selectedOrder.styleRef, pn, null, null, db);
                                           return (
                                             <tr key={pn}>
                                               <td style={styles.td}>{pn}</td>
                                               <td style={styles.td}>{poQty.toLocaleString()}</td>
                                               <td style={styles.td}><span style={{color:'#0073ea', fontSize:'0.8rem'}}>{led.cut||0} cut / {led.sew||0} sew</span></td>
                                               <td style={{...styles.td, color: poQty-led.cut<0?'#e2445c':'#00a65e', fontWeight:'bold'}}>{(poQty-led.cut).toLocaleString()}</td>
                                             </tr>
                                           );
                                       });
                                       return <tr><td colSpan="4" style={{textAlign:'center', padding:'10px', color:'#9699a6'}}>No POs found. Add POs in Style Registry.</td></tr>;
                                    })()}
                                 </tbody>
                              </table>
                           </div>
                        </div>
                     </div>

                     {/* PO DETAILS — size/color breakdown with live progress */}
                     {(selectedOrder?.pos || []).length > 0 && (
                       <div style={{...styles.card, background:'#f0f9ff', borderLeft:'4px solid #0891b2', padding:'14px', marginBottom:'0'}}>
                         <div style={{fontWeight:'bold', fontSize:'0.8rem', color:'#0891b2', marginBottom:'8px'}}>
                           📋 PO BREAKDOWN — {selectedOrder.pos.length} PO(s) · Total: {selectedOrder.pos.reduce((a,p)=>a+(parseFloat(p.qty)||0),0).toLocaleString()} pcs
                         </div>
                         {/* Per-PO size×color table from poSizeTable */}
                         {selectedOrder.pos.map(po => {
                           const sizeRows = (db.poSizeTable||[]).filter(r=>r.style_ref===selectedOrder.styleRef&&r.po_no===po.num);
                           const ledger = getPOLedger(selectedOrder.styleRef, po.num, null, null, db);
                           return (
                             <div key={po.num} style={{marginBottom:'10px', border:'1px solid #bae6fd', borderRadius:'6px', overflow:'hidden'}}>
                               <div style={{background:'#0891b2', color:'white', padding:'5px 10px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                 <span style={{fontWeight:'bold', fontSize:'0.78rem'}}>{po.num} {po.col ? `· ${po.col}` : ''} {po.dest ? `→ ${po.dest}` : ''}</span>
                                 <span style={{fontSize:'0.72rem'}}>{(po.qty||0).toLocaleString()} pcs · Ex-Fty: {po.date||'TBD'}</span>
                               </div>
                               {sizeRows.length > 0 ? (
                                 <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.72rem'}}>
                                   <thead><tr style={{background:'#e0f2fe'}}>
                                     <th style={{padding:'4px 8px', textAlign:'left'}}>Size</th>
                                     <th style={{padding:'4px 8px', textAlign:'left'}}>Color</th>
                                     <th style={{padding:'4px 6px', textAlign:'right'}}>PO Qty</th>
                                     <th style={{padding:'4px 6px', textAlign:'right', color:'#0073ea'}}>Cut</th>
                                     <th style={{padding:'4px 6px', textAlign:'right', color:'#00a65e'}}>Sew</th>
                                     <th style={{padding:'4px 6px', textAlign:'right', color:'#9333ea'}}>Finish</th>
                                     <th style={{padding:'4px 6px', textAlign:'right', color:'#676879'}}>Balance</th>
                                   </tr></thead>
                                   <tbody>
                                     {sizeRows.map((row,ri) => {
                                       const rowLedger = getPOLedger(selectedOrder.styleRef, po.num, row.color, row.size, db);
                                       const poQty = parseInt(row.qty)||0;
                                       const bal = poQty - rowLedger.cut;
                                       return (
                                         <tr key={ri} style={{background: ri%2===0?'white':'#f5f6f8', borderBottom:'1px solid #e0f2fe'}}>
                                           <td style={{padding:'3px 8px', fontWeight:'bold'}}>{row.size}</td>
                                           <td style={{padding:'3px 8px', color:'#676879'}}>{row.color||'—'}</td>
                                           <td style={{padding:'3px 6px', textAlign:'right', fontWeight:'bold'}}>{poQty.toLocaleString()}</td>
                                           <td style={{padding:'3px 6px', textAlign:'right', color:'#0073ea'}}>{rowLedger.cut||0}</td>
                                           <td style={{padding:'3px 6px', textAlign:'right', color:'#00a65e'}}>{rowLedger.sew||0}</td>
                                           <td style={{padding:'3px 6px', textAlign:'right', color:'#9333ea'}}>{rowLedger.finish||0}</td>
                                           <td style={{padding:'3px 6px', textAlign:'right', fontWeight:'bold', color: bal<0?'#e2445c':bal===0?'#00a65e':'#323338'}}>{bal.toLocaleString()}</td>
                                         </tr>
                                       );
                                     })}
                                   </tbody>
                                 </table>
                               ) : (
                                 <div style={{padding:'6px 10px', fontSize:'0.72rem', color:'#676879', background:'#f0f9ff'}}>
                                   No size breakdown in poSizeTable. Cut: {ledger.cut} | Sew: {ledger.sew} | Finish: {ledger.finish}
                                 </div>
                               )}
                             </div>
                           );
                         })}
                         {/* PO target dropdown */}
                         <div style={{display:'flex', gap:'8px', alignItems:'center', marginBottom:'8px'}}>
                           <label style={{fontSize:'0.75rem', color:'#676879', fontWeight:'bold'}}>Allocate for PO:</label>
                           <select style={{...styles.input, fontSize:'0.78rem', padding:'4px 8px'}}
                             value={targetPO} onChange={e=>setTargetPO(e.target.value)}>
                             <option value="">Global (All POs)</option>
                             {(selectedOrder.pos||[]).map(p=>(
                               <option key={p.num} value={p.num}>{p.num} — {p.col || 'All colors'} — {(p.qty||0).toLocaleString()} pcs</option>
                             ))}
                           </select>
                         </div>
                         <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
                           {(selectedOrder.pos||[]).map((po,pi) => {
                             // Parse size breakdown
                             const sb = po.sizeBreakdown || po.size_breakdown || '';
                             const sizes = typeof sb === 'string' && sb.trim()
                               ? sb.split(',').map(s => { const [sz,q] = s.split(':'); return {sz:(sz||'').trim(), q:(q||'').trim()}; }).filter(x=>x.sz)
                               : typeof sb === 'object' && sb ? Object.entries(sb).map(([sz,q])=>({sz,q})) : [];
                             return (
                               <div key={pi} style={{background:'white', border:'1px solid #bae6fd', borderRadius:'6px', padding:'8px 12px', minWidth:'140px'}}>
                                 <div style={{fontWeight:'bold', color:'#0891b2', fontSize:'0.8rem'}}>{po.num}</div>
                                 <div style={{color:'#323338', fontSize:'0.75rem'}}>{po.col || 'All colors'}</div>
                                 <div style={{fontWeight:'bold', color:'#1e3a8a', fontSize:'0.85rem'}}>{(po.qty||0).toLocaleString()} pcs</div>
                                 {po.date && <div style={{fontSize:'0.68rem', color:'#676879'}}>Ex-Fty: {po.date}</div>}
                                 {sizes.length > 0 && (
                                   <div style={{marginTop:'4px', display:'flex', gap:'3px', flexWrap:'wrap'}}>
                                     {sizes.map((s,si)=>(
                                       <span key={si} style={{background:'#e0f2fe', color:'#0369a1', padding:'1px 5px', borderRadius:'3px', fontSize:'0.65rem', fontWeight:'bold'}}>
                                         {s.sz}:{s.q}
                                       </span>
                                     ))}
                                   </div>
                                 )}
                               </div>
                             );
                           })}
                         </div>
                       </div>
                     )}

                     <div style={{...styles.card, background:'#f5f6f8'}}>
                        <div style={styles.grid4}>
                           <div style={styles.field}><label style={styles.label}>Plan Cut Date (PCD)</label><input type="date" style={styles.input} value={planData.pcd} onChange={e=>setPlanData({...planData, pcd:e.target.value})}/></div>
                           <div style={styles.field}><label style={{...styles.label, color:'#0073ea'}}>Sew End (Ex-Fty - 5)</label><input type="date" style={styles.input} value={planData.sewEndDate} onChange={e=>setPlanData({...planData, sewEndDate:e.target.value})}/></div>
                           <div style={styles.field}><label style={{...styles.label, color:'#059669'}}>Fin End (Ex-Fty - 3)</label><input type="date" style={styles.input} value={planData.finEndDate} onChange={e=>setPlanData({...planData, finEndDate:e.target.value})}/></div>
                           <div style={styles.field}><label style={{...styles.label, color:'#c93652'}}>Ex-Fty (Target)</label><input type="date" style={styles.input} value={planData.exFty} disabled /></div>
                        </div>
                        <div style={styles.grid4}>
                           <div style={styles.field}><label style={styles.label}>Style SMV</label><input value={selectedOrder.computedSMV} disabled style={{...styles.input, background:'#eee'}}/></div>
                           <div style={styles.field}><label style={styles.label}>Plan Eff %</label><input type="number" style={styles.input} value={planData.targetEff} onChange={e=>setPlanData({...planData, targetEff:e.target.value})}/></div>
                           <div style={styles.field}><label style={styles.label}>Work Hours</label><input type="number" style={styles.input} value={planData.workingHours} onChange={e=>setPlanData({...planData, workingHours:e.target.value})}/></div>
                        </div>
                     </div>

                     <div style={styles.card}>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'15px', borderBottom:'1px solid #e6e9ef', paddingBottom:'10px'}}>
                            <div style={{display:'flex', gap:'10px'}}>
                                {["CUTTING", "SEWING", "FINISHING"].map(t => (
                                    <button key={t} onClick={()=>setPlanTab(t)} style={{padding:'8px 16px', borderRadius:'4px', cursor:'pointer', border:'none', fontWeight:'bold', background: planTab===t?'#0073ea':'#f0f1f4', color: planTab===t?'#fff':'#676879'}}>{t} PLAN</button>
                                ))}
                            </div>
                            
                            <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                                <label style={{...styles.label, marginBottom:0}}>Planning For:</label>
                                <select style={{...styles.input, minWidth:'150px'}} value={targetPO} onChange={e=>setTargetPO(e.target.value)}>
                                    <option value="">Global / All POs</option>
                                    {(selectedOrder.pos || []).map(p => (
                                        <option key={p.num} value={p.num}>
                                            {p.num}{p.col ? ` · ${p.col}` : ''}{p.qty ? ` · ${parseFloat(p.qty).toLocaleString()} pcs` : ''}
                                        </option>
                                    ))}
                                    {/* Fallback: pull from poSizeTable if pos[] empty */}
                                    {(selectedOrder.pos || []).length === 0 && 
                                        [...new Set((db.poSizeTable || []).filter(r => r.style_ref === selectedOrder.styleRef).map(r => r.po_no))].map(pn => (
                                            <option key={pn} value={pn}>{pn}</option>
                                        ))
                                    }
                                </select>
                            </div>
                        </div>

                        {planTab === "CUTTING" && (
                           <div>
                              <div style={{background:'#e0f2fe', padding:'15px', borderRadius:'6px', marginBottom:'15px', color:'#0369a1'}}>
                                 <strong>Cutting Requirement:</strong> To feed sewing lines starting from {planData.pcd}, you need to cut approx <strong>{Math.ceil(analysis.cutDailyTarget || 0)} pcs/day</strong>.
                                 
                                 <div style={{marginTop:'15px', borderTop:'1px dashed #bae6fd', paddingTop:'10px'}}>
                                     <h5 style={{margin:'0 0 10px 0'}}>Issue Cutting Slip</h5>
                                     <div style={styles.grid4}>
                                         <div style={styles.field}><label style={styles.label}>Table</label><input style={styles.input} value={cutForm.table} onChange={e=>setCutForm({...cutForm, table:e.target.value})}/></div>
                                         <div style={styles.field}><label style={styles.label}>Layers</label><input type="number" style={styles.input} value={cutForm.layers} onChange={e=>setCutForm({...cutForm, layers:e.target.value})}/></div>
                                         <div style={styles.field}><label style={styles.label}>Ratio</label><input style={styles.input} value={cutForm.ratio} onChange={e=>setCutForm({...cutForm, ratio:e.target.value})}/></div>
                                         <button style={{...styles.btnPrimary, background:'#be123c', height:'35px', alignSelf:'end'}} onClick={()=>saveCutting()}>ISSUE CUT</button>
                                     </div>
                                 </div>
                              </div>

                              <div style={{maxHeight:'200px', overflowY:'auto', border:'1px solid #e6e9ef', borderRadius:'6px', marginBottom:'20px'}}>
                                 <table style={styles.table}>
                                    <thead style={{background:'#f5f6f8'}}><tr><th>Factory</th><th>Line</th><th>Allocate</th></tr></thead>
                                    <tbody>
                                       {getFactories(selectedOrder?.product).map(fac => getFactoryLines(fac.name).map(line => (
                                          <tr key={line.id}>
                                             <td style={styles.td}>{fac.name}</td>
                                             <td style={styles.td}>{line.category || line.name}</td>
                                             <td style={styles.td}><button style={{...styles.btnPrimary, padding:'4px 8px', fontSize:'0.75rem'}} onClick={()=>allocateResource('CUTTING', fac.id, line)}>+ Allocate</button></td>
                                          </tr>
                                       )))}
                                    </tbody>
                                 </table>
                              </div>
                              <h5 style={{fontSize:'0.9rem', color:'#676879'}}>Allocated Cutters</h5>
                              <table style={styles.table}>
                                 <thead><tr><th>Resource</th><th>Start</th><th>Remove</th></tr></thead>
                                 <tbody>
                                    {planData.allocatedCutters.map(al => (
                                       <tr key={al.id} style={{background:'#f0fdf4'}}><td style={styles.td}>{al.name}</td><td style={styles.td}>{al.startDate}</td><td style={styles.td}><button style={{color:'red', border:'none', background:'none'}} onClick={()=>removeAlloc('CUTTING', al.id)}>x</button></td></tr>
                                    ))}
                                 </tbody>
                              </table>
                           </div>
                        )}

                        {planTab === "SEWING" && (
                           <div>
                              <div style={{background:'#fff7ed', padding:'15px', borderRadius:'6px', border:'1px solid #fed7aa', marginBottom:'12px'}}>
                                 <h5 style={{margin:'0 0 10px 0', color:'#9a3412'}}>SEWING LINE ANALYSIS</h5>
                                 <div style={styles.grid4}>
                                    <div><small>Days Available</small><div style={{fontSize:'1.2rem', fontWeight:'bold'}}>{analysis.sewDays} Days</div></div>
                                    <div><small>Daily Target</small><div style={{fontSize:'1.2rem', fontWeight:'bold', color:'#0073ea'}}>{Math.ceil(analysis.dailyTarget || 0)} pcs</div><small style={{color:'#9699a6'}}>= {selectedOrder?.totalQty} ÷ {analysis.sewDays} days</small></div>
                                    <div><small>Avg Line Cap</small><div style={{fontSize:'1.2rem', fontWeight:'bold', color:'#00a65e'}}>{analysis.avgLineOutput} pcs</div><small>({analysis.avgMachinesPerLine} machines @ {planData.targetEff}%)</small></div>
                                    <div><small>Required Lines</small><div style={{fontSize:'1.5rem', fontWeight:'bold', color:'#ea580c'}}>{analysis.requiredLines}</div></div>
                                 </div>
                                 {/* PO-wise target breakdown */}
                                 {analysis.poBreakdown && analysis.poBreakdown.length > 0 && (
                                   <div style={{marginTop:'10px', borderTop:'1px solid #fde68a', paddingTop:'10px'}}>
                                     <div style={{fontSize:'0.75rem', fontWeight:'bold', color:'#9a3412', marginBottom:'6px'}}>PO-WISE DAILY TARGETS</div>
                                     <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
                                       {analysis.poBreakdown.map((po,i) => {
                                         const poDailyTarget = analysis.sewDays > 0 ? Math.ceil(po.qty / analysis.sewDays) : 0;
                                         return (
                                           <div key={i} style={{background:'white', border:'1px solid #fde68a', borderRadius:'5px', padding:'6px 10px', fontSize:'0.75rem', minWidth:'100px'}}>
                                             <div style={{fontWeight:'bold', color:'#1e3a8a'}}>{po.poNum}</div>
                                             <div style={{color:'#323338'}}>{po.qty.toLocaleString()} pcs</div>
                                             <div style={{color:'#0073ea', fontWeight:'bold'}}>{poDailyTarget} pcs/day</div>
                                             {po.balance > 0 && <div style={{color:'#c93652', fontSize:'0.68rem'}}>Bal: {po.balance}</div>}
                                           </div>
                                         );
                                       })}
                                     </div>
                                   </div>
                                 )}
                              </div>
                              {/* EFFICIENCY BUILDUP PLAN */}
                              <div style={{background:'#f0fdf4', border:'1px solid #86efac', borderRadius:'6px', padding:'12px', marginBottom:'12px'}}>
                                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer'}} onClick={()=>setShowRampUp(v=>!v)}>
                                  <span style={{fontWeight:'bold', color:'#166534', fontSize:'0.85rem'}}>📈 EFFICIENCY BUILDUP PLAN (Ramp-Up)</span>
                                  <span style={{fontSize:'0.75rem', color:'#676879'}}>{showRampUp ? '▲ Hide' : '▼ Show'}</span>
                                </div>
                                {showRampUp && (
                                  <div style={{marginTop:'10px'}}>
                                    <div style={{fontSize:'0.72rem', color:'#676879', marginBottom:'8px'}}>Set day-by-day efficiency ramp-up. Day 6+ uses Plan Eff %.</div>
                                    <div style={{display:'flex', gap:'6px', flexWrap:'wrap'}}>
                                      {rampDays.map((rd, ri) => {
                                        const lineOutput = selectedOrder?.computedSMV > 0
                                          ? Math.floor((planData.workingHours * 60 * rd.eff / 100) / selectedOrder.computedSMV * analysis.avgMachinesPerLine)
                                          : 0;
                                        return (
                                          <div key={ri} style={{background:'white', border:'1px solid #b3f5d6', borderRadius:'5px', padding:'8px', textAlign:'center', minWidth:'72px'}}>
                                            <div style={{fontSize:'0.7rem', color:'#676879', marginBottom:'3px'}}>Day {rd.day}</div>
                                            <input type="number" min={1} max={100} value={rd.eff}
                                              onChange={e => setRampDays(prev => prev.map((x,i)=>i===ri?{...x,eff:parseFloat(e.target.value)||0}:x))}
                                              style={{width:'52px', textAlign:'center', padding:'3px', border:'1px solid #86efac', borderRadius:'3px', fontSize:'0.82rem', fontWeight:'bold', color:'#166534'}}
                                            />
                                            <div style={{fontSize:'0.65rem', color:'#9699a6'}}>%</div>
                                            <div style={{fontSize:'0.68rem', color:'#0073ea', fontWeight:'bold', marginTop:'2px'}}>{lineOutput} pcs</div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                    <div style={{marginTop:'8px', fontSize:'0.72rem', color:'#676879'}}>
                                      Total ramp-up output: <strong style={{color:'#166634'}}>
                                        {rampDays.slice(0,-1).reduce((a,rd) => {
                                          const lo = selectedOrder?.computedSMV > 0
                                            ? Math.floor((planData.workingHours*60*rd.eff/100)/selectedOrder.computedSMV * analysis.avgMachinesPerLine)
                                            : 0;
                                          return a + lo;
                                        }, 0).toLocaleString()} pcs
                                      </strong> in first {rampDays.length - 1} days
                                    </div>
                                  </div>
                                )}
                              </div>

                              <h4>ALLOCATE FACTORY LINES</h4>
                              <div style={{maxHeight:'200px', overflowY:'auto', border:'1px solid #e6e9ef', borderRadius:'6px', marginBottom:'20px'}}>
                                 <table style={styles.table}>
                                    <thead style={{background:'#f5f6f8'}}><tr><th>Factory</th><th>Line</th><th>Machines</th><th>Calc Capacity</th><th>Action</th></tr></thead>
                                    <tbody>
                                       {getFactories(selectedOrder?.product).map(fac => getFactoryLines(fac.name).map(line => {
                                          const cap = Math.floor((line.machines || 20) * analysis.outputPerMachine);
                                          return (
                                             <tr key={line.id}>
                                                <td style={styles.td}>{fac.name}</td>
                                                <td style={styles.td}>{line.category || line.name}</td>
                                                <td style={styles.td}>{line.machines || 20}</td>
                                                <td style={{...styles.td, fontWeight:'bold', color:'#00a65e'}}>{cap} pcs/day</td>
                                                <td style={styles.td}><button style={{...styles.btnPrimary, padding:'4px 8px', fontSize:'0.75rem'}} onClick={()=>allocateResource('SEWING', fac.id, line)}>+ Allocate</button></td>
                                             </tr>
                                          )
                                       }))}
                                       {getFactories().length === 0 && (
                                           <tr>
                                               <td colSpan="5" style={{textAlign:'center', padding:'20px', color:'#c93652'}}>
                                                   No Garment Suppliers with lines found. <br/>
                                                   Please go to <strong>SOURCING &gt; REGISTRATION</strong> and add a Supplier with 'Garment Supplier' type and defined Sewing Lines.
                                               </td>
                                           </tr>
                                       )}
                                    </tbody>
                                 </table>
                              </div>

                              <h5 style={{margin:'0 0 10px 0'}}>Selected Lines (Reconciliation)</h5>
                              <table style={styles.table}>
                                 <thead style={{background:'#f0f1f4'}}>
                                    <tr>
                                       <th style={{padding:'8px 6px', fontSize:'0.75rem'}}>Factory</th>
                                       <th style={{padding:'8px 6px', fontSize:'0.75rem'}}>Line</th>
                                       <th style={{padding:'8px 6px', fontSize:'0.75rem'}}>PO</th>
                                       <th style={{padding:'8px 6px', fontSize:'0.75rem'}}>PO Limit</th>
                                       <th style={{padding:'8px 6px', fontSize:'0.75rem'}}>Planned Qty</th>
                                       <th style={{padding:'8px 6px', fontSize:'0.75rem'}}>Balance</th>
                                       <th style={{padding:'8px 6px', fontSize:'0.75rem'}}>Start/End</th>
                                       <th style={{padding:'8px 6px', fontSize:'0.75rem'}}>Remove</th>
                                    </tr>
                                 </thead>
                                 <tbody>
                                    {planData.allocatedLines.map(al => {
                                       // Resolve factory name live — saved records may lack factoryName
                                       const resolvedFacName = al.factoryName 
                                           || getFactories().find(f => getFactoryLines(f.name).some(l => l.id === al.resId || (l.category||l.name) === al.name))?.name
                                           || al.factoryId
                                           || '—';
                                       return (
                                       <tr key={al.id} style={{background: (() => { const _lim = getPOQtyLimit(selectedOrder?.styleRef, al.poId==='Global'?null:al.poId, null, null, db); return _lim > 0 && parseFloat(al.plannedQty) > _lim ? '#fef2f2' : '#fff'; })() }}>
                                          <td style={{...styles.td, color:'#0073ea', fontWeight:'bold', fontSize:'0.8rem'}}>{resolvedFacName}</td>
                                          <td style={{...styles.td, fontSize:'0.8rem'}}>{al.name}</td>
                                          <td style={{...styles.td, color:'#a25ddc', fontWeight:'bold', fontSize:'0.8rem'}}>{al.poId}</td>
                                          <td style={{...styles.td, fontWeight:'bold', color:'#676879', fontSize:'0.8rem'}}>
                                            {(() => { const lim = getPOQtyLimit(selectedOrder?.styleRef, al.poId==='Global'?null:al.poId, null, null, db); return lim > 0 ? lim.toLocaleString() : '—'; })()}
                                          </td>
                                          <td style={styles.td}>
                                              <input
                                                type="number" min={0}
                                                value={al.plannedQty}
                                                onChange={e => {
                                                   const raw = parseFloat(e.target.value) || 0;
                                                   const _poLim = getPOQtyLimit(selectedOrder?.styleRef, al.poId==='Global'?null:al.poId, null, null, db);
                                                   const otherAllocated = planData.allocatedLines
                                                     .filter(x => x.id !== al.id && (al.poId==='Global' || x.poId===al.poId))
                                                     .reduce((a,x) => a + (parseFloat(x.plannedQty)||0), 0);
                                                   const ceiling = _poLim > 0 ? _poLim : (selectedOrder?.totalQty || 999999);
                                                   const maxAllowed = Math.max(0, ceiling - otherAllocated);
                                                   const val = Math.max(0, Math.min(raw, maxAllowed));
                                                   setPlanData(p => ({...p, allocatedLines: p.allocatedLines.map(x => x.id===al.id ? {...x, plannedQty: val} : x)}));
                                                }}
                                                style={{width:'80px', padding:'5px', borderRadius:'4px',
                                                  border: (() => { const _lim = getPOQtyLimit(selectedOrder?.styleRef, al.poId==='Global'?null:al.poId, null, null, db); return _lim > 0 && parseFloat(al.plannedQty) > _lim ? '2px solid #e2445c' : '1px solid #c5c7d4'; })()
                                                }}
                                             />
                                          </td>
                                          <td style={{...styles.td, fontWeight:'bold', fontSize:'0.85rem', color: (() => { const _lim = getPOQtyLimit(selectedOrder?.styleRef, al.poId==='Global'?null:al.poId, null, null, db); const bal = _lim - parseFloat(al.plannedQty||0); return bal < 0 ? '#e2445c' : bal === 0 ? '#00a65e' : '#676879'; })() }}>
                                            {(() => { const _lim = getPOQtyLimit(selectedOrder?.styleRef, al.poId==='Global'?null:al.poId, null, null, db); return _lim > 0 ? (_lim - parseFloat(al.plannedQty||0)).toLocaleString() : '—'; })()}
                                          </td>
                                          <td style={{...styles.td, fontSize:'0.78rem'}}>{al.startDate} – {al.endDate}</td>
                                          <td style={styles.td}><button style={{color:'#e2445c', border:'none', background:'none', cursor:'pointer', fontWeight:'bold'}} onClick={()=>removeAlloc('SEWING', al.id)}>✕</button></td>
                                       </tr>
                                       );
                                    })}
                                    {/* Reconciliation Row */}
                                    <tr style={{background:'#f0fdf4', borderTop:'2px solid #e6e9ef'}}>
                                       <td colSpan="3" style={{...styles.td, textAlign:'right', fontSize:'0.85rem', color:'#676879'}}>
                                          TOTAL ORDER QTY: <strong>{selectedOrder.totalQty}</strong> | TOTAL PLANNED:
                                       </td>
                                       <td style={{...styles.td, color:'#00a65e', fontSize:'1.1rem', fontWeight:'bold'}}>{analysis.totalAllocated} pcs</td>
                                       <td colSpan="2" style={{...styles.td, color: analysis.balance > 0 ? '#c93652' : '#00a65e', fontWeight:'bold'}}>
                                          BALANCE: {analysis.balance} pcs
                                       </td>
                                    </tr>
                                 </tbody>
                              </table>
                           </div>
                        )}

                        {planTab === "FINISHING" && (
                           <div>
                              <div style={{background:'#f3e8ff', padding:'15px', borderRadius:'6px', marginBottom:'15px', color:'#6b21a8'}}>
                                 <strong>Finishing Schedule:</strong> From {planData.pcd} to {planData.finEndDate} ({getWorkingDays(planData.pcd, planData.finEndDate)} Days).
                                 <br/>Required Daily Output: <strong>{Math.ceil(analysis.dailyTarget || 0)} pcs/day</strong>.
                                 
                                 <div style={{marginTop:'15px', borderTop:'1px dashed #d8b4fe', paddingTop:'10px'}}>
                                     <h5 style={{margin:'0 0 10px 0'}}>Batch Planning</h5>
                                     <div style={styles.grid4}>
                                         <div style={styles.field}><label style={styles.label}>Process</label><select style={styles.input} value={finForm.type} onChange={e=>setFinForm({...finForm, type:e.target.value})}><option>Packing</option><option>Ironing</option><option>Folding</option></select></div>
                                         <div style={styles.field}><label style={styles.label}>Qty</label><input type="number" style={styles.input} value={finForm.qty} onChange={e=>setFinForm({...finForm, qty:e.target.value})} /></div>
                                         <button style={{...styles.btnPrimary, background:'#a25ddc', height:'35px', alignSelf:'end'}} onClick={()=>saveFinishing()}>ADD BATCH</button>
                                     </div>
                                 </div>
                              </div>
                              <div style={{maxHeight:'200px', overflowY:'auto', border:'1px solid #e6e9ef', borderRadius:'6px', marginBottom:'20px'}}>
                                 <table style={styles.table}>
                                    <thead style={{background:'#f5f6f8'}}><tr><th>Factory</th><th>Resource</th><th>Allocate</th></tr></thead>
                                    <tbody>
                                       {getFactories(selectedOrder?.product).map(fac => getFactoryLines(fac.name).map(line => (
                                          <tr key={line.id}>
                                             <td style={styles.td}>{fac.name}</td>
                                             <td style={styles.td}>{line.category || line.name}</td>
                                             <td style={styles.td}><button style={{...styles.btnPrimary, padding:'4px 8px', fontSize:'0.75rem'}} onClick={()=>allocateResource('FINISHING', fac.id, line)}>+ Allocate</button></td>
                                          </tr>
                                       )))}
                                    </tbody>
                                 </table>
                              </div>
                              <h5 style={{fontSize:'0.9rem', color:'#676879'}}>Allocated Finishing Resources</h5>
                              <table style={styles.table}>
                                 <thead><tr><th>Resource</th><th>End Date</th><th>Remove</th></tr></thead>
                                 <tbody>
                                    {planData.allocatedFinishers.map(al => (
                                       <tr key={al.id} style={{background:'#f0fdf4'}}><td style={styles.td}>{al.name}</td><td style={styles.td}>{al.endDate}</td><td style={styles.td}><button style={{color:'red', border:'none', background:'none'}} onClick={()=>removeAlloc('FINISHING', al.id)}>x</button></td></tr>
                                    ))}
                                 </tbody>
                              </table>
                           </div>
                        )}
                     </div>
                  </div>
               )}
            </>
         )}

         {/* --- TAB 6: PLANNING BOARD --- */}
         {sub === "BOARD" && (
            <div style={{display:'flex', flexDirection:'column', height:'100%', overflow:'hidden'}}>

               {/* BOARD CONTROLS */}
               <div style={{...styles.card, flexShrink:0, display:'flex', gap:'20px', alignItems:'center', marginBottom:'10px', flexWrap:'wrap'}}>
                  <div style={{flex:1, minWidth:'180px'}}>
                     <label style={styles.label}>Filter Factories</label>
                     <MultiSelect options={getFactories().map(f=>f.name)} selected={selectedFactories} onChange={s=>setSelectedFactories(s)} />
                  </div>
                  <div style={{width:'150px'}}><label style={styles.label}>Start Date</label><input type="date" style={styles.input} value={boardStart} onChange={e=>setBoardStart(e.target.value)} /></div>
                  <div style={{width:'120px'}}><label style={styles.label}>Range</label><select style={styles.input} value={boardDays} onChange={e=>setBoardDays(parseInt(e.target.value))}><option value="30">30 Days</option><option value="60">60 Days</option><option value="90">90 Days</option></select></div>
                  <div style={{display:'flex', flexDirection:'column', gap:'6px'}}>
                     <button onClick={getAIReallocationAdvice} disabled={aiLoading}
                        style={{...styles.btnPrimary, background:'#a25ddc', padding:'8px 14px', fontSize:'0.82rem', opacity: aiLoading ? 0.7 : 1}}>
                        {aiLoading ? '⏳ Analysing...' : '🤖 AI Advice'}
                     </button>
                     <div style={{fontSize:'0.68rem', color:'#9699a6', textAlign:'center'}}>Reallocation help</div>
                  </div>
               </div>

               {/* AI ADVICE PANEL */}
               {aiAdvice && (
                  <div style={{...styles.card, background:'#faf5ff', border:'1px solid #d8b4fe', flexShrink:0, marginBottom:'10px', padding:'15px'}}>
                     <div style={{display:'flex', justifyContent:'space-between', marginBottom:'8px'}}>
                        <strong style={{color:'#6b21a8', fontSize:'0.88rem'}}>AI Reallocation Analysis</strong>
                        <button onClick={()=>setAiAdvice(null)} style={{border:'none', background:'none', cursor:'pointer', color:'#9699a6', fontSize:'1.1rem'}}>×</button>
                     </div>
                     <pre style={{margin:0, fontSize:'0.8rem', color:'#323338', whiteSpace:'pre-wrap', fontFamily:'monospace', lineHeight:'1.5'}}>{aiAdvice}</pre>
                  </div>
               )}

               {/* DRAG-DROP MOVE MODAL */}
               {showMoveModal && (
                  <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center'}}>
                     <div style={{background:'white', borderRadius:'12px', padding:'25px', width:'420px', boxShadow:'0 10px 30px rgba(0,0,0,0.2)'}}>
                        <h4 style={{margin:'0 0 15px 0', color:'#1e3a8a'}}>Move / Split Allocation</h4>
                        <div style={{background:'#f5f6f8', padding:'12px', borderRadius:'8px', marginBottom:'15px', fontSize:'0.85rem'}}>
                           <strong>{showMoveModal.booking.styleRef}</strong> → from current line to <strong>{showMoveModal.targetLineName}</strong>
                           <div style={{color:'#676879', marginTop:'4px'}}>Total allocated: {showMoveModal.booking.qty} pcs | Balance: {showMoveModal.booking.balance} pcs</div>
                        </div>
                        <div style={styles.field}>
                           <label style={styles.label}>Quantity to Move</label>
                           <input type="number" style={{...styles.input, border:'2px solid #0073ea'}} value={moveQty}
                              onChange={e=>setMoveQty(Math.min(parseFloat(e.target.value)||0, showMoveModal.booking.qty))}
                              max={showMoveModal.booking.qty} min={1}/>
                           <div style={{display:'flex', gap:'8px', marginTop:'6px'}}>
                              <button onClick={()=>setMoveQty(showMoveModal.booking.qty)} style={{fontSize:'0.75rem', padding:'3px 8px', border:'1px solid #c5c7d4', borderRadius:'4px', cursor:'pointer', background:'white'}}>Move All</button>
                              <button onClick={()=>setMoveQty(Math.ceil(showMoveModal.booking.qty/2))} style={{fontSize:'0.75rem', padding:'3px 8px', border:'1px solid #c5c7d4', borderRadius:'4px', cursor:'pointer', background:'white'}}>Split 50/50</button>
                              <button onClick={()=>setMoveQty(Math.ceil(showMoveModal.booking.balance))} style={{fontSize:'0.75rem', padding:'3px 8px', border:'1px solid #fdab3d', borderRadius:'4px', cursor:'pointer', background:'#fffbeb'}}>Balance Only ({showMoveModal.booking.balance})</button>
                           </div>
                        </div>
                        <div style={{display:'flex', gap:'10px', justifyContent:'flex-end', marginTop:'15px'}}>
                           <button onClick={()=>{setShowMoveModal(null);setDragBooking(null);}} style={{...styles.btnPrimary, background:'#676879'}}>Cancel</button>
                           <button onClick={()=>executeMoveAllocation(showMoveModal.booking, showMoveModal.targetLineId, showMoveModal.targetFacId, showMoveModal.targetLineName, moveQty)}
                              style={{...styles.btnPrimary, background:'#0073ea'}} disabled={!moveQty}>
                              Confirm Move
                           </button>
                        </div>
                     </div>
                  </div>
               )}

               {/* LEGEND */}
               <div style={{flexShrink:0, display:'flex', gap:'16px', padding:'0 0 8px 0', flexWrap:'wrap'}}>
                  {[['#e3faf0','#00a65e','On Track'],['#fff5e6','#fdab3d','At Risk'],['#fde8eb','#c93652','Critical']].map(([bg,col,lbl])=>(
                     <div key={lbl} style={{display:'flex', alignItems:'center', gap:'5px', fontSize:'0.75rem'}}>
                        <div style={{width:'12px', height:'12px', background:bg, border:`1px solid ${col}`, borderRadius:'2px'}}></div>
                        <span style={{color:'#676879'}}>{lbl}</span>
                     </div>
                  ))}
                  <div style={{fontSize:'0.72rem', color:'#9699a6', marginLeft:'auto'}}>💡 Drag a style strip to move/split its allocation to another line</div>
               </div>
               
               {/* BOARD GRID */}
               <div style={{flex:1, background:'white', border:'1px solid #e6e9ef', borderRadius:'12px', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 4px 6px -1px rgba(0,0,0,0.1)'}}>
                  <div style={{flex:1, overflow:'auto', position:'relative'}}>
                      <div style={{display:'flex', position:'sticky', top:0, zIndex:30, width:'max-content'}}>
                         <div style={{width:'240px', minWidth:'240px', position:'sticky', left:0, zIndex:40, background:'#f5f6f8', borderRight:'1px solid #e6e9ef', borderBottom:'1px solid #c5c7d4', padding:'15px', fontWeight:'bold', color:'#676879', fontSize:'0.82rem'}}>PRODUCTION LINE</div>
                         {dates.map((d,i) => (
                             <div key={i} style={{width:'42px', minWidth:'42px', background:'#f5f6f8', borderRight:'1px solid #f0f1f4', borderBottom:'1px solid #c5c7d4', textAlign:'center', padding:'8px 0'}}>
                                <div style={{fontSize:'0.82rem', fontWeight:'700', color: d.getDay()===0?'#e2445c':'#323338'}}>{d.getDate()}</div>
                                <div style={{fontSize:'0.62rem', color:'#9699a6', textTransform:'uppercase'}}>{d.toLocaleDateString('en-US', {weekday:'short'})}</div>
                             </div>
                         ))}
                      </div>
                      <div style={{width:'max-content'}}>
                         {rows.map(fac => (
                            <React.Fragment key={fac.id}>
                               <div style={{position:'sticky', left:0, zIndex:20, padding:'8px 15px', background:'#e8f0ff', borderBottom:'1px solid #e6e9ef', color:'#1e3a8a', fontWeight:'700', fontSize:'0.82rem', letterSpacing:'0.05em', width:'100vw'}}>{fac.name}</div>
                               {(fac.lines || []).map(line => (
                                  <div key={line.id}
                                     style={{display:'flex', minHeight:'64px', borderBottom:'1px solid #f0f1f4', position:'relative', background: dragOverLine===line.id ? '#e8f0ff' : 'white', transition:'background 0.15s'}}
                                     onDragOver={e=>{e.preventDefault(); setDragOverLine(line.id);}}
                                     onDragLeave={()=>setDragOverLine(null)}
                                     onDrop={e=>{
                                        e.preventDefault(); setDragOverLine(null);
                                        if (dragBooking && dragBooking.sourceLineId !== line.id) {
                                           setMoveQty(dragBooking.booking.balance || dragBooking.booking.qty);
                                           setShowMoveModal({ booking: dragBooking.booking, targetLineId: line.id, targetFacId: fac.id, targetLineName: line.name });
                                        }
                                        setDragBooking(null);
                                     }}
                                  >
                                     {/* Line Label */}
                                     <div style={{width:'240px', minWidth:'240px', position:'sticky', left:0, zIndex:10, background:'white', borderRight:'1px solid #e6e9ef', padding:'8px 12px', display:'flex', flexDirection:'column', justifyContent:'center'}}>
                                        <div style={{fontWeight:'600', color:'#676879', fontSize:'0.82rem'}}>{line.name}</div>
                                        {line.bookings.length > 0 && (
                                           <div style={{fontSize:'0.68rem', color:'#676879', marginTop:'2px'}}>
                                              {line.bookings.map(b => {
                                                const bsd = getStyleData(b.styleRef, db);
                                                const info = [bsd?.buyer || bsd?.brand, b.poId !== 'Global' ? b.poId : null].filter(Boolean).join('/');
                                                return `${b.styleRef}${info?' ('+info+')':''}: ${b.sewActual}/${b.qty}`;
                                              }).join(' | ')}
                                           </div>
                                        )}
                                        {dragOverLine === line.id && (
                                           <div style={{fontSize:'0.68rem', color:'#0073ea', fontWeight:'bold', marginTop:'2px'}}>⬇ Drop here to move</div>
                                        )}
                                     </div>
                                     {/* Timeline strip area */}
                                     <div style={{position:'relative', width:`${dates.length * 42}px`, minWidth:`${dates.length * 42}px`}}>
                                        {/* Day grid background */}
                                        <div style={{display:'flex', height:'100%', position:'absolute', inset:0, pointerEvents:'none'}}>
                                           {dates.map((d,i)=><div key={i} style={{width:'42px', minWidth:'42px', borderRight:'1px solid #f5f6f8', background: d.getDay()===0?'rgba(239,68,68,0.04)':'transparent', height:'100%'}}></div>)}
                                        </div>
                                        {/* Bookings */}
                                        {line.bookings.map((b, bi) => {
                                           const start = new Date(b.startDate);
                                           const end = new Date(b.endDate);
                                           const vs = new Date(boardStart);
                                           if(end < vs) return null;
                                           const diff = Math.ceil((start - vs)/(1000*60*60*24));
                                           const offset = (diff < 0 ? 0 : diff) * 42;
                                           const duration = Math.max(1, Math.ceil((end - start)/(1000*60*60*24)) + 1);
                                           const width = duration * 42;
                                           const pct = b.qty > 0 ? Math.round((b.sewActual / b.qty) * 100) : 0;
                                           // Status color
                                           let statusBg = b.canFinish ? '#166534' : (b.daysLeft < 5 ? '#c93652' : '#fdab3d');
                                           let statusBorder = b.canFinish ? '#86efac' : (b.daysLeft < 5 ? '#fca5a5' : '#fde68a');
                                           return (
                                              <div key={bi}
                                                 draggable
                                                 onDragStart={e=>{
                                                    e.dataTransfer.effectAllowed='move';
                                                    setDragBooking({ booking: b, sourceLineId: line.id, sourceStyleId: b.styleId });
                                                 }}
                                                 onDragEnd={()=>{if(!showMoveModal) setDragBooking(null);}}
                                                 style={{
                                                    position:'absolute', left:`${offset}px`, width:`${width}px`,
                                                    top:'6px', height:'52px', background: b.color,
                                                    borderRadius:'6px', color:'white', overflow:'hidden',
                                                    boxShadow: dragBooking?.booking?.id===b.id ? '0 0 0 3px #0073ea' : '0 2px 4px rgba(0,0,0,0.15)',
                                                    cursor:'grab', zIndex:5, userSelect:'none',
                                                    border: `1px solid ${statusBorder}`
                                                 }}
                                                 title={`${b.styleRef} | Planned: ${b.qty} | Sewn: ${b.sewActual} | Balance: ${b.balance} | ${b.canFinish?'On Track':'AT RISK'} | Drag to move`}
                                              >
                                                 {/* Top: Style name */}
                                                 <div style={{padding:'3px 6px 1px', fontWeight:'700', fontSize:'0.75rem', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{b.styleRef}</div>
                                                 {/* Middle: Progress bar */}
                                                 <div style={{margin:'0 6px 2px', height:'4px', background:'rgba(255,255,255,0.3)', borderRadius:'2px'}}>
                                                    <div style={{width:`${pct}%`, height:'4px', background:'white', borderRadius:'2px'}}></div>
                                                 </div>
                                                 {/* Bottom: Stats */}
                                                 <div style={{padding:'0 6px', display:'flex', justifyContent:'space-between', fontSize:'0.62rem', color:'rgba(255,255,255,0.9)'}}>
                                                    <span>✂{b.cutActual}</span>
                                                    <span>🧵{b.sewActual}/{b.qty}</span>
                                                    <span style={{color: b.canFinish ? 'rgba(255,255,255,0.9)' : '#fde68a', fontWeight: b.canFinish ? 'normal' : 'bold'}}>
                                                       {b.canFinish ? `Bal:${b.balance}` : `⚠${b.balance}`}
                                                    </span>
                                                 </div>
                                              </div>
                                           );
                                        })}
                                     </div>
                                  </div>
                               ))}
                            </React.Fragment>
                         ))}
                         {rows.length === 0 && <div style={{padding:'50px', textAlign:'center', color:'#9699a6'}}>No Factory Lines Found. Add Garment Suppliers with sewing lines in Sourcing → Registration.</div>}
                      </div>
                  </div>
               </div>
            </div>
         )}


         {/* --- TAB 7: PRODUCTION FOLLOWUP --- */}
         {sub === "FOLLOWUP" && (
            <div style={{display:'flex', flexDirection:'column', gap:'20px'}}>

               {/* SHIPPING & HANDOVER PANEL */}
               <div style={{...styles.card, borderTop:'4px solid #00a65e', padding:'16px 20px'}}>
                  <div style={{marginBottom:'10px'}}>
                     <h4 style={{margin:0, color:'#14532d', fontSize:'0.92rem', letterSpacing:'0.5px'}}>🚢 SHIPPING & HANDOVER LOGS</h4>
                     <div style={{fontSize:'0.72rem', color:'#676879', marginTop:'2px'}}>Record actual shipped dates per PO, upload handover documents, log invoice & AWB numbers</div>
                  </div>
                  <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(185px, 1fr))', gap:'10px'}}>
                     {(db.mgmtStyles || []).map(s => {
                        const shipLogs = (db.shipmentLogs || []).filter(l => l.style_ref === s.styleRef);
                        const _stylePOs = extractStylePOs(s);
                        const poCount = _stylePOs.length || (db.poSizeTable||[]).filter((r,i,a)=>r.style_ref===s.styleRef&&a.findIndex(x=>x.po_no===r.po_no&&x.style_ref===r.style_ref)===i).length;
                        const shippedCount = shipLogs.length;
                        const allShipped = poCount > 0 && shippedCount >= poCount;
                        return (
                           <div key={s.id} style={{border:`1px solid ${allShipped ? '#86efac' : '#e6e9ef'}`, borderRadius:'8px', padding:'10px', background: allShipped ? '#f0fdf4' : 'white', cursor:'pointer'}}
                              onClick={() => setShippingView({ style: s.styleRef, planId: null })}>
                              <div style={{fontWeight:'bold', color:'#1e3a8a', fontSize:'0.85rem', marginBottom:'3px'}}>{s.styleRef}</div>
                              <div style={{fontSize:'0.7rem', color:'#676879', marginBottom:'6px'}}>{s.buyer} · {s.season}</div>
                              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                 <span style={{fontSize:'0.68rem', color: shippedCount > 0 ? '#00a65e' : '#9699a6'}}>
                                    {shippedCount > 0 ? `✓ ${shippedCount}/${poCount} POs logged` : `${poCount} PO${poCount !== 1 ? 's' : ''} · Not shipped`}
                                 </span>
                                 <span style={{background:'#00a65e', color:'white', padding:'2px 7px', borderRadius:'4px', fontSize:'0.68rem', fontWeight:'bold'}}>Open →</span>
                              </div>
                           </div>
                        );
                     })}
                     {(db.mgmtStyles || []).length === 0 && (
                        <div style={{padding:'20px', color:'#9699a6', fontSize:'0.82rem', gridColumn:'1/-1', textAlign:'center'}}>No styles found. Add styles in the Style Registry first.</div>
                     )}
                  </div>
               </div>

               {/* PRODUCTION SUMMARY FILTERS (Date Range + Cascade) */}
               <div style={{...styles.card, borderTop:'4px solid #0073ea', padding:'16px 20px'}}>
                  <h4 style={{margin:'0 0 12px 0', color:'#1e3a8a', fontSize:'0.88rem', textTransform:'uppercase', letterSpacing:'0.5px'}}>📊 PRODUCTION SUMMARY FILTERS</h4>
                  <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:'12px', alignItems:'end'}}>
                     <div style={styles.field}>
                        <label style={styles.label}>From Date</label>
                        <input type="date" style={styles.input} value={prodDateRange.from} onChange={e=>setProdDateRange(p=>({...p, from:e.target.value}))}/>
                     </div>
                     <div style={styles.field}>
                        <label style={styles.label}>To Date</label>
                        <input type="date" style={styles.input} value={prodDateRange.to} onChange={e=>setProdDateRange(p=>({...p, to:e.target.value}))}/>
                     </div>
                     <div style={styles.field}>
                        <label style={styles.label}>Brand</label>
                        <select style={styles.input} value={prodFilters.brand} onChange={e=>setProdFilters(p=>({...p, brand:e.target.value, season:'', style:'', po:''}))}>
                           <option value="">All Brands</option>
                           {[...new Set((db.mgmtStyles||[]).map(s=>s.brand).filter(Boolean))].map(b=><option key={b} value={b}>{b}</option>)}
                        </select>
                     </div>
                     <div style={styles.field}>
                        <label style={styles.label}>Season</label>
                        <select style={styles.input} value={prodFilters.season} onChange={e=>setProdFilters(p=>({...p, season:e.target.value, style:'', po:''}))}>
                           <option value="">All Seasons</option>
                           {[...new Set((db.mgmtStyles||[]).filter(s=>!prodFilters.brand||s.brand===prodFilters.brand).map(s=>s.season).filter(Boolean))].map(s=><option key={s} value={s}>{s}</option>)}
                        </select>
                     </div>
                     <div style={styles.field}>
                        <label style={styles.label}>Style</label>
                        <select style={styles.input} value={prodFilters.style} onChange={e=>setProdFilters(p=>({...p, style:e.target.value, po:''}))}>
                           <option value="">All Styles</option>
                           {(db.mgmtStyles||[])
                              .filter(s=>(!prodFilters.brand||s.brand===prodFilters.brand)&&(!prodFilters.season||s.season===prodFilters.season))
                              .map(s=><option key={s.id} value={s.styleRef}>{s.styleRef}</option>)}
                        </select>
                     </div>
                     <div style={styles.field}>
                        <label style={styles.label}>PO</label>
                        <select style={styles.input} value={prodFilters.po} onChange={e=>setProdFilters(p=>({...p, po:e.target.value}))}>
                           {(() => {
                             const _sd2 = getStyleData(prodFilters.style, db);
                             const _tq2 = _sd2?.totalQty || 0;
                             return <option value="">{_tq2 > 0 ? `All POs (${_tq2.toLocaleString()} total)` : 'All POs'}</option>;
                           })()}
                           {prodFilters.style && getStylePOOptions(prodFilters.style, db).map(p => (
                             <option key={p.num} value={p.num}>{p.num}{p.col ? ` · ${p.col}` : ''}{p.qty ? ` · ${parseFloat(p.qty).toLocaleString()} pcs` : ''}</option>
                           ))}
                        </select>
                     </div>
                     <div style={styles.field}>
                        <label style={styles.label}>Factory</label>
                        <select style={styles.input} value={prodFilters.factory} onChange={e=>setProdFilters(p=>({...p, factory:e.target.value, line:''}))}>
                           <option value="">All Factories</option>
                           {getFactories((db.mgmtStyles||[]).find(s=>s.styleRef===prodFilters.style)?.product).map(f=><option key={f.id} value={f.name}>{f.name}</option>)}
                        </select>
                     </div>
                     <div style={styles.field}>
                        <label style={styles.label}>Line</label>
                        <select style={styles.input} value={prodFilters.line} onChange={e=>setProdFilters(p=>({...p, line:e.target.value}))}>
                           <option value="">All Lines</option>
                           {prodFilters.factory && (getFactoryLines(prodFilters.factory)).map(l=><option key={l.id} value={l.category||l.name}>{l.category||l.name}</option>)}
                        </select>
                     </div>
                     <button onClick={()=>setProdFilters({brand:'',season:'',style:'',po:'',factory:'',line:''})} style={{...styles.btnPrimary, background:'#676879', height:'36px', alignSelf:'end'}}>Reset</button>
                  </div>
               </div>

               {/* PRODUCTION KPI CARDS */}
               <div style={{display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:'15px'}}>
                  <div style={{...styles.card, background:'#e8f0ff', borderLeft:'4px solid #0073ea', marginBottom:0}}>
                     <div style={styles.label}>CUTTING</div><div style={{fontSize:'1.8rem', fontWeight:'bold', color:'#1e3a8a'}}>{prodSum.cutTotal.toLocaleString()}</div>
                     <div style={{fontSize:'0.72rem', color:'#676879', marginTop:'2px'}}>{prodDateRange.from === prodDateRange.to ? prodDateRange.from : `${prodDateRange.from} → ${prodDateRange.to}`}</div>
                  </div>
                  <div style={{...styles.card, background:'#f0fdf4', borderLeft:'4px solid #00a65e', marginBottom:0}}>
                     <div style={styles.label}>SEWING</div><div style={{fontSize:'1.8rem', fontWeight:'bold', color:'#14532d'}}>{prodSum.sewTotal.toLocaleString()}</div>
                  </div>
                  <div style={{...styles.card, background:'#fdf4ff', borderLeft:'4px solid #c026d3', marginBottom:0}}>
                     <div style={styles.label}>FINISHING</div><div style={{fontSize:'1.8rem', fontWeight:'bold', color:'#701a75'}}>{prodSum.finTotal.toLocaleString()}</div>
                  </div>
               </div>

               {/* DAILY PRODUCTION ENTRY — CASCADING */}
               <div style={styles.card}>
                  {/* Production save notification toast */}
               {prodNotify && (
                 <div style={{
                   position: 'fixed', top: '70px', right: '20px', zIndex: 99999,
                   background: prodNotify.type === 'error' ? '#e2445c' : '#00a65e',
                   color: 'white', padding: '12px 20px', borderRadius: '8px',
                   boxShadow: '0 4px 16px rgba(0,0,0,0.25)', fontSize: '0.88rem',
                   maxWidth: '420px', fontWeight: '500',
                   animation: 'slideIn 0.3s ease'
                 }}>
                   {prodNotify.msg}
                 </div>
               )}
               <h4 style={styles.title}>DAILY PRODUCTION ENTRY</h4>
                  {/* Step 1: Brand / Season / Style / PO / Factory */}
                  <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:'12px', marginBottom:'10px', padding:'12px', background:'#f5f6f8', borderRadius:'8px', border:'1px solid #e6e9ef'}}>
                     <div>
                        <label style={styles.label}>Brand</label>
                        <select style={styles.input} value={prodFilters.brand} onChange={e=>setProdFilters(p=>({...p, brand:e.target.value, season:'', style:'', po:'', line:''}))}>
                           <option value="">Select Brand</option>
                           {[...new Set((db.mgmtStyles||[]).map(s=>s.brand).filter(Boolean))].map(b=><option key={b} value={b}>{b}</option>)}
                        </select>
                     </div>
                     <div>
                        <label style={styles.label}>Season</label>
                        <select style={styles.input} value={prodFilters.season} onChange={e=>setProdFilters(p=>({...p, season:e.target.value, style:'', po:'', line:''}))}>
                           <option value="">Select Season</option>
                           {[...new Set((db.mgmtStyles||[]).filter(s=>!prodFilters.brand||s.brand===prodFilters.brand).map(s=>s.season).filter(Boolean))].map(s=><option key={s} value={s}>{s}</option>)}
                        </select>
                     </div>
                     <div>
                        <label style={styles.label}>Style *</label>
                        <select style={{...styles.input, border: !prodFilters.style ? '2px solid #fdab3d' : '1px solid #c5c7d4'}}
                           value={prodFilters.style} onChange={e=>setProdFilters(p=>({...p, style:e.target.value, po:'', line:''}))}>
                           <option value="">Select Style *</option>
                           {(db.mgmtStyles||[]).filter(s=>(!prodFilters.brand||s.brand===prodFilters.brand)&&(!prodFilters.season||s.season===prodFilters.season)).map(s=><option key={s.id} value={s.styleRef}>{s.styleRef}</option>)}
                        </select>
                     </div>
                     <div>
                        <label style={styles.label}>PO #</label>
                        <select style={styles.input} value={prodFilters.po} onChange={e=>setProdFilters(p=>({...p, po:e.target.value}))}>
                           {(() => {
                             const _totalSd = getStyleData(prodFilters.style, db);
                             const _totalQ = _totalSd?.totalQty || 0;
                             return <option value="">{_totalQ > 0 ? `All POs (${_totalQ.toLocaleString()} total)` : 'All / General'}</option>;
                           })()}
                           {prodFilters.style && getStylePOOptions(prodFilters.style, db).map(p => (
                             <option key={p.num} value={p.num}>{p.num}{p.col ? ` · ${p.col}` : ''}{p.qty ? ` · ${parseFloat(p.qty).toLocaleString()} pcs` : ''}</option>
                           ))}
                        </select>
                     </div>
                     <div>
                        <label style={styles.label}>Factory *</label>
                        <select style={{...styles.input, border: !prodFilters.factory ? '2px solid #fdab3d' : '1px solid #c5c7d4'}}
                           value={prodFilters.factory} onChange={e=>setProdFilters(p=>({...p, factory:e.target.value, line:''}))}>
                           <option value="">Select Factory *</option>
                           {getFactories((db.mgmtStyles||[]).find(s=>s.styleRef===prodFilters.style)?.product).map(f=><option key={f.id} value={f.name}>{f.name}</option>)}
                        </select>
                     </div>
                  </div>

                  {/* PO Qty Tracker Banner */}
                  {prodFilters.style && (() => {
                    const _bannerSd = getStyleData(prodFilters.style, db);
                    if (!_bannerSd) return null;
                    // For a specific PO: use that PO's registered qty
                    // For "All POs": ALWAYS use style.totalQty as the authoritative ceiling
                    const _allPOOptions = getStylePOOptions(prodFilters.style, db);
                    const _registeredPOQty = _allPOOptions.reduce((a,p)=>a+(parseFloat(p.qty)||0),0);
                    const _styleTotalQty   = _bannerSd.totalQty || _registeredPOQty;
                    const _selectedPOQty   = (() => {
                      if (!prodFilters.po) return _styleTotalQty; // All POs = style total
                      const po = _allPOOptions.find(p=>p.num===prodFilters.po);
                      return po ? parseFloat(po.qty)||0 : _styleTotalQty;
                    })();
                    const _unregisteredQty = _styleTotalQty - _registeredPOQty;
                    const _led = getPOLedger(prodFilters.style, prodFilters.po||null, null, null, db);
                    return (
                      <div style={{background:'#1f1f1f', borderRadius:'8px', padding:'10px 14px', marginBottom:'10px', color:'white'}}>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px'}}>
                          <div style={{fontSize:'0.72rem', color:'#9699a6', fontWeight:'bold', letterSpacing:'0.05em'}}>
                            📊 QTY TRACKER — {prodFilters.style}{prodFilters.po ? ` · PO: ${prodFilters.po}` : ` · All POs`}
                          </div>
                          {!prodFilters.po && _unregisteredQty > 0 && (
                            <div style={{background:'#fff5e6', color:'#92400e', padding:'2px 8px', borderRadius:'4px', fontSize:'0.65rem', fontWeight:'bold'}}>
                              ⚠ {_unregisteredQty.toLocaleString()} pcs not linked to any PO — add POs in Style Registry
                            </div>
                          )}
                        </div>
                        <div style={{display:'flex', gap:'10px', flexWrap:'wrap'}}>
                          {[
                            { label: prodFilters.po ? 'THIS PO' : 'STYLE TOTAL', val: _selectedPOQty, color:'#e6e9ef' },
                            { label:'CUT',     val: _led.cut,     color:'#93c5fd' },
                            { label:'SEW',     val: _led.sew,     color:'#86efac' },
                            { label:'FINISH',  val: _led.finish,  color:'#d8b4fe' },
                            { label:'SHIPPED', val: _led.shipped, color:'#fde68a' },
                          ].map(({label,val,color}) => (
                            <div key={label} style={{textAlign:'center', minWidth:'70px'}}>
                              <div style={{fontSize:'0.6rem', color:'#676879', textTransform:'uppercase', letterSpacing:'0.05em'}}>{label}</div>
                              <div style={{fontSize:'1.1rem', fontWeight:'bold', color}}>{(val||0).toLocaleString()}</div>
                            </div>
                          ))}
                          {_selectedPOQty - _led.cut !== 0 && (
                            <div style={{textAlign:'center', minWidth:'70px'}}>
                              <div style={{fontSize:'0.6rem', color:'#676879', textTransform:'uppercase'}}>CUT BAL</div>
                              <div style={{fontSize:'1.1rem', fontWeight:'bold', color: (_selectedPOQty-_led.cut) < 0 ? '#eb6175' : '#4ade80'}}>
                                {(_selectedPOQty - _led.cut).toLocaleString()}
                              </div>
                            </div>
                          )}
                          {!prodFilters.po && _registeredPOQty > 0 && _registeredPOQty < _styleTotalQty && (
                            <div style={{textAlign:'center', minWidth:'80px'}}>
                              <div style={{fontSize:'0.6rem', color:'#fdab3d', textTransform:'uppercase'}}>POs LINKED</div>
                              <div style={{fontSize:'0.85rem', fontWeight:'bold', color:'#fdab3d'}}>{_registeredPOQty.toLocaleString()}</div>
                              <div style={{fontSize:'0.58rem', color:'#676879'}}>of {_styleTotalQty.toLocaleString()}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Step 2: Once Factory selected, show CUTTING / SEWING / FINISHING tabs for allocated lines */}
                  {prodFilters.factory && prodFilters.style ? (
                     <div>
                        {/* Line Tabs */}
                        <div style={{display:'flex', gap:'8px', marginBottom:'12px', borderBottom:'2px solid #e6e9ef', paddingBottom:'8px'}}>
                           {['CUTTING','SEWING','FINISHING'].map(t=>(
                              <button key={t} onClick={()=>setProdLineTab(t)}
                                 style={{padding:'8px 18px', border:'none', borderRadius:'6px 6px 0 0', cursor:'pointer', fontWeight:'bold', fontSize:'0.82rem',
                                    background: prodLineTab===t ? (t==='CUTTING'?'#0073ea':t==='SEWING'?'#00a65e':'#9333ea') : '#f0f1f4',
                                    color: prodLineTab===t ? 'white' : '#676879'}}>
                                 {t}
                              </button>
                           ))}
                        </div>
                        {/* Lines from this factory — only allocated lines for the style become active */}
                        {(() => {
                           const styleObj = (db.mgmtStyles||[]).find(s=>s.styleRef===prodFilters.style);
                           const allocLines = styleObj?.allocatedLines || styleObj?.planningData?.allocatedLines || [];
                           const factoryLines = (getFactoryLines(prodFilters.factory));
                           const allocatedInFactory = factoryLines.filter(l => allocLines.some(al=>al.resId===l.id||al.name===(l.category||l.name)));
                           const allLines = factoryLines; // show all but grey out non-allocated
                           return (
                              <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px, 1fr))', gap:'12px'}}>
                                 {allLines.map((line, lineIdx) => {
                                    const lname = line.category||line.name;
                                    // Use stable key for lineEntry state — line.id may be undefined
                                    const lineKey = line.id !== undefined && line.id !== null
                                      ? String(line.id)
                                      : (lname + '_' + lineIdx);
                                    // ── Compute poSizes/poColors HERE so logLine can access them ──────
                                    const _cardScData = getPOSizeColor(prodFilters.style, prodFilters.po || null, db);
                                    const poSizes  = _cardScData.sizes.length > 0 ? _cardScData.sizes
                                      : (() => { const sd = getStyleData(prodFilters.style, db); return sd?.sizes || []; })();
                                    const poColors = _cardScData.colors.length > 0 ? _cardScData.colors
                                      : (() => { const sd = getStyleData(prodFilters.style, db); return sd?.colors || []; })();
                                    // Build a unique display name using lineCode
                                    const _lineIdx = factoryLines.findIndex(fl=>fl.id===line.id);
                                    // lineCode: prefer stored code, else LINE + factory index + line index
                                    const _facObj = (db?.factories||db?.factoryList||[]).find(f=>f.name===prodFilters.factory)||{};
                                    const _facNum = String(_facObj.id||_facObj.factoryId||1).slice(-3).padStart(3,'0');
                                    const _lineCode = line.lineCode || line.uid ||
                                      ('LINE' + _facNum + '-' + String(lineIdx+1).padStart(2,'0'));
                                    const isAllocated = allocLines.some(al=>al.resId===line.id||al.name===lname);
                                    const todayLogs = (db.productionLogs||[]).filter(l=>
                                       l.date===new Date().toISOString().split('T')[0] &&
                                       l.style===prodFilters.style && l.factory===prodFilters.factory && l.line===lname
                                    );
                                    const todaySew = todayLogs.reduce((a,b)=>a+parseFloat(b.sewing||0),0);
                                    const todayCut = todayLogs.reduce((a,b)=>a+parseFloat(b.cutting||0),0);
                                    const todayFin = todayLogs.reduce((a,b)=>a+parseFloat(b.finishing||0),0);
                                    // Use keyed state (no hooks in map — avoids React error #310)
                                    const lineEntry = getLineEntry(lineKey);
                                    const updateLE = (val) => setLineEntry(lineKey, val);

                                    const logLine = async () => {
                                       // ── Color + Size — use same poSizes/poColors as the card dropdown ──
                                       const _effColor = lineEntry.color || (poColors.length===1 ? poColors[0] : '');
                                       const _effSize  = lineEntry.size  || (poSizes.length===1  ? poSizes[0]  : '');
                                       if (!_effColor) { prodToast('⚠ Select COLOR before logging', 'error'); return; }
                                       if (!_effSize)  {
                                         if (poSizes.length === 0) prodToast('⚠ No sizes found — enter size breakdown in Documents & BoM first', 'error');
                                         else prodToast('⚠ Select SIZE before logging — ' + poSizes.join(', ') + ' available', 'error');
                                         return;
                                       }
                                       if (!isAllocated) { prodToast(`⚠ ${lname} not allocated. Go to ALLOCATION tab first.`, 'error'); return; }
                                       const qty = parseFloat(prodLineTab==='CUTTING'?lineEntry.cutting:prodLineTab==='SEWING'?lineEntry.sewing:lineEntry.finishing) || 0;
                                       if (!qty || qty <= 0) { prodToast('⚠ Enter a qty greater than zero', 'error'); return; }
                                       // REQUIRE size and color when PO data has them
                                       // Use auto-selected value when only one option
                                       if (poSizes.length > 0  && !_effSize)  { prodToast('⚠ Select a SIZE — this PO has size-wise qty', 'error'); return; }
                                       if (poColors.length > 0 && !_effColor) { prodToast('⚠ Select a COLOR — this PO has color-wise qty', 'error'); return; }
                                       // CROSS-VALIDATE against PO qty hierarchy
                                       const _vResult = validateStageQty(
                                         prodLineTab, prodFilters.style, prodFilters.po || null,
                                         _effColor || null, _effSize || null, qty, db
                                       );
                                       if (!_vResult.ok) {
                                         if (prodLineTab === 'CUTTING') {
                                           const ok = window.confirm(`⚠️ EXCEEDS LIMIT\n\n${_vResult.reason}\n\nRequest ADMIN approval?`);
                                           if (!ok) { prodToast(`⚠ Cut qty blocked: ${_vResult.reason}`, 'error'); return; }
                                         } else {
                                           prodToast(`⚠ ${prodLineTab} blocked: ${_vResult.reason}`, 'error');
                                           return;
                                         }
                                       }
                                       // Get brand/buyer/product from style for the log record
                                       const _logSd = getStyleData(prodFilters.style, db);
                                       const newLog = {
                                          id: Date.now(), date: prodDate,
                                          factory: prodFilters.factory, style: prodFilters.style,
                                          brand: _logSd?.brand || '', buyer: _logSd?.buyer || '',
                                          season: _logSd?.season || '', product: _logSd?.product || '',
                                          po_no: (prodFilters.po||'').split(/[\s·•]/)[0].trim()||'', line: lname,
                                          line_id: line?.lineCode || line?.line_id || '',
                                          source: 'FOLLOWUP',   // tag for data separation (vs DATAPAD)
                                         hour: lineEntry.hour, size: _effSize, color: _effColor,
                                          cutting: prodLineTab==='CUTTING'?parseFloat(lineEntry.cutting)||0:0,
                                          sewing: prodLineTab==='SEWING'?parseFloat(lineEntry.sewing)||0:0,
                                          finishing: prodLineTab==='FINISHING'?parseFloat(lineEntry.finishing)||0:0,
                                       };
                                       const _saveResult = await saveToDatabase('planning', { type:'DAILY_LOG', ...newLog, quantity: newLog.cutting || newLog.sewing || newLog.finishing, size:newLog.size, color:newLog.color, po:newLog.po_no });
                                       if (_saveResult?.id) {
                                          const _savedLog = { ...newLog, id: _saveResult.id };
                                          setDb(prev=>({...prev, productionLogs:[...(prev.productionLogs||[]), _savedLog]}));
                                          setLineEntry(line.id, { hour:'09-10', size:'', color:'', cutting:0, sewing:0, finishing:0 });
                                          prodToast(`✅ ${prodLineTab} logged: ${qty} pcs · ${lname} · Size: ${_effSize||'—'} · Color: ${_effColor||'—'}`);
                                       } else {
                                          const _errMsg = _saveResult?.error || 'Unknown error — check browser console (F12)';
                                          prodToast(`❌ Save failed: ${_errMsg}`, 'error');
                                          console.error('[logLine] save failed:', _saveResult);
                                       }
                                    };

                                    // Get PO-specific allocation for this line
                                    const allocForLine = allocLines.find(al=>al.resId===line.id||al.name===lname);
                                    // Get key dates from style data
                                    const _cardSd = getStyleData(prodFilters.style, db);
                                    const _cardExFty = _cardSd?.exFty || '';
                                    const _cardInsp  = _cardSd?.finalInspDate || '';
                                    // Running total today
                                    const runningTodaySew = (db.productionLogs||[])
                                      .filter(l=>l.date===localToday()&&l.style===prodFilters.style&&l.factory===prodFilters.factory&&l.line===lname)
                                      .reduce((a,b)=>a+(parseFloat(b.sewing)||0),0);
                                    // Daily target from allocation
                                    const linePlannedQty = parseFloat(allocForLine?.plannedQty||0);
                                    const lineSewDays = getWorkingDays(allocForLine?.startDate||planData?.pcd, allocForLine?.endDate||planData?.sewEndDate);
                                    const lineDailyTarget = lineSewDays > 0 ? Math.ceil(linePlannedQty / lineSewDays) : 0;

                                    return (
                                       <div key={line.id} style={{border:`1px solid ${isAllocated?'#86efac':'#e6e9ef'}`, borderRadius:'8px', padding:'12px', background: isAllocated?'white':'#f5f6f8', opacity: isAllocated?1:0.65}}>
                                          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px'}}>
                                             <div>
                                               <div style={{fontWeight:'bold', color: isAllocated?'#1e3a8a':'#9699a6', fontSize:'0.85rem'}}>
                                              {lname}
                                              {isAllocated && <span style={{marginLeft:'6px',fontSize:'0.62rem',
                                                background:'#e8f0ff',color:'#185FA5',padding:'1px 6px',
                                                borderRadius:'10px',fontWeight:'600',verticalAlign:'middle'}}>{_lineCode}</span>}
                                            </div>
                                               {isAllocated && lineDailyTarget > 0 && (
                                                 <div style={{fontSize:'0.68rem', color:'#0073ea'}}>
                                                   Target: <strong>{lineDailyTarget}</strong>/day
                                                   {runningTodaySew > 0 && (
                                                     <span style={{marginLeft:'6px', color: runningTodaySew >= lineDailyTarget ? '#00a65e' : '#fdab3d'}}>
                                                       Today: {runningTodaySew} ({Math.round(runningTodaySew/lineDailyTarget*100)}%)
                                                     </span>
                                                   )}
                                                 </div>
                                               )}
                                             </div>
                                             {isAllocated
                                                ? <span style={{background:'#e3faf0', color:'#166534', padding:'2px 6px', borderRadius:'4px', fontSize:'0.65rem', fontWeight:'bold'}}>✓ ALLOCATED</span>
                                                : <span style={{background:'#f0f1f4', color:'#9699a6', padding:'2px 6px', borderRadius:'4px', fontSize:'0.65rem'}}>Not Allocated</span>
                                             }
                                          </div>
                                          {/* Today's total for this line */}
                                          <div style={{display:'flex', gap:'8px', marginBottom:'8px', fontSize:'0.72rem'}}>
                                             <span style={{background:'#e8f0ff', color:'#1e3a8a', padding:'2px 6px', borderRadius:'4px'}}>✂ {todayCut}</span>
                                             <span style={{background:'#f0fdf4', color:'#166534', padding:'2px 6px', borderRadius:'4px'}}>🧵 {todaySew}</span>
                                             <span style={{background:'#fdf4ff', color:'#701a75', padding:'2px 6px', borderRadius:'4px'}}>📦 {todayFin}</span>
                                             <span style={{color:'#9699a6', fontSize:'0.65rem', alignSelf:'center'}}>Today</span>
                                          </div>
                                          {/* Entry form */}
                                          {isAllocated && (
                                             <>
                                             <div style={{display:'flex', gap:'6px', flexWrap:'wrap', alignItems:'flex-end'}}>
                                                {(() => {
                                                   // Auto-compute current hour slot
                                                   const now = new Date();
                                                   const h = now.getHours();
                                                   const slots = ["06-07","07-08","08-09","09-10","10-11","11-12","12-13","13-14","14-15","15-16","16-17","17-18","18-19","19-20","20-21"];
                                                   const currentSlot = slots.find(s => parseInt(s.split('-')[0]) === h) || slots[0];
                                                   // Auto-set if empty
                                                   if (!lineEntry.hour || lineEntry.hour === '09-10') {
                                                     setTimeout(() => updateLE({...lineEntry, hour: currentSlot}), 0);
                                                   }
                                                   return (
                                                     <div style={{flex:'0 0 80px'}}>
                                                       <div style={{fontSize:'0.65rem', color:'#676879', marginBottom:'2px'}}>Hour <span style={{color:'#00a65e', fontSize:'0.6rem'}}>auto</span></div>
                                                       <select style={{...styles.input, padding:'4px', fontSize:'0.78rem'}} value={lineEntry.hour || currentSlot} onChange={e=>{const v=e.target.value; setLineEntry(lineKey, prev=>({...(prev||getLineEntry(lineKey)), hour:v}));}}>
                                                         {slots.map(h=><option key={h} value={h}>{h}</option>)}
                                                       </select>
                                                     </div>
                                                   );
                                                })()}
                                                {(() => {
                                                   // poSizes/poColors computed at card level above — reuse here
                                                   return (<>
                                                     <div style={{flex:'0 0 70px'}}>
                                                       <div style={{fontSize:'0.65rem', color:'#676879', marginBottom:'2px'}}>Size</div>
                                                       {poSizes.length > 0 ? (
                                                         <select style={{...styles.input, padding:'4px', fontSize:'0.78rem', border: (!lineEntry.size && poSizes.length!==1) ? '2px solid #fdab3d' : '1px solid #c5c7d4'}} value={lineEntry.size || (poSizes.length===1?poSizes[0]:'')} onChange={e=>{const v=e.target.value; setLineEntry(lineKey, prev=>({...(prev||getLineEntry(lineKey)), size:v}));}}>
                                                           <option value="">Select *</option>
                                                           {poSizes.map(sz=><option key={sz} value={sz}>{sz}</option>)}
                                                         </select>
                                                       ) : (
                                                         <input style={{...styles.input, padding:'4px', fontSize:'0.78rem'}} value={lineEntry.size} onChange={e=>updateLE({...lineEntry, size:e.target.value})} placeholder="S/M/L"/>
                                                       )}
                                                     </div>
                                                     <div style={{flex:'0 0 80px'}}>
                                                       <div style={{fontSize:'0.65rem', color:'#676879', marginBottom:'2px'}}>Color</div>
                                                       {poColors.length > 0 ? (
                                                         <select style={{...styles.input, padding:'4px', fontSize:'0.78rem', border: (!lineEntry.color && poColors.length!==1) ? '2px solid #fdab3d' : '1px solid #c5c7d4'}} value={lineEntry.color || (poColors.length===1?poColors[0]:'')} onChange={e=>{const v=e.target.value; setLineEntry(lineKey, prev=>({...(prev||getLineEntry(lineKey)), color:v}));}}>
                                                           <option value="">Select *</option>
                                                           {poColors.map(co=><option key={co} value={co}>{co}</option>)}
                                                         </select>
                                                       ) : (
                                                         <input style={{...styles.input, padding:'4px', fontSize:'0.78rem'}} value={lineEntry.color} onChange={e=>updateLE({...lineEntry, color:e.target.value})} placeholder="Color"/>
                                                       )}
                                                     </div>
                                                   </>);
                                                })()}
                                                {prodLineTab==='CUTTING' && (
                                                   <div style={{flex:'0 0 65px'}}>
                                                      <div style={{fontSize:'0.65rem', color:'#1e3a8a', marginBottom:'2px', fontWeight:'bold'}}>Cut Qty</div>
                                                      <input type="number" style={{...styles.input, padding:'4px', fontSize:'0.78rem', borderColor:'#0073ea'}} value={lineEntry.cutting} onChange={e=>updateLE({...lineEntry, cutting:e.target.value})}/>
                                                   </div>
                                                )}
                                                {prodLineTab==='SEWING' && (
                                                   <div style={{flex:'0 0 65px'}}>
                                                      <div style={{fontSize:'0.65rem', color:'#00a65e', marginBottom:'2px', fontWeight:'bold'}}>Sew Qty</div>
                                                      <input type="number" style={{...styles.input, padding:'4px', fontSize:'0.78rem', borderColor:'#00a65e'}} value={lineEntry.sewing} onChange={e=>updateLE({...lineEntry, sewing:e.target.value})}/>
                                                   </div>
                                                )}
                                                {prodLineTab==='FINISHING' && (
                                                   <div style={{flex:'0 0 65px'}}>
                                                      <div style={{fontSize:'0.65rem', color:'#9333ea', marginBottom:'2px', fontWeight:'bold'}}>Fin Qty</div>
                                                      <input type="number" style={{...styles.input, padding:'4px', fontSize:'0.78rem', borderColor:'#9333ea'}} value={lineEntry.finishing} onChange={e=>updateLE({...lineEntry, finishing:e.target.value})}/>
                                                   </div>
                                                )}
                                                <button
                                                  onClick={async (e)=>{ const b=e.currentTarget; b.textContent='⟳'; b.disabled=true; b.style.opacity='0.6'; try{await logLine();}finally{b.textContent='LOG'; b.disabled=false; b.style.opacity='1';} }}
                                                  style={{...styles.btnPrimary, padding:'5px 10px', fontSize:'0.78rem', height:'32px', alignSelf:'flex-end', background: prodLineTab==='CUTTING'?'#0073ea':prodLineTab==='SEWING'?'#00a65e':'#9333ea', minWidth:'45px'}}>LOG</button>
                                             </div>
                                             {/* QtyBadge — shows used/remaining per stage */}
                                             <div style={{marginTop:'6px'}}>
                                               <QtyBadge
                                                 stage={prodLineTab}
                                                 styleRef={prodFilters.style}
                                                 poNo={prodFilters.po || null}
                                                 color={lineEntry.color || null}
                                                 size={lineEntry.size || null}
                                                 db={db}
                                               />
                                             </div>
                                             </>
                                          )}
                                       </div>
                                    );
                                 })}
                                 {allLines.length === 0 && <div style={{gridColumn:'1/-1', padding:'20px', textAlign:'center', color:'#9699a6', fontSize:'0.82rem'}}>No lines found for this factory. Add lines in Sourcing → Registration.</div>}
                              </div>
                           );
                        })()}
                     </div>
                  ) : (
                     <div style={{padding:'20px', background:'#fffbeb', borderRadius:'8px', border:'1px solid #fde68a', color:'#92400e', fontSize:'0.85rem', textAlign:'center'}}>
                        ← Select a <strong>Style</strong> and <strong>Factory</strong> above to start entering production data
                     </div>
                  )}
               </div>

               {/* PRODUCTION LOGS TABLE */}
               <div style={styles.card}>
                  <h4 style={styles.title}>PRODUCTION LOGS ({prodDateRange.from === prodDateRange.to ? prodDateRange.from : `${prodDateRange.from} → ${prodDateRange.to}`})</h4>
                  <table style={styles.table}>
                     <thead><tr>
                        <th style={styles.th}>Date</th><th style={styles.th}>Time</th><th style={styles.th}>Brand</th><th style={styles.th}>Buyer</th><th style={styles.th}>Style</th>
                        <th style={styles.th}>Line</th><th style={styles.th}>PO</th><th style={styles.th}>Size/Color</th>
                        <th style={styles.th}>Cut</th><th style={styles.th}>Sew</th><th style={styles.th}>Finish</th>
                     </tr></thead>
                     <tbody>
                        {prodSum.logs.slice().reverse().slice(0,50).map(l => {
                           const sObj = (db.mgmtStyles||[]).find(s => s.styleRef === l.style || s.name === l.style);
                           const logBrand = l.brand || sObj?.brand || sObj?.brandName || '—';
                           const logBuyer = l.buyer || sObj?.buyer || sObj?.buyerName || '—';
                           return (
                             <tr key={l.id} style={{borderBottom:'1px solid #f5f6f8'}}>
                               <td style={{...styles.td, fontSize:'0.75rem', color:'#676879', whiteSpace:'nowrap'}}>{l.date}</td>
                               <td style={{...styles.td, fontSize:'0.75rem', whiteSpace:'nowrap'}}>{l.hour}</td>
                               <td style={{...styles.td, fontSize:'0.78rem', fontWeight:'bold', color:'#1e3a8a'}}>{logBrand}</td>
                               <td style={{...styles.td, fontSize:'0.78rem', color:'#676879'}}>{logBuyer}</td>
                               <td style={{...styles.td, fontWeight:'bold', color:'#0073ea', fontSize:'0.82rem'}}>{l.style}</td>
                               <td style={{...styles.td, fontSize:'0.78rem'}}>{l.line} <span style={{color:'#9699a6', fontSize:'0.7rem'}}>({l.factory})</span></td>
                               <td style={{...styles.td, fontSize:'0.75rem', color:'#0073ea'}}>{l.po_no||l.po||'—'}</td>
                               <td style={{...styles.td, fontSize:'0.75rem'}}>{l.size||'—'} / {l.color||'—'}</td>
                               <td style={{...styles.td, color:'#1e3a8a', fontWeight: l.cutting>0?'bold':'normal'}}>{l.cutting||0}</td>
                               <td style={{...styles.td, color:'#00a65e', fontWeight: l.sewing>0?'bold':'normal'}}>{l.sewing||0}</td>
                               <td style={{...styles.td, color:'#9333ea', fontWeight: l.finishing>0?'bold':'normal'}}>{l.finishing||0}</td>
                             </tr>
                           );
                        })}
                        {prodSum.logs.length === 0 && (() => {
                          const allLogs = (db.productionLogs||[]).filter(l=>
                            (!prodFilters.style||l.style===prodFilters.style)&&
                            (!prodFilters.factory||l.factory===prodFilters.factory)
                          );
                          const hasOutsideRange = allLogs.length > 0;
                          return (
                            <tr><td colSpan="11" style={{textAlign:'center', padding:'20px'}}>
                              {hasOutsideRange ? (
                                <div>
                                  <div style={{color:'#c07a00', fontWeight:'700', marginBottom:'6px'}}>
                                    ⚠ {allLogs.length} entries exist outside this date range
                                  </div>
                                  <div style={{color:'#9699a6', fontSize:'0.82rem', marginBottom:'8px'}}>
                                    QTY TRACKER shows all-time data. Click below to see all logs.
                                  </div>
                                  <button onClick={()=>setProdDateRange({from:'2020-01-01',to:new Date().toISOString().split('T')[0]})}
                                    style={{padding:'6px 16px', background:'#1e3a8a', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'700', fontSize:'0.82rem'}}>
                                    Show All Time Logs
                                  </button>
                                </div>
                              ) : (
                                <span style={{color:'#9699a6'}}>No production logs found. Use LOG button above to record entries.</span>
                              )}
                            </td></tr>
                          );
                        })()}
                     </tbody>
                  </table>
                  {prodSum.logs.length > 50 && <div style={{textAlign:'center', padding:'8px', fontSize:'0.78rem', color:'#9699a6'}}>Showing latest 50 of {prodSum.logs.length} records</div>}
               </div>
            </div>
         )}


         {/* ====================================================================================== */}
         {/* TAB 8: CUT PLAN — FULL AUTOMATION */}
         {/* ====================================================================================== */}

        {/* ═══ CUT PLAN — see CutPlan.jsx ═══ */}
        {sub === "CUTPLAN" && (
          <CutPlan
            db={db} setDb={setDb}
            saveToDatabase={saveToDatabase}
            extractStylePOs={extractStylePOs}
            broadcastUpdate={broadcastUpdate}
          />
        )}


        {/* ================================================================================== */}
        {/* TAB 9: AUTO PLAN — Comprehensive Automated Production Planning                      */}
        {/* ================================================================================== */}
        {sub === "AUTOPLAN" && (() => {
          const today = new Date().toISOString().split('T')[0];
          const allStyles = db.mgmtStyles || [];
          const allStyleRefs = allStyles.map(s => s.styleRef);
          const confirmedPOs = apPoList.filter(p => p.confirmed);
          const planResult = apPlanResult;
          const dashPlan = planResult?.plans || {};
          const conflicts = planResult?.conflictKeys || [];
          const totalPOs = Object.keys(dashPlan).length;
          const totalStyles2 = [...new Set(Object.values(dashPlan).map(p => p.styleRef))].length;
          const totalPcs = Object.values(dashPlan).reduce((a,p) => a + p.totalQty, 0);

          const C = { bg:'#0f0c29', accent:'#a25ddc', accentLight:'#f3eafb', cut:'#fdab3d', sew:'#0073ea', fin:'#00c875', pack:'#ec4899', conflict:'#e2445c', ok:'#00c875', warn:'#fdab3d', card:'rgba(255,255,255,0.97)', border:'#e6e9ef', textPrimary:'#1f1f1f', textMuted:'#676879' };

          const chip = (label, color, bg) => (
            <span key={label} style={{ display:'inline-block', padding:'2px 10px', borderRadius:'20px', fontSize:'0.72rem', fontWeight:'bold', color, background: bg || color+'22', border:`1px solid ${color}44`, whiteSpace:'nowrap' }}>{label}</span>
          );
          const StatusDot = ({ ok, warn, label }) => (
            <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'5px', fontSize:'0.8rem' }}>
              <div style={{ width:'10px', height:'10px', borderRadius:'50%', flexShrink:0, background: ok ? C.ok : warn ? C.warn : '#c5c7d4' }}/>
              <span style={{ color: ok ? '#166534' : warn ? '#92400e' : C.textMuted }}>{label}</span>
            </div>
          );

          const selectedPOEntry = apPoList.find(p => p.key === apSelectedPO);
          const selectedPOReadiness = selectedPOEntry ? apCheckReadiness(selectedPOEntry) : null;
          const selectedPOScore = selectedPOReadiness ? apReadinessScore(selectedPOReadiness) : 0;

          const apNavItems = [
            { id:'BOARD', label:'📋 PO Board', desc:'All POs & status' },
            { id:'CONFIRM', label:'✅ Confirm PO', desc:'Import & confirm details' },
            { id:'READINESS', label:'🔍 Readiness', desc:'Check all dependencies' },
            { id:'LEARNING', label:'🧠 AI Learning', desc:'Learning curve base data' },
            { id:'GENERATE', label:'⚡ Generate Plan', desc:'Run auto-planning engine' },
            { id:'DASHBOARD', label:'📊 Master Dashboard', desc:'Live plan overview' },
          ];

          return (
            <div style={{ display:'flex', gap:0, height:'calc(100vh - 130px)', overflow:'hidden', background:'#f0f1f4', borderRadius:'12px' }}>

              {/* LEFT SIDEBAR */}
              <div style={{ width:'200px', flexShrink:0, background:C.textPrimary, display:'flex', flexDirection:'column', borderRadius:'12px 0 0 12px', overflow:'hidden' }}>
                <div style={{ padding:'20px 16px 12px', background:'linear-gradient(135deg,#4c1d95,#1e1b4b)' }}>
                  <div style={{ fontSize:'0.65rem', fontWeight:'bold', color:'#a78bfa', textTransform:'uppercase', letterSpacing:'1px' }}>Auto Plan</div>
                  <div style={{ fontSize:'1rem', fontWeight:'900', color:'white', marginTop:'2px' }}>⚡ Engine</div>
                </div>
                {apNavItems.map(n => (
                  <button key={n.id} onClick={() => setApView(n.id)} style={{ textAlign:'left', padding:'12px 16px', border:'none', borderBottom:'1px solid rgba(255,255,255,0.06)', cursor:'pointer',
                    background: apView===n.id ? 'rgba(124,58,237,0.4)' : 'transparent',
                    borderLeft: `3px solid ${apView===n.id?'#a78bfa':'transparent'}`, transition:'all 0.15s' }}>
                    <div style={{ fontSize:'0.82rem', fontWeight: apView===n.id?'bold':'normal', color: apView===n.id?'#e9d5ff':'#9699a6' }}>{n.label}</div>
                    <div style={{ fontSize:'0.68rem', color:'#676879', marginTop:'2px' }}>{n.desc}</div>
                  </button>
                ))}
                <div style={{ marginTop:'auto', padding:'14px 16px', borderTop:'1px solid rgba(255,255,255,0.08)', background:'rgba(0,0,0,0.2)' }}>
                  <button onClick={async ()=>{
                    const payload = { type:'AUTOPLAN_STATE', poList:apPoList, planResult:apPlanResult, manualEdits:apManualEdits, holidays:apHolidays, buffer:apBuffer, savedAt:new Date().toISOString() };
                    try { localStorage.setItem('nirvikalp_autoplan_state', JSON.stringify(payload)); } catch(e){}
                    const r = await saveToDatabase('planning', payload);
                    alert(r?.success ? '✅ AutoPlan state saved to server!' : '⚠ Saved to browser only. Check server connection.');
                  }} style={{ width:'100%', padding:'7px', marginBottom:'10px', background:'#a25ddc', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'bold', fontSize:'0.78rem' }}>
                    💾 Save Plan State
                  </button>
                  <div style={{ fontSize:'0.65rem', color:'#676879', textTransform:'uppercase', marginBottom:'6px' }}>Quick Stats</div>
                  {[
                    { label:'POs loaded', val: apPoList.length },
                    { label:'Confirmed', val: confirmedPOs.length, color: confirmedPOs.length>0?'#4ade80':undefined },
                    { label:'Plans done', val: totalPOs, color:'#a78bfa' },
                    { label:'Conflicts', val: conflicts.length, color: conflicts.length>0?'#eb6175':undefined },
                  ].map(s => (
                    <div key={s.label} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.72rem', marginBottom:'3px' }}>
                      <span style={{ color:'#676879' }}>{s.label}</span>
                      <span style={{ fontWeight:'bold', color: s.color || '#9699a6' }}>{s.val}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* MAIN CONTENT */}
              <div style={{ flex:1, overflowY:'auto', background:'#f5f6f8', borderRadius:'0 12px 12px 0' }}>

                {/* ── PO BOARD ── */}
                {apView === 'BOARD' && (
                  <div style={{ padding:'24px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
                      <div>
                        <h2 style={{ margin:0, fontSize:'1.3rem', color:C.textPrimary, fontWeight:'900' }}>📋 PO Planning Board</h2>
                        <p style={{ margin:'4px 0 0', color:C.textMuted, fontSize:'0.82rem' }}>Add POs, confirm details, then generate plan</p>
                      </div>
                      <div style={{ display:'flex', gap:'10px' }}>
                        <button onClick={() => {
                          const loaded = [];
                          allStyles.forEach(s => {
                            const sd = getStyleData(s.styleRef, db);
                            if (!sd) return;
                            // Use poSizeTable rows if pos[] is empty — never use dummy 'GENERAL' PO
                          const _poTableNums = sd.pos.length === 0
                            ? [...new Set((db.poSizeTable||[]).filter(r=>r.style_ref===s.styleRef).map(r=>r.po_no))]
                            : [];
                          const pos = sd.pos.length > 0 ? sd.pos
                            : _poTableNums.length > 0 ? _poTableNums.map(pn => {
                                const rows = (db.poSizeTable||[]).filter(r=>r.style_ref===s.styleRef&&r.po_no===pn);
                                return { num:pn, qty:rows.reduce((a,r)=>a+(parseInt(r.qty)||0),0), col:[...new Set(rows.map(r=>r.color).filter(Boolean))].join('/'), date:rows[0]?.ex_fty_date||'' };
                              })
                            : sd.totalQty > 0 ? [{num:'PO-001', qty:sd.totalQty, col:'', date:sd.exFty}] : [];
                            pos.forEach(po => {
                              const key = `${s.styleRef}::${po.num}::${po.col||''}`;
                              if (!apPoList.find(p => p.key === key)) {
                                const pcd = sd.planProcesses?.find(p => p.name?.toLowerCase().includes('pcd')||p.name?.toLowerCase().includes('cutting'))?.planDate || '';
                                const exFty = po.date || sd.exFty || '';
                                // Resolve sizes with qty: poSizeTable rows → sizeBreakdown string → size names (qty 0)
                                let sizes = [];
                                const _sizeRows = (db.poSizeTable||[]).filter(r=>r.style_ref===s.styleRef&&r.po_no===po.num);
                                if (_sizeRows.length > 0) {
                                  sizes = _sizeRows.map(r=>({size:r.size, qty:parseInt(r.qty)||0})).filter(r=>r.size);
                                } else {
                                  const _sb = po.sizeBreakdown || '';
                                  if (typeof _sb === 'string' && _sb.trim()) {
                                    sizes = _sb.split(',').map(p=>{const[sz,q]=p.split(':');return{size:sz?.trim(),qty:parseInt(q)||0};}).filter(s=>s.size&&s.qty>0);
                                  } else if (_sb && typeof _sb === 'object') {
                                    sizes = Object.entries(_sb).map(([k,v])=>({size:k,qty:parseInt(v)||0})).filter(s=>s.qty>0);
                                  }
                                  if (sizes.length === 0) sizes = sd.sizes.map(sz=>({size:sz,qty:0}));
                                }
                                loaded.push({
                                  key, styleRef:s.styleRef, poNo:po.num,
                                  color: po.col || '', sizes,
                                  totalQty: po.qty || sd.totalQty,
                                  pcd, exFty,
                                  fiDate: sd.finalInspDate || (exFty ? addDays(exFty,-2) : ''),
                                  confirmed:false, postponed:false
                                });
                              }
                            });
                          });
                          if(loaded.length) { setApPoList(prev => [...prev, ...loaded]); alert(`Loaded ${loaded.length} PO(s) from Registry`); }
                          else alert('All style POs already loaded, or no styles with PO data found.');
                        }} style={{ ...styles.btnPrimary, background:'#676879', fontSize:'0.82rem' }}>Auto-Load from Registry</button>
                        <button onClick={() => setApView('CONFIRM')} style={{ background:'linear-gradient(135deg,#a25ddc,#5b21b6)', color:'white', border:'none', padding:'10px 22px', borderRadius:'8px', fontWeight:'bold', cursor:'pointer', fontSize:'0.88rem' }}>+ Add PO</button>
                      </div>
                    </div>

                    {apPoList.length === 0 ? (
                      <div style={{ textAlign:'center', padding:'60px 20px', background:C.card, borderRadius:'12px', border:`2px dashed ${C.border}` }}>
                        <div style={{ fontSize:'3rem', marginBottom:'12px' }}>📦</div>
                        <div style={{ fontSize:'1.1rem', color:C.textPrimary, fontWeight:'bold', marginBottom:'6px' }}>No POs added yet</div>
                        <div style={{ color:C.textMuted, fontSize:'0.88rem' }}>Click "Auto-Load from Registry" or "Add PO" above</div>
                      </div>
                    ) : (
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(330px,1fr))', gap:'16px' }}>
                        {apPoList.map(po => {
                          const rd = apCheckReadiness(po);
                          const score = apReadinessScore(rd);
                          const isConflict = conflicts.includes(po.key);
                          return (
                            <div key={po.key} onClick={() => { setApSelectedPO(po.key); setApView('READINESS'); }}
                              style={{ background:C.card, borderRadius:'12px', border:`2px solid ${isConflict?C.conflict:po.confirmed?C.ok+'55':C.border}`,
                                cursor:'pointer', transition:'all 0.2s', overflow:'hidden', boxShadow: isConflict?`0 0 0 3px ${C.conflict}22`:'0 2px 8px rgba(0,0,0,0.05)' }}>
                              <div style={{ padding:'12px 16px', background: isConflict?'#fef2f2': po.confirmed?'#f0fdf4':'#f5f6f8', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                                <div>
                                  <div style={{ fontWeight:'900', color:C.textPrimary, fontSize:'0.95rem' }}>{po.poNo}</div>
                                  <div style={{ fontSize:'0.75rem', color:C.textMuted }}>{po.styleRef} {po.color && `· ${po.color}`}</div>
                                </div>
                                <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'4px' }}>
                                  {po.confirmed ? chip('✓ Confirmed','#166534','#e3faf0') : po.postponed ? chip('⏸ Postponed','#92400e','#fff5e6') : chip('Pending','#676879','#f0f1f4')}
                                  {isConflict && chip('⚠ CONFLICT',C.conflict,'#fef2f2')}
                                </div>
                              </div>
                              <div style={{ padding:'14px 16px' }}>
                                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px', marginBottom:'10px' }}>
                                  <div><div style={{ color:C.textMuted, fontSize:'0.68rem', textTransform:'uppercase' }}>Total Qty</div><div style={{ fontWeight:'bold', fontSize:'1rem', color:C.textPrimary }}>{(po.totalQty||0).toLocaleString()}</div></div>
                                  <div><div style={{ color:C.textMuted, fontSize:'0.68rem', textTransform:'uppercase' }}>PCD</div><div style={{ fontWeight:'bold', fontSize:'0.82rem', color: po.pcd && po.pcd < today ? C.conflict : C.textPrimary }}>{po.pcd||'—'}</div></div>
                                  <div><div style={{ color:C.textMuted, fontSize:'0.68rem', textTransform:'uppercase' }}>Ex-Fty</div><div style={{ fontWeight:'bold', fontSize:'0.82rem', color:C.sew }}>{po.exFty||dashPlan[po.key]?.computedExFty||'—'}</div></div>
                                </div>
                                <div style={{ marginBottom:'10px' }}>
                                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.72rem', color:C.textMuted, marginBottom:'3px' }}>
                                    <span>Readiness</span><span style={{ fontWeight:'bold', color: score>=80?C.ok:score>=50?C.warn:C.conflict }}>{score}%</span>
                                  </div>
                                  <div style={{ height:'5px', background:'#e6e9ef', borderRadius:'3px' }}>
                                    <div style={{ height:'100%', borderRadius:'3px', width:`${score}%`, background: score>=80?C.ok:score>=50?C.warn:C.conflict, transition:'width 0.4s' }}/>
                                  </div>
                                </div>
                                <div style={{ display:'flex', gap:'6px' }}>
                                  {!po.confirmed && <button onClick={e=>{ e.stopPropagation(); setApPoList(prev=>prev.map(p=>p.key===po.key?{...p,confirmed:true,postponed:false}:p)); }} style={{ flex:1, padding:'6px', background:'#e3faf0', color:'#166534', border:'1px solid #86efac', borderRadius:'6px', cursor:'pointer', fontSize:'0.75rem', fontWeight:'bold' }}>✓ Confirm</button>}
                                  {po.confirmed && <button onClick={e=>{ e.stopPropagation(); setApPoList(prev=>prev.map(p=>p.key===po.key?{...p,confirmed:false}:p)); }} style={{ flex:1, padding:'6px', background:'#fff5e6', color:'#92400e', border:'1px solid #fcd34d', borderRadius:'6px', cursor:'pointer', fontSize:'0.75rem' }}>Unconfirm</button>}
                                  <button onClick={e=>{ e.stopPropagation(); setApPoList(prev=>prev.map(p=>p.key===po.key?{...p,postponed:!p.postponed}:p)); }} style={{ flex:1, padding:'6px', background:'#f0f1f4', color:'#676879', border:'1px solid #e6e9ef', borderRadius:'6px', cursor:'pointer', fontSize:'0.75rem' }}>⏸</button>
                                  <button onClick={e=>{ e.stopPropagation(); if(window.confirm('Remove?')) setApPoList(prev=>prev.filter(p=>p.key!==po.key)); }} style={{ padding:'6px 10px', background:'#fef2f2', color:C.conflict, border:`1px solid ${C.conflict}44`, borderRadius:'6px', cursor:'pointer', fontSize:'0.75rem' }}>✕</button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* ── CONFIRM PO ── */}
                {apView === 'CONFIRM' && (
                  <div style={{ padding:'24px' }}>
                    <h2 style={{ margin:'0 0 4px', color:C.textPrimary, fontWeight:'900' }}>✅ Add & Confirm PO Details</h2>
                    <p style={{ margin:'0 0 20px', color:C.textMuted, fontSize:'0.82rem' }}>Fill details and confirm. Plans only generate for confirmed POs.</p>
                    <div style={{ display:'grid', gridTemplateColumns:'2fr 1.4fr', gap:'24px' }}>
                      <div style={{ background:C.card, borderRadius:'12px', padding:'22px', border:`1px solid ${C.border}` }}>
                        <div style={{ fontWeight:'bold', color:C.textPrimary, marginBottom:'16px' }}>PO ENTRY FORM</div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'16px' }}>
                          {[
                            ['Style Ref','styleRef'],
                            ['PO Number','poNo'],
                            ['Color','color'],
                            ['Total Qty','totalQty'],
                            ['PCD','pcd'],
                            ['Ex-Factory Date','exFty'],
                            ['Final Inspection','fiDate'],
                          ].map(([lbl,key]) => {
                            // Get live data for dropdowns
                            const apSD = apPoForm.styleRef ? getStyleData(apPoForm.styleRef, db) : null;
                            const apPOs = apSD?.pos || [];
                            const apColors = apSD?.colors || [];
                            const isDate = key==='pcd'||key==='exFty'||key==='fiDate';
                            const isNum = key==='totalQty';
                            return (
                            <div key={key}>
                              <label style={{ fontSize:'0.72rem', fontWeight:'bold', color:C.textMuted, display:'block', marginBottom:'4px', textTransform:'uppercase' }}>{lbl}</label>
                              {key==='styleRef' ? (
                                <select style={styles.input} value={apPoForm.styleRef} onChange={e => {
                                  const sd2 = getStyleData(e.target.value, db);
                                  const pl = (db.stylePlans||[]).find(p=>p.style===e.target.value);
                                  const pcd = pl?.processes?.find(p=>p.name.toLowerCase().includes('pcd')||p.name.toLowerCase().includes('cutting'))?.planDate||'';
                                  const exFty = sd2?.exFty || pl?.processes?.find(p=>p.name.toLowerCase().includes('ex-factory'))?.planDate||'';
                                  setApPoForm(prev=>({...prev, styleRef:e.target.value, poNo:'', color:'', totalQty:'', pcd, exFty, fiDate:exFty?addDays(exFty,-2):''}));
                                }}>
                                  <option value="">-- Select Style --</option>
                                  {allStyleRefs.map(r=><option key={r}>{r}</option>)}
                                </select>
                              ) : key==='poNo' ? (
                                <select style={styles.input} value={apPoForm.poNo} onChange={e=>{
                                  const poNum = e.target.value;
                                  const po = apPOs.find(p => p.num === poNum);
                                  // Resolve sizes WITH qty — poSizeTable → sizeBreakdown string → blank size names
                                  let autoSizes = [];
                                  const sizeRows = (db.poSizeTable||[]).filter(r => r.style_ref === apPoForm.styleRef && r.po_no === poNum);
                                  if (sizeRows.length > 0) {
                                    autoSizes = sizeRows.map(r => ({ size: r.size, qty: parseInt(r.qty)||0 })).filter(s => s.size);
                                  } else {
                                    const sbStr = po?.sizeBreakdown || '';
                                    if (typeof sbStr === 'string' && sbStr.trim()) {
                                      autoSizes = sbStr.split(',').map(p => { const [sz,q]=p.split(':'); return {size:sz?.trim(), qty:parseInt(q)||0}; }).filter(s=>s.size&&s.qty>0);
                                    } else if (sbStr && typeof sbStr === 'object') {
                                      autoSizes = Object.entries(sbStr).map(([k,v])=>({size:k,qty:parseInt(v)||0})).filter(s=>s.qty>0);
                                    }
                                  }
                                  if (autoSizes.length === 0) autoSizes = (apSD?.sizes||[]).map(s=>({size:s,qty:0}));
                                  setApPoForm(prev=>({...prev, poNo: poNum,
                                    color: po?.col || prev.color,
                                    totalQty: po?.qty ? String(po.qty) : prev.totalQty,
                                    exFty: po?.date || prev.exFty,
                                    sizes: autoSizes
                                  }));
                                }}>
                                  <option value="">-- Select PO --</option>
                                  {apPOs.map(p=><option key={p.num} value={p.num}>{p.num}{p.col?` · ${p.col}`:''}{p.qty?` (${parseFloat(p.qty).toLocaleString()})`:''}</option>)}
                                  {apPOs.length===0 && <option value="" disabled>No POs — check Style Registry</option>}
                                </select>
                              ) : key==='color' ? (
                                apColors.length > 0 ? (
                                  <select style={styles.input} value={apPoForm.color} onChange={e=>setApPoForm({...apPoForm,color:e.target.value})}>
                                    <option value="">-- Select Color --</option>
                                    {apColors.map(col=><option key={col}>{col}</option>)}
                                  </select>
                                ) : <input style={styles.input} value={apPoForm.color} onChange={e=>setApPoForm({...apPoForm,color:e.target.value})} placeholder="Color"/>
                              ) : (
                                <input type={isDate?'date':isNum?'number':'text'} style={styles.input} value={apPoForm[key]} onChange={e=>setApPoForm({...apPoForm,[key]:e.target.value})}/>
                              )}
                            </div>
                            );
                          })}
                        </div>
                        {/* Size breakdown */}
                        <div style={{ background:'#f5f6f8', border:`1px solid ${C.border}`, borderRadius:'8px', padding:'14px', marginBottom:'14px' }}>
                          <div style={{ fontWeight:'bold', color:C.textPrimary, marginBottom:'10px', fontSize:'0.85rem' }}>Size-Wise Qty Breakdown</div>
                          <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', marginBottom:'8px' }}>
                            {(apPoForm.sizes||[]).map((s,i)=>(
                              <div key={i} style={{ background:'white', border:`1px solid ${C.border}`, borderRadius:'6px', padding:'5px 8px', display:'flex', alignItems:'center', gap:'5px', fontSize:'0.78rem' }}>
                                <span style={{ fontWeight:'bold', minWidth:'20px' }}>{s.size}</span>
                                <input type="number" style={{ width:'55px', padding:'2px 4px', border:`1px solid ${C.border}`, borderRadius:'4px', fontSize:'0.78rem' }}
                                  value={s.qty} onChange={e=>{ const nb=[...apPoForm.sizes]; nb[i]={...s,qty:parseFloat(e.target.value)||0}; setApPoForm({...apPoForm,sizes:nb}); }}/>
                                <button onClick={()=>setApPoForm({...apPoForm,sizes:apPoForm.sizes.filter((_,j)=>j!==i)})} style={{ background:'none',border:'none',color:C.conflict,cursor:'pointer',fontSize:'0.8rem',padding:0 }}>✕</button>
                              </div>
                            ))}
                          </div>
                          <div style={{ display:'flex', gap:'6px', alignItems:'center', flexWrap:'wrap' }}>
                            <select style={{ ...styles.input, width:'76px' }} value={apPoSizeRow.size} onChange={e=>setApPoSizeRow({...apPoSizeRow,size:e.target.value})}>
                              <option value="">Size</option>{AP_SIZES.map(s=><option key={s}>{s}</option>)}
                            </select>
                            <input type="number" placeholder="Qty" style={{ ...styles.input, width:'70px' }} value={apPoSizeRow.qty} onChange={e=>setApPoSizeRow({...apPoSizeRow,qty:e.target.value})}/>
                            <button onClick={()=>{ if(!apPoSizeRow.size||!apPoSizeRow.qty)return; setApPoForm({...apPoForm,sizes:[...(apPoForm.sizes||[]),{size:apPoSizeRow.size,qty:parseFloat(apPoSizeRow.qty)||0}]}); setApPoSizeRow({size:'',qty:''}); }} style={{ ...styles.btnPrimary, background:'#a25ddc', padding:'6px 12px', fontSize:'0.78rem' }}>+ Size</button>
                            <button onClick={()=>{ const t=parseInt(apPoForm.totalQty)||0; if(!t)return alert('Enter total qty first'); setApPoForm({...apPoForm,sizes:[{size:'S',qty:Math.round(t*.15)},{size:'M',qty:Math.round(t*.30)},{size:'L',qty:Math.round(t*.30)},{size:'XL',qty:Math.round(t*.25)}]}); }} style={{ ...styles.btnPrimary, background:'#676879', padding:'6px 12px', fontSize:'0.78rem' }}>Auto Split</button>
                          </div>
                          <div style={{ marginTop:'6px', fontSize:'0.75rem', color:C.textMuted }}>Total: <strong>{(apPoForm.sizes||[]).reduce((a,s)=>a+(s.qty||0),0).toLocaleString()}</strong> / Required: <strong>{(parseInt(apPoForm.totalQty)||0).toLocaleString()}</strong></div>
                        </div>
                        <div style={{ display:'flex', gap:'10px' }}>
                          <button onClick={apAddPO} style={{ ...styles.btnPrimary, flex:1, background:'#a25ddc' }}>＋ Add to List</button>
                          <button onClick={()=>{ if(!apPoForm.styleRef||!apPoForm.poNo)return alert('Fill Style & PO'); const key=`${apPoForm.styleRef}::${apPoForm.poNo}::${apPoForm.color}`; setApPoList(prev=>{ const ex=prev.find(p=>p.key===key); return ex?prev.map(p=>p.key===key?{...p,...apPoForm,confirmed:true}:p):[...prev,{...apPoForm,key,confirmed:true,postponed:false}]; }); setApPoForm({styleRef:'',poNo:'',color:'',sizes:[],totalQty:'',pcd:'',exFty:'',fiDate:''}); alert('PO added & confirmed!'); }} style={{ ...styles.btnPrimary, flex:1, background:'#059669' }}>✓ Add & Confirm</button>
                        </div>
                      </div>
                      <div style={{ background:C.card, borderRadius:'12px', padding:'20px', border:`1px solid ${C.border}` }}>
                        <div style={{ fontWeight:'bold', color:C.textPrimary, marginBottom:'14px' }}>CONFIRMATION CHECKLIST</div>
                        {[
                          { label:'Style in system', ok: !!apPoForm.styleRef&&allStyles.some(s=>s.styleRef===apPoForm.styleRef) },
                          { label:'PO number', ok: !!apPoForm.poNo },
                          { label:'Color specified', ok: !!apPoForm.color },
                          { label:'Qty > 0', ok: parseInt(apPoForm.totalQty)>0 },
                          { label:'Size breakdown', ok: (apPoForm.sizes||[]).some(s=>s.qty>0) },
                          { label:'PCD set', ok: !!apPoForm.pcd },
                          { label:'Ex-Factory date', ok: !!apPoForm.exFty },
                          { label:'FI date', ok: !!apPoForm.fiDate },
                        ].map(item => <StatusDot key={item.label} ok={item.ok} label={item.label}/>)}
                      </div>
                    </div>
                  </div>
                )}

                {/* ── READINESS ── */}
                {apView === 'READINESS' && (
                  <div style={{ padding:'24px' }}>
                    <h2 style={{ margin:'0 0 4px', color:C.textPrimary, fontWeight:'900' }}>🔍 Readiness Check</h2>
                    <p style={{ margin:'0 0 18px', color:C.textMuted, fontSize:'0.82rem' }}>All dependencies for accurate automated planning</p>
                    <div style={{ display:'flex', gap:'12px', alignItems:'center', marginBottom:'20px', background:C.card, borderRadius:'10px', padding:'14px', border:`1px solid ${C.border}` }}>
                      <label style={{ fontSize:'0.8rem', fontWeight:'bold', color:C.textMuted, whiteSpace:'nowrap' }}>Select PO:</label>
                      <select style={{ ...styles.input, flex:1 }} value={apSelectedPO||''} onChange={e=>setApSelectedPO(e.target.value)}>
                        <option value="">-- Select PO --</option>
                        {apPoList.map(p=><option key={p.key} value={p.key}>{p.poNo} | {p.styleRef} | {p.color||'—'}</option>)}
                      </select>
                      {selectedPOEntry && <div style={{ fontSize:'1.4rem', fontWeight:'900', color:selectedPOScore>=80?C.ok:selectedPOScore>=50?C.warn:C.conflict }}>{selectedPOScore}%</div>}
                    </div>

                    {selectedPOEntry && selectedPOReadiness ? (
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                        {[
                          { key:'hasSizeBreakdown', label:'Size-Wise Qty Breakdown', desc:'PO must have color-size qty details', action:'Confirm PO', act:()=>setApView('CONFIRM'), dept:'Cutting' },
                          { key:'hasMarkerPlan', label:'Marker Plan', desc:'Marker no, width, length, panels defined', action:'Cut Plan', act:()=>setSub('CUTPLAN'), dept:'Cutting' },
                          { key:'hasSpecSheet', label:'Spec Sheet / Tech Pack', desc:'Style specification document attached', action:'Style Passport', act:()=>{ const s=allStyles.find(x=>x.styleRef===selectedPOEntry.styleRef); if(s&&openStylePassport)openStylePassport(s); }, dept:'Cutting' },
                          { key:'hasNoOfPanels', label:'No. of Panels', desc:'Number of cut panels per garment', action:'Style Passport', act:()=>{ const s=allStyles.find(x=>x.styleRef===selectedPOEntry.styleRef); if(s&&openStylePassport)openStylePassport(s); }, dept:'Cutting' },
                          { key:'hasPCD', label:'PCD (Cut Date)', desc:'Deadline for cutting to start', action:'Edit PO', act:()=>setApView('CONFIRM'), dept:'Timeline' },
                          { key:'hasExFty', label:'Ex-Factory Date', desc:'Committed shipment date', action:'Edit PO', act:()=>setApView('CONFIRM'), dept:'Timeline' },
                          { key:'hasFIDate', label:'Final Inspection Date', desc:'Quality inspection deadline', action:'Edit PO', act:()=>setApView('CONFIRM'), dept:'Timeline' },
                          { key:'hasFabric', label:'Fabric in Inventory', desc:'Matching rolls with positive balance', action:'Material Mgmt', act:()=>{}, dept:'Fabric' },
                          { key:'hasSMV', label:'SMV Defined', desc:'Standard Minute Value for line capacity calc', action:'Style Passport', act:()=>{ const s=allStyles.find(x=>x.styleRef===selectedPOEntry.styleRef); if(s&&openStylePassport)openStylePassport(s); }, dept:'Sewing' },
                        ].map(item => {
                          const passed = selectedPOReadiness[item.key];
                          return (
                            <div key={item.key} style={{ background:C.card, borderRadius:'10px', padding:'14px 16px', border:`1px solid ${passed?C.ok+'44':'#fca5a5'}`, borderLeft:`4px solid ${passed?C.ok:C.conflict}`, display:'flex', justifyContent:'space-between', alignItems:'center', gap:'10px' }}>
                              <div>
                                <div style={{ fontSize:'0.65rem', color:C.textMuted, textTransform:'uppercase', marginBottom:'2px' }}>{item.dept}</div>
                                <div style={{ fontWeight:'bold', color:C.textPrimary, marginBottom:'3px', display:'flex', alignItems:'center', gap:'6px' }}>
                                  <span>{passed?'✅':'❌'}</span>{item.label}
                                </div>
                                <div style={{ fontSize:'0.72rem', color:C.textMuted }}>{item.desc}</div>
                              </div>
                              {!passed && <button onClick={item.act} style={{ flexShrink:0, padding:'5px 10px', background:C.accentLight, color:C.accent, border:`1px solid ${C.accent}44`, borderRadius:'6px', cursor:'pointer', fontSize:'0.72rem', fontWeight:'bold', whiteSpace:'nowrap' }}>{item.action} →</button>}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ textAlign:'center', padding:'40px', color:C.textMuted, background:C.card, borderRadius:'12px', border:`1px solid ${C.border}` }}>
                        <div style={{ fontSize:'2.5rem', marginBottom:'10px' }}>🔍</div>Select a PO above to run readiness check
                      </div>
                    )}

                    {apPoList.length > 0 && (
                      <div style={{ background:C.card, borderRadius:'10px', padding:'16px', border:`1px solid ${C.border}`, marginTop:'20px', overflowX:'auto' }}>
                        <div style={{ fontWeight:'bold', color:C.textPrimary, marginBottom:'10px', fontSize:'0.9rem' }}>ALL POs READINESS SUMMARY</div>
                        <table style={{ ...styles.table, fontSize:'0.75rem' }}>
                          <thead><tr>{['PO / Style','Qty','PCD','Ex-Fty','Sizes','Marker','SMV','Fabric','Score','Status'].map(h=><th key={h} style={{ ...styles.th, fontSize:'0.68rem', background:'#f5f6f8' }}>{h}</th>)}</tr></thead>
                          <tbody>
                            {apPoList.map(po => {
                              const rd = apCheckReadiness(po); const sc = apReadinessScore(rd);
                              return (
                                <tr key={po.key} onClick={()=>setApSelectedPO(po.key)} style={{ cursor:'pointer', background:po.key===apSelectedPO?'#f3eafb':'white', borderBottom:`1px solid ${C.border}` }}>
                                  <td style={{ ...styles.td, fontWeight:'bold', color:C.accent }}>{po.poNo}<br/><span style={{ fontWeight:'normal', color:C.textMuted, fontSize:'0.7rem' }}>{po.styleRef}</span></td>
                                  <td style={styles.td}>{(po.totalQty||0).toLocaleString()}</td>
                                  <td style={{ ...styles.td, color:po.pcd&&po.pcd<today?C.conflict:undefined, fontWeight:'bold' }}>{po.pcd||'—'}</td>
                                  <td style={{ ...styles.td, color:C.sew }}>{po.exFty||'—'}</td>
                                  {[rd.hasSizeBreakdown,rd.hasMarkerPlan,rd.hasSMV,rd.hasFabric].map((ok,i)=><td key={i} style={{ ...styles.td, textAlign:'center' }}>{ok?'✅':'❌'}</td>)}
                                  <td style={styles.td}><div style={{ display:'flex', alignItems:'center', gap:'6px' }}><div style={{ flex:1, height:'6px', background:'#e6e9ef', borderRadius:'3px' }}><div style={{ height:'100%', borderRadius:'3px', width:`${sc}%`, background:sc>=80?C.ok:sc>=50?C.warn:C.conflict }}/></div><span style={{ fontWeight:'bold', color:sc>=80?C.ok:sc>=50?C.warn:C.conflict }}>{sc}%</span></div></td>
                                  <td style={styles.td}>{po.confirmed?chip('✓','#166534','#e3faf0'):po.postponed?chip('⏸','#92400e','#fff5e6'):chip('Pending','#676879','#f0f1f4')}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* ── AI LEARNING CURVES ── */}
                {apView === 'LEARNING' && (
                  <div style={{ padding:'24px' }}>
                    <h2 style={{ margin:'0 0 4px', color:C.textPrimary, fontWeight:'900' }}>🧠 AI Learning Curve Base Data</h2>
                    <p style={{ margin:'0 0 18px', color:C.textMuted, fontSize:'0.82rem' }}>Add base productivity figures per dept. Auto-planner matches closest parameters to estimate output.</p>
                    <div style={{ display:'flex', gap:'8px', marginBottom:'18px', background:C.card, padding:'8px', borderRadius:'10px', border:`1px solid ${C.border}` }}>
                      {[['cutting','✂ Cutting',C.cut],['sewing','🧵 Sewing',C.sew],['finishing','✨ Finishing',C.fin],['packing','📦 Packing',C.pack]].map(([d,lbl,col])=>(
                        <button key={d} onClick={()=>{ setApLcDept(d); setApLcEntry(p=>({...p,dept:d})); }} style={{ flex:1, padding:'10px', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:apLcDept===d?'bold':'normal', background:apLcDept===d?col:'transparent', color:apLcDept===d?'white':C.textMuted }}>{lbl}</button>
                      ))}
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1.6fr 1fr', gap:'20px' }}>
                      <div style={{ background:C.card, borderRadius:'12px', padding:'22px', border:`1px solid ${C.border}` }}>
                        <div style={{ fontWeight:'bold', marginBottom:'14px', color:C.textPrimary }}>ADD {apLcDept.toUpperCase()} BASE ENTRY</div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                          {apLcDept==='cutting' && [
                            ['Plies per Lay','plies','number'],['Lay Length (m)','layLen','number'],['Width (cm)','width','number'],['No. of Parts','parts','number'],
                            ['Fabric Type','fabricType','text'],['Content','content','text'],['GSM','weight','number'],['Difficulty Index','diffIndex','number'],
                            ['Output (pcs/day) *','output','number'],
                          ].map(([lbl,key,type])=>(
                            <div key={key}><label style={{ fontSize:'0.72rem',fontWeight:'bold',color:key==='output'?C.cut:C.textMuted,display:'block',marginBottom:'3px',textTransform:'uppercase' }}>{lbl}</label><input type={type} style={{ ...styles.input, borderColor:key==='output'?C.cut:undefined }} value={apLcEntry[key]||''} onChange={e=>setApLcEntry(p=>({...p,[key]:e.target.value}))}/></div>
                          ))}
                          {apLcDept==='sewing' && [
                            ['SMV Range (e.g. 15-25)','smv','text'],['Qty Range','qty','text'],['Country','country','text'],['Zone / Region','zone','text'],
                            ['Fabric Type','fabricType','text'],['Content','content','text'],['Target Efficiency % *','efficiency','number'],['Output/Line/Day *','output','number'],
                          ].map(([lbl,key,type])=>(
                            <div key={key}><label style={{ fontSize:'0.72rem',fontWeight:'bold',color:key==='output'||key==='efficiency'?C.sew:C.textMuted,display:'block',marginBottom:'3px',textTransform:'uppercase' }}>{lbl}</label><input type={type} style={{ ...styles.input, borderColor:key==='output'||key==='efficiency'?C.sew:undefined }} value={apLcEntry[key]||''} onChange={e=>setApLcEntry(p=>({...p,[key]:e.target.value}))}/></div>
                          ))}
                          {['finishing','packing'].includes(apLcDept) && [
                            ['Fabric Type','fabricType','text'],['No. Operations','parts','number'],['Output (pcs/person/day) *','output','number'],['Qty Range','qty','text'],
                          ].map(([lbl,key,type])=>(
                            <div key={key}><label style={{ fontSize:'0.72rem',fontWeight:'bold',color:key==='output'?C.fin:C.textMuted,display:'block',marginBottom:'3px',textTransform:'uppercase' }}>{lbl}</label><input type={type} style={{ ...styles.input, borderColor:key==='output'?C.fin:undefined }} value={apLcEntry[key]||''} onChange={e=>setApLcEntry(p=>({...p,[key]:e.target.value}))}/></div>
                          ))}
                        </div>
                        <button onClick={apSaveLcEntry} style={{ ...styles.btnPrimary, width:'100%', marginTop:'16px', background:apLcDept==='cutting'?C.cut:apLcDept==='sewing'?C.sew:apLcDept==='finishing'?C.fin:C.pack }}>＋ Save Learning Entry</button>
                      </div>
                      <div>
                        <div style={{ background:C.card, borderRadius:'12px', padding:'18px', border:`1px solid ${C.border}`, marginBottom:'14px', maxHeight:'380px', overflowY:'auto' }}>
                          <div style={{ fontWeight:'bold', marginBottom:'12px', fontSize:'0.9rem', color:C.textPrimary }}>SAVED ({apLearning.filter(r=>r.dept===apLcDept).length})</div>
                          {apLearning.filter(r=>r.dept===apLcDept).map((r,i)=>(
                            <div key={i} style={{ background:'#f5f6f8', border:`1px solid ${C.border}`, borderRadius:'8px', padding:'10px', marginBottom:'6px', fontSize:'0.78rem' }}>
                              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'4px' }}>
                                <span style={{ fontWeight:'bold', color:C.textPrimary }}>{r.fabricType||'—'} {r.content&&`· ${r.content}`}</span>
                                <span style={{ fontWeight:'900', color:apLcDept==='cutting'?C.cut:apLcDept==='sewing'?C.sew:C.fin }}>{r.output}{r.efficiency&&!r.output?`${r.efficiency}%`:''}</span>
                              </div>
                              <div style={{ display:'flex', flexWrap:'wrap', gap:'3px' }}>
                                {r.plies&&<span style={{ background:'white', border:`1px solid ${C.border}`, padding:'1px 5px', borderRadius:'6px' }}>Ply:{r.plies}</span>}
                                {r.parts&&<span style={{ background:'white', border:`1px solid ${C.border}`, padding:'1px 5px', borderRadius:'6px' }}>Pts:{r.parts}</span>}
                                {r.zone&&<span style={{ background:'white', border:`1px solid ${C.border}`, padding:'1px 5px', borderRadius:'6px' }}>Zone:{r.zone}</span>}
                                {r.diffIndex&&<span style={{ background:'white', border:`1px solid ${C.border}`, padding:'1px 5px', borderRadius:'6px' }}>Diff:{r.diffIndex}</span>}
                              </div>
                              <button onClick={()=>setApLearning(p=>p.filter(x=>x!==r))} style={{ background:'none', border:'none', color:C.conflict, cursor:'pointer', fontSize:'0.7rem', marginTop:'4px' }}>✕ Remove</button>
                            </div>
                          ))}
                          {apLearning.filter(r=>r.dept===apLcDept).length===0 && <div style={{ textAlign:'center', padding:'20px', color:C.textMuted, fontSize:'0.82rem' }}>No entries yet</div>}
                        </div>
                        {/* Buffer & Holidays */}
                        <div style={{ background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:'10px', padding:'14px' }}>
                          <div style={{ fontWeight:'bold', color:'#92400e', marginBottom:'10px', fontSize:'0.85rem' }}>⚙ Planning Settings</div>
                          <label style={{ fontSize:'0.72rem', fontWeight:'bold', color:'#92400e', display:'block', marginBottom:'4px', textTransform:'uppercase' }}>Daily Buffer %</label>
                          <input type="number" min={0} max={20} style={{ ...styles.input, width:'80px', marginBottom:'12px' }} value={apBuffer} onChange={e=>setApBuffer(parseFloat(e.target.value)||5)}/>
                          <label style={{ fontSize:'0.72rem', fontWeight:'bold', color:'#92400e', display:'block', marginBottom:'4px', textTransform:'uppercase' }}>Block Holidays</label>
                          <div style={{ display:'flex', gap:'6px', marginBottom:'6px' }}>
                            <input type="date" style={{ ...styles.input, flex:1 }} value={apNewHoliday} onChange={e=>setApNewHoliday(e.target.value)}/>
                            <button onClick={()=>{ if(apNewHoliday){ setApHolidays(p=>[...p,apNewHoliday]); setApNewHoliday(''); }}} style={{ ...styles.btnPrimary, background:'#92400e', padding:'6px 10px', fontSize:'0.78rem' }}>Add</button>
                          </div>
                          <div style={{ display:'flex', flexWrap:'wrap', gap:'4px' }}>
                            {apHolidays.map((h,i)=>(
                              <span key={i} style={{ background:'white', border:'1px solid #fed7aa', borderRadius:'5px', padding:'2px 7px', fontSize:'0.7rem', display:'flex', gap:'4px', alignItems:'center' }}>
                                {h}<button onClick={()=>setApHolidays(p=>p.filter((_,j)=>j!==i))} style={{ background:'none', border:'none', cursor:'pointer', color:C.conflict, lineHeight:1, padding:0 }}>×</button>
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── GENERATE ── */}
                {apView === 'GENERATE' && (
                  <div style={{ padding:'24px' }}>
                    <h2 style={{ margin:'0 0 4px', color:C.textPrimary, fontWeight:'900' }}>⚡ Generate Automated Plan</h2>
                    <p style={{ margin:'0 0 18px', color:C.textMuted, fontSize:'0.82rem' }}>Plans generated for all confirmed POs. Proceeds with warnings if readiness is incomplete.</p>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'18px' }}>
                      {[
                        { label:'Confirmed POs', val: confirmedPOs.length, ok: confirmedPOs.length>0, col:C.ok },
                        { label:'Learning Entries', val: apLearning.length, ok: apLearning.length>0, col: apLearning.length>0?C.sew:C.warn },
                        { label:'Planning Buffer', val: apBuffer+'%', ok: true, col:C.accent },
                        { label:'Holidays Blocked', val: apHolidays.length+'d', ok: true, col:'#9699a6' },
                      ].map(k=>(
                        <div key={k.label} style={{ background:C.card, borderRadius:'12px', padding:'18px', border:`1px solid ${C.border}`, borderTop:`4px solid ${k.col}`, textAlign:'center' }}>
                          <div style={{ fontSize:'0.68rem', color:C.textMuted, textTransform:'uppercase', marginBottom:'6px' }}>{k.label}</div>
                          <div style={{ fontSize:'2rem', fontWeight:'900', color:k.col }}>{k.val}</div>
                        </div>
                      ))}
                    </div>
                    {confirmedPOs.length===0 && <div style={{ background:'#fef2f2', border:`1px solid ${C.conflict}44`, borderRadius:'8px', padding:'12px', color:'#991b1b', marginBottom:'16px', fontSize:'0.85rem' }}>⚠ No confirmed POs. Go to PO Board and confirm at least one.</div>}
                    {apLearning.length===0 && <div style={{ background:'#fff5e6', border:'1px solid #fcd34d', borderRadius:'8px', padding:'12px', color:'#92400e', marginBottom:'16px', fontSize:'0.85rem' }}>⚠ No learning data — using defaults (500 pcs/day cut, 60% sewing eff).</div>}
                    {confirmedPOs.length>0 && (
                      <div style={{ background:C.card, borderRadius:'12px', padding:'18px', border:`1px solid ${C.border}`, marginBottom:'18px' }}>
                        <div style={{ fontWeight:'bold', marginBottom:'12px', color:C.textPrimary }}>POs TO BE PLANNED ({confirmedPOs.length})</div>
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(250px,1fr))', gap:'8px' }}>
                          {confirmedPOs.map(po=>{ const rd=apCheckReadiness(po); const sc=apReadinessScore(rd); return (
                            <div key={po.key} style={{ background:'#f5f6f8', borderRadius:'8px', padding:'12px', border:`1px solid ${C.border}`, borderLeft:`4px solid ${sc>=80?C.ok:sc>=50?C.warn:C.conflict}` }}>
                              <div style={{ fontWeight:'bold', color:C.textPrimary }}>{po.poNo} <span style={{ fontSize:'0.72rem', fontWeight:'normal', color:C.textMuted }}>{po.styleRef}</span></div>
                              <div style={{ fontSize:'0.75rem', color:C.textMuted }}>{(po.totalQty||0).toLocaleString()} pcs · PCD: {po.pcd||'—'} · Ex-Fty: {po.exFty||'—'}</div>
                              <div style={{ fontSize:'0.72rem', marginTop:'4px', color:sc>=80?C.ok:C.warn }}>{sc>=80?'✓ Ready':sc>=50?`⚠ ${sc}% ready`:`⚠ Only ${sc}% ready`}</div>
                            </div>
                          );})}
                        </div>
                      </div>
                    )}
                    <div style={{ display:'flex', gap:'12px' }}>
                      <button onClick={()=>{ if(confirmedPOs.length===0)return alert('No confirmed POs'); const result=apGeneratePlan(confirmedPOs); setApPlanResult(result); setApView('DASHBOARD'); }} style={{ ...styles.btnPrimary, background:'linear-gradient(135deg,#a25ddc,#5b21b6)', padding:'14px 36px', fontSize:'1rem', borderRadius:'10px', opacity:confirmedPOs.length===0?0.5:1, cursor:confirmedPOs.length===0?'not-allowed':'pointer' }}>⚡ Generate Plan Now</button>
                      <button onClick={()=>setApView('DASHBOARD')} style={{ ...styles.btnPrimary, background:'#676879', padding:'14px 22px', borderRadius:'10px' }}>View Existing Plan →</button>
                    </div>
                  </div>
                )}

                {/* ── MASTER DASHBOARD ── */}
                {apView === 'DASHBOARD' && (
                  <div style={{ padding:'20px' }}>
                    <div style={{ background:'linear-gradient(135deg,#1e1b4b,#312e81,#4c1d95)', borderRadius:'14px', padding:'20px 26px', marginBottom:'18px', color:'white', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <div>
                        <div style={{ fontSize:'1.35rem', fontWeight:'900' }}>📊 Master Production Dashboard</div>
                        <div style={{ fontSize:'0.8rem', color:'#a78bfa', marginTop:'3px' }}>{planResult?`Last generated: ${new Date(planResult.generatedAt).toLocaleString()}`:'No plan generated yet'}</div>
                      </div>
                      <div style={{ display:'flex', gap:'10px' }}>
                        <button onClick={()=>setApManualMode(m=>!m)} style={{ padding:'8px 16px', borderRadius:'8px', border:'2px solid rgba(255,255,255,0.3)', background:apManualMode?'white':'transparent', color:apManualMode?'#a25ddc':'white', fontWeight:'bold', cursor:'pointer', fontSize:'0.8rem' }}>{apManualMode?'📝 Manual ON':'📝 Manual Mode'}</button>
                        <button onClick={()=>setApView('GENERATE')} style={{ padding:'8px 16px', borderRadius:'8px', background:'#a25ddc', color:'white', border:'none', fontWeight:'bold', cursor:'pointer', fontSize:'0.8rem' }}>⚡ Regenerate</button>
                        <button onClick={()=>window.print()} style={{ padding:'8px 16px', borderRadius:'8px', background:'rgba(255,255,255,0.15)', color:'white', border:'1px solid rgba(255,255,255,0.3)', fontWeight:'bold', cursor:'pointer', fontSize:'0.8rem' }}>🖨 Print</button>
                      </div>
                    </div>

                    {!planResult ? (
                      <div style={{ textAlign:'center', padding:'60px', background:C.card, borderRadius:'12px', border:`2px dashed ${C.border}` }}>
                        <div style={{ fontSize:'3rem', marginBottom:'12px' }}>⚡</div>
                        <div style={{ fontSize:'1.1rem', fontWeight:'bold', marginBottom:'8px' }}>No plan generated yet</div>
                        <div style={{ color:C.textMuted, marginBottom:'18px' }}>Add & confirm POs, then click Generate Plan</div>
                        <button onClick={()=>setApView('GENERATE')} style={{ ...styles.btnPrimary, background:'#a25ddc', padding:'12px 28px' }}>Go to Generate →</button>
                      </div>
                    ) : (
                      <>
                        {/* Manual mode banner */}
                        {apManualMode && (
                          <div style={{ background:'linear-gradient(90deg,#fff5e6,#fff7ed)', border:'2px solid #fdab3d', borderRadius:'10px', padding:'10px 16px', marginBottom:'14px', display:'flex', alignItems:'center', gap:'10px' }}>
                            <span style={{ fontSize:'1.3rem' }}>📝</span>
                            <div>
                              <div style={{ fontWeight:'bold', color:'#92400e', fontSize:'0.88rem' }}>Manual Edit Mode — Click any bar segment to override daily target</div>
                              <div style={{ fontSize:'0.75rem', color:'#92400e' }}>Manual overrides shown in amber. Dashboard shows Manual vs Auto comparison.</div>
                            </div>
                            <div style={{ marginLeft:'auto', display:'flex', gap:'6px' }}>
                              {chip('🤖 Auto','#a25ddc',C.accentLight)}{chip('📝 Manual','#92400e','#fff5e6')}
                            </div>
                            {Object.keys(apManualEdits).length>0 && <button onClick={()=>setApManualEdits({})} style={{ padding:'5px 12px', background:'#fff5e6', color:'#92400e', border:'1px solid #fcd34d', borderRadius:'6px', cursor:'pointer', fontSize:'0.75rem', fontWeight:'bold', flexShrink:0 }}>Reset All</button>}
                          </div>
                        )}

                        {/* KPIs */}
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'12px', marginBottom:'18px' }}>
                          {[
                            { label:'Total POs', val: totalPOs, col:'#a25ddc' },
                            { label:'Total Styles', val: totalStyles2, col:'#0073ea' },
                            { label:'Total Pieces', val: totalPcs.toLocaleString(), col:'#059669' },
                            { label:'⚠ Conflicts', val: conflicts.length, col: conflicts.length>0?C.conflict:'#9699a6' },
                            { label:'Manual Edits', val: Object.keys(apManualEdits).length, col: Object.keys(apManualEdits).length>0?C.warn:'#9699a6' },
                          ].map(k=>(
                            <div key={k.label} style={{ background:C.card, borderRadius:'12px', padding:'14px 16px', border:`1px solid ${C.border}`, borderTop:`4px solid ${k.col}` }}>
                              <div style={{ fontSize:'0.65rem', textTransform:'uppercase', color:C.textMuted, marginBottom:'4px' }}>{k.label}</div>
                              <div style={{ fontSize:'1.6rem', fontWeight:'900', color:k.col }}>{k.val}</div>
                            </div>
                          ))}
                        </div>

                        {/* Conflict banner */}
                        {conflicts.length>0 && (
                          <div style={{ background:'#fef2f2', border:'2px solid #fca5a5', borderRadius:'10px', padding:'12px 16px', marginBottom:'16px', display:'flex', justifyContent:'space-between', alignItems:'center', gap:'12px' }}>
                            <div>
                              <div style={{ fontWeight:'bold', color:'#991b1b' }}>⚠ {conflicts.length} Capacity Conflict{conflicts.length>1?'s':''} Detected</div>
                              <div style={{ fontSize:'0.78rem', color:'#b91c1c', marginTop:'2px' }}>Sewing lines overloaded on overlapping dates. Adjust PCD or allocate more lines.</div>
                            </div>
                            <div style={{ display:'flex', flexWrap:'wrap', gap:'5px' }}>
                              {conflicts.map(k=>{ const po=apPoList.find(p=>p.key===k); return <span key={k} style={{ background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:'6px', padding:'3px 9px', fontSize:'0.78rem', fontWeight:'bold', color:'#991b1b' }}>{po?.poNo||k}</span>; })}
                            </div>
                          </div>
                        )}

                        {/* TIMELINE GANTT */}
                        <div style={{ background:C.card, borderRadius:'12px', border:`1px solid ${C.border}`, overflow:'hidden', marginBottom:'18px' }}>
                          <div style={{ padding:'14px 18px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                            <div style={{ fontWeight:'bold', color:C.textPrimary }}>📅 PO-Wise Production Timeline</div>
                            <div style={{ display:'flex', gap:'12px', fontSize:'0.72rem', color:C.textMuted, alignItems:'center' }}>
                              {[['✂ Cut',C.cut],['🧵 Sew',C.sew],['✨ Fin',C.fin],['📦 Pkg',C.pack]].map(([l,c])=><span key={l} style={{ display:'flex', alignItems:'center', gap:'3px' }}><span style={{ width:'8px',height:'8px',borderRadius:'2px',background:c,display:'inline-block' }}/>{l}</span>)}
                              <span style={{ color:'#e2445c', fontWeight:'bold' }}>│ Today</span>
                              <span style={{ color:C.warn }}>│ PCD</span>
                              <span style={{ color:C.accent }}>│ Ex-Fty</span>
                            </div>
                          </div>

                          {Object.values(dashPlan).map(rawPlan => {
                            const plan = apMergedPlan(rawPlan);
                            const isConflict = conflicts.includes(plan.poKey);
                            const isLate = plan.computedExFty && plan.exFty && plan.computedExFty > plan.exFty;
                            const ganttStart = new Date(plan.cutting.start || today);
                            const ganttEnd = new Date(plan.packing.end || addDays(today, 60));
                            const totalMs = Math.max(ganttEnd - ganttStart, 86400000);
                            const pct = d => d ? Math.min(99, Math.max(0, Math.round(((new Date(d)-ganttStart)/totalMs)*100))) : 0;
                            const wid = (s,e) => s&&e ? Math.max(2, Math.round(((new Date(e)-new Date(s))/totalMs)*100)) : 0;

                            return (
                              <div key={plan.poKey} style={{ borderBottom:`1px solid ${C.border}`, background: isConflict?'#fff5f5':isLate?'#fffbeb':'white' }}>
                                <div style={{ display:'grid', gridTemplateColumns:'220px 1fr' }}>
                                  {/* Label */}
                                  <div style={{ padding:'12px 14px', borderRight:`1px solid ${C.border}`, background: isConflict?'#fef2f2':isLate?'#fffbeb':'#f5f6f8' }}>
                                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'4px' }}>
                                      <div style={{ fontWeight:'900', color:isConflict?C.conflict:C.accent, fontSize:'0.9rem' }}>{plan.poNo}</div>
                                      <div style={{ display:'flex', gap:'3px' }}>{isConflict&&<span style={{ fontSize:'0.7rem', fontWeight:'bold', color:C.conflict }}>⚠</span>}{isLate&&<span style={{ fontSize:'0.7rem', color:C.warn }}>LATE</span>}</div>
                                    </div>
                                    <div style={{ fontSize:'0.7rem', color:C.textMuted, marginBottom:'5px' }}>{plan.styleRef} {plan.color&&`· ${plan.color}`}</div>
                                    <div style={{ fontSize:'0.68rem', color:C.textMuted, display:'flex', flexDirection:'column', gap:'2px' }}>
                                      <span>Qty: <strong>{plan.totalQty?.toLocaleString()}</strong></span>
                                      <span style={{ color:C.sew }}>Ex-Fty: <strong>{plan.computedExFty||plan.exFty}</strong></span>
                                      <span>Lines: <strong style={{ color:C.sew }}>{plan.linesNeeded}</strong> · SMV: <strong>{plan.smv?.toFixed(1)}</strong></span>
                                    </div>
                                  </div>
                                  {/* Gantt bars */}
                                  <div style={{ position:'relative', height:'88px', padding:'8px' }}>
                                    <div style={{ position:'absolute', top:0, bottom:0, left:`${pct(today)}%`, width:'1.5px', background:'#e2445c', zIndex:3 }}/>
                                    {plan.pcd && <div style={{ position:'absolute', top:0, bottom:0, left:`${pct(plan.pcd)}%`, width:'1.5px', background:C.warn, zIndex:3 }}/>}
                                    {(plan.computedExFty||plan.exFty) && <div style={{ position:'absolute', top:0, bottom:0, left:`${pct(plan.computedExFty||plan.exFty)}%`, width:'1.5px', background:'#a25ddc', zIndex:3 }}/>}
                                    {[
                                      { s:plan.cutting.start, e:plan.cutting.end, col:C.cut, lbl:'CUT', top:8 },
                                      { s:plan.sewing.start, e:plan.sewing.end, col:C.sew, lbl:'SEW', top:28 },
                                      { s:plan.finishing.start, e:plan.finishing.end, col:C.fin, lbl:'FIN', top:48 },
                                      { s:plan.packing.start, e:plan.packing.end, col:C.pack, lbl:'PKG', top:68 },
                                    ].map(bar=>(
                                      <div key={bar.lbl} style={{ position:'absolute', top:`${bar.top}px`, left:`${pct(bar.s)}%`, width:`${Math.max(wid(bar.s,bar.e),2)}%`, height:'14px', background:`${bar.col}cc`, borderRadius:'3px', display:'flex', alignItems:'center', paddingLeft:'3px', overflow:'hidden' }}>
                                        <span style={{ fontSize:'0.55rem', color:'white', fontWeight:'bold', whiteSpace:'nowrap' }}>{bar.lbl}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                {/* Mini bar chart for sewing daily targets */}
                                <div style={{ display:'grid', gridTemplateColumns:'220px 1fr', borderTop:`1px dashed #f0f1f4` }}>
                                  <div style={{ padding:'4px 14px 8px', background:isConflict?'#fef2f2':'#fafafa', borderRight:`1px solid ${C.border}` }}>
                                    {[
                                      { lbl:'Cut', val:`${plan.cutting.schedule.length}d · ${plan.cutting.dailyTarget}/d`, col:C.cut },
                                      { lbl:'Sew', val:`${plan.sewing.schedule.length}d · ${plan.linesNeeded} ln`, col:C.sew },
                                      { lbl:'Fin', val:`${plan.finishing.schedule.length}d`, col:C.fin },
                                      { lbl:'Pkg', val:`${plan.packing.schedule.length}d`, col:C.pack },
                                    ].map(s=>(
                                      <div key={s.lbl} style={{ fontSize:'0.65rem', display:'flex', justifyContent:'space-between' }}>
                                        <span style={{ color:s.col, fontWeight:'bold', minWidth:'28px' }}>{s.lbl}</span>
                                        <span style={{ color:C.textMuted }}>{s.val}</span>
                                      </div>
                                    ))}
                                  </div>
                                  <div style={{ display:'flex', alignItems:'flex-end', gap:'1px', padding:'4px 8px 6px', overflowX:'hidden' }}>
                                    {plan.sewing.schedule.slice(0, 60).map((d, di) => {
                                      const eff = apManualEdits[plan.poKey]?.sewing?.[di] ?? d.target;
                                      const isEd = apManualMode && apManualEdits[plan.poKey]?.sewing?.[di] !== undefined;
                                      return (
                                        <div key={di} title={`${d.date}: ${eff} pcs`}
                                          onClick={()=>{ if(!apManualMode) return; const nv=prompt(`Edit sewing target for ${d.date}:`, eff); if(nv!==null) apApplyManualEdit(plan.poKey,'sewing',di,parseInt(nv)||0); }}
                                          style={{ width:'5px', flexShrink:0, background:isEd?C.warn:C.sew, borderRadius:'2px', height:`${Math.max(4, Math.round((eff/plan.sewing.dailyTarget)*38))}px`, cursor:apManualMode?'pointer':'default', transition:'height 0.2s', border:isEd?`1px solid #92400e`:'none' }}/>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* DETAIL TABLE */}
                        <div style={{ background:C.card, borderRadius:'12px', border:`1px solid ${C.border}`, overflow:'hidden', marginBottom:'16px' }}>
                          <div style={{ padding:'12px 18px', borderBottom:`1px solid ${C.border}`, fontWeight:'bold', color:C.textPrimary }}>📋 Detailed Plan Summary</div>
                          <div style={{ overflowX:'auto' }}>
                            <table style={{ ...styles.table, fontSize:'0.78rem' }}>
                              <thead><tr style={{ background:'#f5f6f8' }}>
                                {['PO / Style','Qty','PCD','Cut Days','Cut Target/d','Sew Start','Sew End','Lines','Fin End','Pkg End','Computed Ex-Fty','Committed','Status'].map(h=>(
                                  <th key={h} style={{ ...styles.th, fontSize:'0.65rem', whiteSpace:'nowrap' }}>{h}</th>
                                ))}
                              </tr></thead>
                              <tbody>
                                {Object.values(dashPlan).map(rawPlan=>{
                                  const plan=apMergedPlan(rawPlan);
                                  const isConflict=conflicts.includes(plan.poKey);
                                  const isLate=plan.computedExFty&&plan.exFty&&plan.computedExFty>plan.exFty;
                                  return (
                                    <tr key={plan.poKey} style={{ borderBottom:`1px solid ${C.border}`, background:isConflict?'#fff5f5':isLate?'#fffbeb':'white' }}>
                                      <td style={{ ...styles.td, fontWeight:'bold', color:isConflict?C.conflict:C.accent }}>
                                        {plan.poNo}<br/><span style={{ fontWeight:'normal', fontSize:'0.68rem', color:C.textMuted }}>{plan.styleRef} {plan.color&&`· ${plan.color}`}</span>
                                      </td>
                                      <td style={{ ...styles.td, fontWeight:'bold' }}>{plan.totalQty?.toLocaleString()}</td>
                                      <td style={{ ...styles.td, color:plan.pcd&&plan.pcd<today?C.conflict:undefined, fontWeight:'bold' }}>{plan.pcd||'—'}</td>
                                      <td style={{ ...styles.td, color:C.cut }}>{plan.cutting.schedule.length}</td>
                                      <td style={{ ...styles.td, color:C.cut, fontWeight:'bold' }}>{plan.cutting.dailyTarget}</td>
                                      <td style={styles.td}>{plan.sewing.start}</td>
                                      <td style={styles.td}>{plan.sewing.end}</td>
                                      <td style={{ ...styles.td, fontWeight:'bold', color:C.sew }}>{plan.linesNeeded}</td>
                                      <td style={styles.td}>{plan.finishing.end}</td>
                                      <td style={styles.td}>{plan.packing.end}</td>
                                      <td style={{ ...styles.td, fontWeight:'bold', color:isLate?C.conflict:C.ok }}>
                                        {plan.computedExFty}
                                        {isLate&&<div style={{ fontSize:'0.62rem', color:C.conflict }}>⚠ committed {plan.exFty}</div>}
                                      </td>
                                      <td style={{ ...styles.td, color:C.textMuted }}>{plan.exFty||'—'}</td>
                                      <td style={styles.td}>
                                        {isConflict ? chip('⚠ Conflict',C.conflict,'#fef2f2') : isLate ? chip('⚠ Late',C.warn,'#fff5e6') : chip('✓ On Track',C.ok,'#f0fdf4')}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Size-wise breakdown per PO */}
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px,1fr))', gap:'12px' }}>
                          {Object.values(dashPlan).slice(0,6).map(rawPlan=>{
                            const plan=rawPlan;
                            return (
                              <div key={plan.poKey} style={{ background:C.card, borderRadius:'10px', padding:'14px', border:`1px solid ${C.border}` }}>
                                <div style={{ fontWeight:'bold', color:C.textPrimary, marginBottom:'8px', fontSize:'0.85rem' }}>{plan.poNo} · {plan.styleRef}</div>
                                <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                                  {(apPoList.find(p=>p.key===plan.poKey)?.sizes||[]).map(s=>(
                                    <div key={s.size} style={{ background:'#f5f6f8', border:`1px solid ${C.border}`, borderRadius:'6px', padding:'6px 10px', textAlign:'center', minWidth:'50px' }}>
                                      <div style={{ fontSize:'0.9rem', fontWeight:'900', color:C.accent }}>{s.size}</div>
                                      <div style={{ fontSize:'0.7rem', color:C.textMuted }}>{s.qty}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                )}

              </div>{/* end main content */}
            </div>
          );
        })()}

      </div>
    </div>
  );
}

